import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";
import { S, NAVY, NAVY_DEEP, GOLD, BEIGE, BEIGE_DRK, SLATE, WHITE, BEIGE_DARK, BORDER, GREEN, GREEN_BG, RED, RED_BG, AGENT_BG, STAGE_COLORS, ASHGRO_SERVICE_OPTIONS, fmt, initials, fmtVal, uid, sameId } from "./constants";
import { Pagination, MultiSelect, ListEditor, AttachmentsPanel } from "./components/shared";
import { LeadFormModal, ClientFormModal, ConvertModal, ActivityModal } from "./components/modals";
import { LeadsList, LeadDetail } from "./components/leads";
import { ClientsList, ClientDetail } from "./components/clients";
import { GmailInbox } from "./components/GmailInbox";
import AshGroCalculator from "./components/GrowthCalculator";

const ORG_ID = "9f6aa37c-e4e5-42f3-ac47-a83bc27598f5";
/** Production CRM URL — use for password reset redirect; mirror in Supabase Dashboard → Authentication → URL Configuration (Site URL + Redirect URLs). */
const AUTH_SITE_URL = process.env.REACT_APP_AUTH_SITE_URL || "https://crm.ashgropartners.com";

function readHashTypeRecovery() {
  if (typeof window === "undefined") return false;
  const raw = window.location.hash.replace(/^#/, "");
  if (!raw) return false;
  try {
    return new URLSearchParams(raw).get("type") === "recovery";
  } catch {
    return false;
  }
}

const DEFAULT_STAGES = ["New Lead", "Contacted", "Demo Scheduled", "Proposal Sent", "Closed Won", "Closed Lost"];
const DEFAULT_LEAD_SOURCES = ["AI Web Agent", "Referral", "Outbound", "LinkedIn", "Conference", "Email Campaign", "Direct", "Other"];
const DEFAULT_SERVICES = [...ASHGRO_SERVICE_OPTIONS];
const DEFAULT_CLIENT_STATUSES = ["Active", "At Risk", "Churned", "Paused"];
const FIELD_TYPES = ["Text", "Number", "Date", "Dropdown", "Checkbox", "Email", "Phone", "URL", "Textarea"];

const SAMPLE_LEADS = [
  { id: 1, clubName: "Oakmont Country Club", contactName: "James Whitfield", email: "jwhitfield@oakmont.com", phone: "+1-404-555-0021", title: "Membership Director", stage: "Demo Scheduled", service: "AshGro CRM Pro", source: "AI Web Agent", activity: [{ type: "Phone Call", date: "Feb 25, 2026, 10:14 AM", content: "Spoke with James about CRM needs. Team of 4 membership staff. Very interested in bulk email features." }, { type: "Email", date: "Feb 24, 2026, 3:30 PM", content: "Sent intro email and service overview deck. James replied same day." }], createdAt: "2026-02-10", followUp: "2026-03-04", value: 129.99, customFields: {} },
  { id: 2, clubName: "Piedmont Golf Club", contactName: "Sarah Langston", email: "slangston@piedmontgc.com", phone: "+1-404-555-0088", title: "General Manager", stage: "New Lead", service: "AI Club Concierge", source: "AI Web Agent", activity: [{ type: "AI Web Agent", date: "Feb 27, 2026, 9:02 AM", content: "Submitted inquiry via AshGro website AI agent. Asked about 24/7 prospect engagement and pricing." }], createdAt: "2026-02-27", followUp: "2026-03-01", value: 349.99, customFields: {} },
  { id: 3, clubName: "Riverview Athletic Club", contactName: "Michael Torres", email: "mtorres@riverviewac.com", phone: "+1-703-555-0047", title: "Club President", stage: "Proposal Sent", service: "Membership & Club Consulting", source: "Referral", activity: [{ type: "Email", date: "Feb 20, 2026, 2:00 PM", content: "Proposal sent. Full membership sales strategic plan. Following up March 1." }, { type: "Phone Call", date: "Feb 15, 2026, 11:30 AM", content: "Discovery call. Club has 380 members, team of 3." }], createdAt: "2026-02-01", followUp: "2026-03-01", value: 5000, customFields: {} },
  { id: 4, clubName: "Dunwoody Country Club", contactName: "Robert Ellis", email: "rellis@dunwoodycc.com", phone: "+1-404-555-0062", title: "General Manager", stage: "Contacted", service: "Membership Sales Training", source: "Outbound", activity: [{ type: "Phone Call", date: "Feb 22, 2026, 9:00 AM", content: "Intro call completed. Team of 5 needing structured sales process training." }], createdAt: "2026-02-18", followUp: "2026-03-02", value: 3500, customFields: {} },
  { id: 5, clubName: "Heritage Pines Club", contactName: "Diana Mercer", email: "dmercer@heritagepines.com", phone: "+1-404-555-0031", title: "Membership Director", stage: "New Lead", service: "AshGro CRM", source: "LinkedIn", activity: [], createdAt: "2026-01-10", followUp: "", value: 79.99, customFields: {} },
];
const SAMPLE_CLIENTS = [
  { id: 101, clubName: "The Magnolia Club", contactName: "Patricia Holt", email: "pholt@magnoliaclub.com", phone: "+1-404-555-0093", title: "Membership Director", service: "CRM + AI Bundle", source: "LinkedIn", status: "Active", startDate: "2026-03-01", renewalDate: "2027-03-01", value: 349.99, activity: [{ type: "Onboarding Call", date: "Mar 1, 2026, 10:00 AM", content: "Onboarding completed. Patricia and her team are live." }, { type: "Email", date: "Feb 14, 2026, 4:00 PM", content: "Contract signed. 12-month annual subscription." }], customFields: {} },
  { id: 102, clubName: "Stonebrook Golf Club", contactName: "David Carmichael", email: "dcarmichael@stonebrook.com", phone: "+1-703-555-0019", title: "GM", service: "AshGro CRM Pro", source: "Referral", status: "At Risk", startDate: "2025-09-01", renewalDate: "2026-09-01", value: 129.99, activity: [{ type: "Email", date: "Jan 10, 2026, 11:00 AM", content: "David mentioned they may not renew due to budget cuts. Needs follow-up." }], customFields: {} },
];

const KB_CAT_COLORS = { "Services & Pricing": { bg: "#EEF2FF", text: "#3730A3" }, "FAQs": { bg: "#FFF7ED", text: "#C2410C" }, "About AshGro": { bg: GREEN_BG, text: GREEN }, "Club Consulting": { bg: "#F0F9FF", text: "#0369A1" }, "Onboarding": { bg: "#FDF4FF", text: "#7E22CE" }, "Objection Handling": { bg: RED_BG, text: RED }, "Case Studies": { bg: "#FFFBEB", text: "#92400E" }, "Other": { bg: BEIGE_DARK, text: SLATE } };

const CSV_COLUMNS = ["clubName","contactName","email","phone","title","stage","service","source","value","followUp"];
const CSV_TEMPLATE = CSV_COLUMNS.join(",") + "\nOakmont Country Club,James Whitfield,jwhitfield@oakmont.com,+1-404-555-0021,Membership Director,New Lead,AshGro CRM Pro,Referral,129.99,2026-04-01\n";

// eslint-disable-next-line no-unused-vars
function BulkUploadModal({
  bulkStep, bulkPreview, bulkErrors, leads, setBulkStep, setBulkPreview, setBulkErrors, setShowBulkUpload, setPage,
  confirmBulkImport,
}) {
  const parseCSV = (text) => {
    const lines = text.trim().split("\n").filter(l => l.trim());
    if (lines.length < 2) return { rows: [], errors: ["CSV must have a header row and at least one data row."] };
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/\s+/g,"").replace(/[^a-z]/g,""));
    const colMap = {};
    CSV_COLUMNS.forEach(col => {
      const idx = headers.findIndex(h => h === col.toLowerCase() || h === col.replace(/([A-Z])/g, m => m.toLowerCase()));
      if (idx !== -1) colMap[col] = idx;
    });
    const rows = []; const errors = [];
    lines.slice(1).forEach((line, i) => {
      const vals = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
      const row = {}; CSV_COLUMNS.forEach(col => { row[col] = colMap[col] !== undefined ? (vals[colMap[col]] || "") : ""; });
      if (!row.clubName) { errors.push(`Row ${i+2}: Missing Club Name`); return; }
      if (!row.email || !row.email.includes("@")) { errors.push(`Row ${i+2}: Missing or invalid Email`); return; }
      rows.push({ ...row, id: Date.now() + i, createdAt: new Date().toISOString().split("T")[0], activity: [], customFields: {}, stage: row.stage || "New Lead", source: row.source || "Bulk Import", value: parseFloat(row.value) || 0 });
    });
    return { rows, errors };
  };
  const onFileSelect = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const { rows, errors } = parseCSV(e.target.result);
      setBulkPreview(rows); setBulkErrors(errors); setBulkStep("preview");
    };
    reader.readAsText(file);
  };
  return (
    <div style={S.modal}><div style={{ ...S.modalBox, width: 640 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div><h3 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Bulk Import Leads</h3><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Upload a CSV file to import multiple leads at once.</p></div>
        <button onClick={() => { setShowBulkUpload(false); setBulkStep("upload"); setBulkPreview([]); setBulkErrors([]); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: SLATE }}>×</button>
      </div>
      {bulkStep === "upload" && (
        <div>
          <div
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = "#FFFCF5"; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = BEIGE; }}
            onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = BEIGE; onFileSelect(e.dataTransfer.files[0]); }}
            style={{ border: "2px dashed " + BORDER, borderRadius: 12, padding: "40px 24px", textAlign: "center", background: BEIGE, marginBottom: 20, cursor: "pointer", transition: "all 0.2s" }}
            onClick={() => document.getElementById("csv-file-input").click()}
          >
            <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: 14, marginBottom: 4 }}>Drop your CSV here or click to browse</div>
            <div style={{ fontSize: 12, color: SLATE }}>Accepts .csv files · Duplicates are automatically skipped</div>
            <input id="csv-file-input" type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={e => onFileSelect(e.target.files[0])} />
          </div>
          <div style={{ background: BEIGE, borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: SLATE, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Required & Supported Columns</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {CSV_COLUMNS.map(col => (<span key={col} style={{ padding: "3px 10px", borderRadius: 6, background: ["clubName","email"].includes(col) ? NAVY : WHITE, color: ["clubName","email"].includes(col) ? WHITE : NAVY, border: "1px solid " + BORDER, fontSize: 11, fontFamily: "monospace" }}>{col}{["clubName","email"].includes(col) ? " *" : ""}</span>))}
            </div>
            <div style={{ fontSize: 11, color: SLATE, marginTop: 8 }}>* Required. All other columns are optional.</div>
          </div>
          <button onClick={() => { const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "ashgro-leads-template.csv"; a.click(); }} style={{ ...S.btnGold, width: "100%", justifyContent: "center" }}>
            ⬇ Download CSV Template
          </button>
        </div>
      )}
      {bulkStep === "preview" && (
        <div>
          {bulkErrors.length > 0 && (
            <div style={{ background: RED_BG, border: "1px solid #FCA5A5", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: RED, fontSize: 13, marginBottom: 6 }}>⚠ {bulkErrors.length} row{bulkErrors.length > 1 ? "s" : ""} skipped</div>
              {bulkErrors.map((e, i) => <div key={i} style={{ fontSize: 12, color: RED }}>{e}</div>)}
            </div>
          )}
          <div style={{ background: GREEN_BG, border: "1px solid #86EFAC", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: GREEN, fontSize: 13 }}>✓ {bulkPreview.length} leads ready to import</div>
            <div style={{ fontSize: 12, color: GREEN, marginTop: 2 }}>Existing leads with matching emails will be skipped automatically.</div>
          </div>
          <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid " + BORDER, borderRadius: 10, marginBottom: 20 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ position: "sticky", top: 0, background: WHITE }}>
                {["Club Name","Contact","Email","Stage","Service","Value"].map(h => <th key={h} style={{ ...S.th, padding: "10px 12px" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {bulkPreview.map((r, i) => {
                  const isDupe = leads.some(l => l.email.toLowerCase() === r.email.toLowerCase());
                  return (
                    <tr key={i} style={{ opacity: isDupe ? 0.4 : 1 }}>
                      <td style={{ ...S.td, padding: "8px 12px" }}><div style={{ fontWeight: 600, fontSize: 12 }}>{r.clubName}</div>{isDupe && <div style={{ fontSize: 10, color: RED }}>duplicate — will skip</div>}</td>
                      <td style={{ ...S.td, fontSize: 12, padding: "8px 12px" }}>{r.contactName || "—"}</td>
                      <td style={{ ...S.td, fontSize: 12, color: GOLD, padding: "8px 12px" }}>{r.email}</td>
                      <td style={{ ...S.td, padding: "8px 12px" }}><span style={S.stagePill(r.stage)}>{r.stage}</span></td>
                      <td style={{ ...S.td, fontSize: 12, padding: "8px 12px" }}>{r.service || "—"}</td>
                      <td style={{ ...S.td, fontSize: 12, fontWeight: 700, padding: "8px 12px" }}>{r.value ? fmtVal(r.value) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => { setBulkStep("upload"); setBulkPreview([]); setBulkErrors([]); }} style={S.btnGhost}>← Back</button>
            <button onClick={confirmBulkImport} disabled={bulkPreview.length === 0} style={S.btnPrimary}>Import {bulkPreview.length} Leads</button>
          </div>
        </div>
      )}
      {bulkStep === "done" && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
          <div style={{ fontFamily: "Merriweather,serif", fontSize: 18, fontWeight: 700, color: NAVY, marginBottom: 8 }}>Import Complete</div>
          <div style={{ fontSize: 13, color: SLATE, marginBottom: 24 }}>Your leads have been added to the pipeline.</div>
          <button onClick={() => { setShowBulkUpload(false); setBulkStep("upload"); setBulkPreview([]); setBulkErrors([]); setPage("leads"); }} style={S.btnPrimary}>View Leads</button>
        </div>
      )}
    </div></div>
  );
}

// eslint-disable-next-line no-unused-vars
function ProposalModal({
  showProposalModal, proposalRecord, generatedProposal, proposalLoading, proposalCopied,
  setShowProposalModal, setGeneratedProposal, setProposalCopied, generateProposal,
}) {
  if (!showProposalModal) return null;
  const formatProposal = (text) => text?.split("\n").map((line, i) => {
    if (line.startsWith("## ")) return <div key={i} style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 16, color: NAVY, marginTop: 24, marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid " + BORDER }}>{line.replace("## ", "")}</div>;
    if (line.startsWith("- ") || line.startsWith("• ")) return <div key={i} style={{ fontSize: 13, color: NAVY, lineHeight: 1.8, paddingLeft: 16 }}>{"• " + line.replace(/^[-•]\s/, "")}</div>;
    if (line.trim() === "") return <div key={i} style={{ height: 8 }} />;
    return <div key={i} style={{ fontSize: 13, color: NAVY, lineHeight: 1.8 }}>{line}</div>;
  });
  return (
    <div style={S.modal}><div style={{ ...S.modalBox, width: 720, maxHeight: "90vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div><h3 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>AI Proposal Generator</h3>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>{proposalRecord?.clubName} · {proposalRecord?.service || "Service TBD"}</p></div>
        <button onClick={() => { setShowProposalModal(false); setGeneratedProposal(null); }} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: SLATE }}>×</button>
      </div>
      {!generatedProposal && !proposalLoading && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
          <div style={{ fontFamily: "Merriweather,serif", fontSize: 17, color: NAVY, marginBottom: 8 }}>Ready to generate a proposal</div>
          <div style={{ fontSize: 13, color: SLATE, marginBottom: 24, lineHeight: 1.7 }}>The AI will write a full, branded proposal for {proposalRecord?.clubName} based on their profile, service interest, and deal value.</div>
          <button onClick={generateProposal} style={{ ...S.btnPrimary, padding: "12px 28px", fontSize: 14 }}>✦ Generate Proposal</button>
        </div>
      )}
      {proposalLoading && <div style={{ textAlign: "center", padding: "60px 0" }}>
        <div style={{ display: "inline-flex", gap: 8 }}>{[0,1,2].map(n => <div key={n} style={{ width: 10, height: 10, borderRadius: "50%", background: GOLD, animation: "pulse 1.4s infinite", animationDelay: n * 0.2 + "s" }} />)}</div>
        <div style={{ fontSize: 13, color: SLATE, marginTop: 16 }}>Drafting your proposal...</div>
      </div>}
      {generatedProposal && !proposalLoading && (
        <div>
          <div style={{ background: BEIGE, borderRadius: 10, padding: "24px 28px", maxHeight: 440, overflowY: "auto", marginBottom: 16 }}>
            {formatProposal(generatedProposal)}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => { navigator.clipboard.writeText(generatedProposal); setProposalCopied(true); setTimeout(() => setProposalCopied(false), 2000); }} style={{ ...S.btnGold }}>{proposalCopied ? "✓ Copied!" : "Copy Proposal"}</button>
            <button onClick={generateProposal} style={S.btnGhost}>↻ Regenerate</button>
            <a href={`mailto:${proposalRecord?.email}?subject=Your AshGro Proposal — ${proposalRecord?.clubName}&body=${encodeURIComponent(generatedProposal)}`} style={{ ...S.btnPrimary, textDecoration: "none" }}>✉ Open in Mail</a>
          </div>
        </div>
      )}
    </div></div>
  );
}

// eslint-disable-next-line no-unused-vars
function Settings({
  editingList, setEditingList, settingsTab, setSettingsTab, newIntakeCount,
  showNewField, setShowNewField, newFieldForm, setNewFieldForm, addCustomField,
  editingField, setEditingField, saveEditField, deleteCustomField,
  customLeadFields, customClientFields, stages, leadSources, services, clientStatuses,
  saveStages, saveLeadSources, saveServices, saveClientStatuses,
  companyName, setCompanyName, companyEmail, setCompanyEmail, emailSignature, setEmailSignature, persist,
  pwForm, setPwForm, handlePwChange, pwUpdating,
  showAddUser, setShowAddUser, userForm, setUserForm, saveUsers, users, saveCurrentUser, currentUser,
  editingUser, setEditingUser, userEditForm, setUserEditForm, saveEditUser,
  leads, clients, templateForm, setTemplateForm, editingTemplate, setEditingTemplate,
  showAddTemplate, setShowAddTemplate, saveEmailTemplates, emailTemplates, templatePreview, setTemplatePreview,
  simulateWebLead, IntegrationsEmbed,
  syncGmailEmails, gmailSyncing, lastSynced, gmailError, saveNotifications, notifications,
  gmailConnected, setGmailConnected, showToast, saveLeads, saveClients,
  supabase, userId,
}) {
  if (!currentUser) return <div style={{ padding: 32, color: SLATE }}>Loading settings...</div>;
  const allFieldDefs = [{ key: "stages", label: "Lead Stages", items: Array.isArray(stages) ? stages : [], onSave: saveStages, desc: "Pipeline stages on leads." }, { key: "sources", label: "Lead Sources", items: Array.isArray(leadSources) ? leadSources : [], onSave: saveLeadSources, desc: "Track where leads come from." }, { key: "services", label: "Services", items: Array.isArray(services) ? services : [], onSave: saveServices, desc: "AshGro service offerings." }, { key: "clientStatuses", label: "Client Statuses", items: Array.isArray(clientStatuses) ? clientStatuses : [], onSave: saveClientStatuses, desc: "Status options for clients." }];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 24 }}>
      {editingList && <ListEditor title={editingList.label} items={editingList.items} onSave={editingList.onSave} onClose={() => setEditingList(null)} showToast={showToast} />}
      <div style={{ ...S.card, padding: "8px 0", alignSelf: "start" }}>
        {[{ id: "fields", label: "Custom Fields", icon: "⊞" }, { id: "lists", label: "Dropdown Lists", icon: "☰" }, { id: "templates", label: "Email Templates", icon: "✉" }, { id: "proposals", label: "Proposal Settings", icon: "📄" }, { id: "users", label: "Team & Users", icon: "👥" }, { id: "integrations", label: newIntakeCount > 0 ? `Integrations (${newIntakeCount})` : "Integrations", icon: "🔗" }, { id: "gmail", label: "Gmail & Notifications", icon: "📧" }, { id: "account", label: "Account", icon: "⚙" }, { id: "security", label: "Password & Security", icon: "🔒" }]
          .filter(({ id }) => { if (["fields", "lists", "users", "proposals"].includes(id)) return currentUser?.role === "Admin" || currentUser?.role === "admin"; return true; })
          .map(({ id, label, icon }) => (
          <button key={id} onClick={() => setSettingsTab(id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 18px", border: "none", background: settingsTab === id ? BEIGE : "transparent", color: settingsTab === id ? NAVY : SLATE, fontSize: 13, fontFamily: "Open Sans, sans-serif", fontWeight: settingsTab === id ? 700 : 400, cursor: "pointer", textAlign: "left", borderLeft: settingsTab === id ? "3px solid " + GOLD : "3px solid transparent" }}><span>{icon}</span>{label}</button>
        ))}
      </div>
      <div>
        {settingsTab === "fields" && (
          (currentUser?.role === "Admin" || currentUser?.role === "admin") ? (
          <div>
            <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}><div><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Custom Fields</h2><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Add custom fields to lead and client records.</p></div><button onClick={() => setShowNewField(true)} style={S.btnPrimary}>+ Add Custom Field</button></div>
            {showNewField && (
              <div style={{ ...S.settingsCard, border: "2px solid " + GOLD, marginBottom: 20 }}>
                <div style={{ fontFamily: "Merriweather,serif", fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 16 }}>New Custom Field</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
                  <div><label style={S.label}>Field Label *</label><input value={newFieldForm.label} onChange={e => setNewFieldForm({ ...newFieldForm, label: e.target.value })} placeholder="e.g. Club Size" style={S.input} /></div>
                  <div><label style={S.label}>Field Type</label><select value={newFieldForm.type} onChange={e => setNewFieldForm({ ...newFieldForm, type: e.target.value })} style={S.input}>{FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                  <div><label style={S.label}>Apply To</label><select value={newFieldForm.target} onChange={e => setNewFieldForm({ ...newFieldForm, target: e.target.value })} style={S.input}><option value="lead">Leads</option><option value="client">Clients</option></select></div>
                  {newFieldForm.type === "Dropdown" && <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Dropdown Options (comma-separated)</label><input value={newFieldForm.options} onChange={e => setNewFieldForm({ ...newFieldForm, options: e.target.value })} placeholder="Option 1, Option 2, Option 3" style={S.input} /></div>}
                  <div style={{ gridColumn: "1/-1", display: "flex", alignItems: "center", gap: 8 }}><input type="checkbox" checked={newFieldForm.required} onChange={e => setNewFieldForm({ ...newFieldForm, required: e.target.checked })} /><label style={{ fontSize: 13, color: NAVY, cursor: "pointer" }}>Required field</label></div>
                </div>
                <div style={{ display: "flex", gap: 10 }}><button onClick={() => setShowNewField(false)} style={S.btnGhost}>Cancel</button><button onClick={addCustomField} style={S.btnPrimary}>Add Field</button></div>
              </div>
            )}
            {[{ title: "Lead Custom Fields", desc: "These fields appear on all lead records.", fields: Array.isArray(customLeadFields) ? customLeadFields : [], target: "lead" }, { title: "Client Custom Fields", desc: "These fields appear on all client records.", fields: Array.isArray(customClientFields) ? customClientFields : [], target: "client" }].map(({ title, desc, fields, target }) => (
              <div key={target} style={S.settingsCard}>
                <div style={S.settingsCardTitle}>{title}</div><div style={S.settingsCardSub}>{desc}</div>
                {(!fields || fields.length === 0) ? <div style={{ padding: "20px 0", color: SLATE, fontSize: 13, fontStyle: "italic" }}>No custom fields yet.</div> : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>{["Field Label", "Type", "Required", ""].map(h => <th key={h} style={{ ...S.th, padding: "10px 12px" }}>{h}</th>)}</tr></thead>
                    <tbody>{(Array.isArray(fields) ? fields : []).map(f => (<tr key={f.id}><td style={{ ...S.td, fontWeight: 600, padding: "10px 12px" }}>{f.label}</td><td style={{ ...S.td, color: SLATE, padding: "10px 12px" }}>{f.type}</td><td style={{ ...S.td, padding: "10px 12px" }}><span style={{ fontSize: 12, color: f.required ? GREEN : SLATE }}>{f.required ? "✓ Required" : "Optional"}</span></td><td style={{ ...S.td, padding: "10px 12px" }}><div style={{ display: "flex", gap: 8 }}><button onClick={() => setEditingField({ ...f, _target: target, options: Array.isArray(f.options) ? f.options.join(", ") : "" })} style={S.btnSm}>Edit</button><button onClick={() => deleteCustomField(f.id, target)} style={S.btnDanger}>Remove</button></div></td></tr>))}</tbody>
                  </table>
                )}
              </div>
            ))}
            {editingField && (
              <div style={S.modal}><div style={{ ...S.modalBox, width: 480 }}>
                <h3 style={{ margin: "0 0 20px", fontFamily: "Merriweather,serif", fontSize: 18, color: NAVY }}>Edit Field</h3>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                  <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Field Label</label><input value={editingField.label} onChange={e => setEditingField({ ...editingField, label: e.target.value })} style={S.input} /></div>
                  <div><label style={S.label}>Field Type</label><select value={editingField.type} onChange={e => setEditingField({ ...editingField, type: e.target.value })} style={S.input}>{FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                  {editingField.type === "Dropdown" && <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Options (comma-separated)</label><input value={typeof editingField.options === "string" ? editingField.options : editingField.options?.join(", ") || ""} onChange={e => setEditingField({ ...editingField, options: e.target.value })} style={S.input} /></div>}
                  <div style={{ gridColumn: "1/-1", display: "flex", alignItems: "center", gap: 8 }}><input type="checkbox" checked={!!editingField.required} onChange={e => setEditingField({ ...editingField, required: e.target.checked })} /><label style={{ fontSize: 13, color: NAVY }}>Required field</label></div>
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}><button onClick={() => setEditingField(null)} style={S.btnGhost}>Cancel</button><button onClick={saveEditField} style={S.btnPrimary}>Save Changes</button></div>
              </div></div>
            )}
          </div>
          ) : (
            <div style={{ padding: 32, color: SLATE, fontSize: 14 }}>
              Only admins can manage this section.
            </div>
          )
        )}
        {settingsTab === "lists" && (
          (currentUser?.role === "Admin" || currentUser?.role === "admin") ? (
          <div>
            <div style={{ marginBottom: 20 }}><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Dropdown Lists</h2><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Manage options that appear in dropdowns across the platform.</p></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              {allFieldDefs.map(({ key, label, items, onSave, desc }) => (
                <div key={key} style={S.settingsCard}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}><div><div style={S.settingsCardTitle}>{label}</div><div style={{ fontSize: 12, color: SLATE, marginTop: 2 }}>{desc}</div></div><button onClick={() => setEditingList({ label, items: Array.isArray(items) ? items : [], onSave })} style={{ ...S.btnGold, padding: "6px 14px", fontSize: 12 }}>Edit</button></div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{(Array.isArray(items) ? items : []).map(item => (<span key={item} style={{ padding: "3px 10px", borderRadius: 20, background: BEIGE_DARK, color: NAVY, fontSize: 12 }}>{item}</span>))}</div>
                </div>
              ))}
            </div>
          </div>
          ) : (
            <div style={{ padding: 32, color: SLATE, fontSize: 14 }}>
              Only admins can manage this section.
            </div>
          )
        )}
        {settingsTab === "account" && (
          <div>
            <div style={{ marginBottom: 20 }}><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Account Settings</h2><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Update your company profile and contact information.</p></div>
            <div style={S.settingsCard}><div style={S.settingsCardTitle}>Company Profile</div><div style={S.settingsCardSub}>This information appears throughout the platform.</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}><div><label style={S.label}>Company Name</label><input value={companyName} onChange={e => setCompanyName(e.target.value)} style={S.input} /></div><div><label style={S.label}>Sales Email</label><input value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} style={S.input} /></div></div><button onClick={() => { persist("ashgro-company", companyName); persist("ashgro-cemail", companyEmail); showToast("Account settings saved"); }} style={S.btnPrimary}>Save Changes</button></div>
            <div style={{ marginTop: 24, paddingTop: 24, borderTop: "1px solid #eee" }}>
              <div style={S.settingsCardTitle}>Email Signature</div>
              <div style={S.settingsCardSub}>Automatically appended to all outgoing emails.</div>
              <textarea
                style={{ ...S.input, height: 140, resize: "vertical", marginTop: 10, fontFamily: "Open Sans, sans-serif", fontSize: 13 }}
                placeholder={"Best,\nYour Name\nAshGro Membership Partners\nsales@ashgropartners.com"}
                value={emailSignature}
                onChange={e => { setEmailSignature(e.target.value); persist("ashgro-email-signature", e.target.value, false); }}
              />
            </div>
            <div style={S.settingsCard}><div style={S.settingsCardTitle}>Data Management</div><div style={S.settingsCardSub}>Manage your CRM data. These actions cannot be undone.</div><div style={{ display: "flex", gap: 12 }}><button onClick={() => { if (window.confirm("Reset all leads to sample data?")) { saveLeads(SAMPLE_LEADS); showToast("Leads reset"); }}} style={S.btnDanger}>Reset Leads</button><button onClick={() => { if (window.confirm("Clear all leads permanently?")) { saveLeads([]); showToast("Leads cleared"); }}} style={S.btnDanger}>Clear All Leads</button><button onClick={() => { if (window.confirm("Clear all clients permanently?")) { saveClients([]); showToast("Clients cleared"); }}} style={S.btnDanger}>Clear All Clients</button></div></div>
          </div>
        )}
        {settingsTab === "security" && (
          <div>
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Password & Security</h2>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Manage your account password and security settings.</p>
            </div>
            <div style={{ ...S.settingsCard, borderLeft: "4px solid " + GOLD, background: BEIGE }}>
              <div style={S.sectionLabel}>Security</div>
              <div style={S.settingsCardTitle}>Change Password</div>
              <div style={S.settingsCardSub}>Use a strong password at least 8 characters long.</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14, maxWidth: 440, marginTop: 8, marginBottom: 20 }}>
                <div>
                  <label style={S.label}>New Password</label>
                  <input type="password" value={pwForm.newPw} onChange={e => setPwForm({ ...pwForm, newPw: e.target.value })} placeholder="At least 8 characters" style={S.input} autoComplete="new-password" disabled={pwUpdating} />
                </div>
                <div>
                  <label style={S.label}>Confirm New Password</label>
                  <input type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} placeholder="Re-enter new password" style={S.input} autoComplete="new-password" disabled={pwUpdating} />
                </div>
              </div>
              <button type="button" onClick={handlePwChange} disabled={pwUpdating} style={{ ...S.btnPrimary, opacity: pwUpdating ? 0.7 : 1, cursor: pwUpdating ? "wait" : "pointer" }}>{pwUpdating ? "Updating…" : "Update Password"}</button>
            </div>
            <div style={S.settingsCard}><div style={S.settingsCardTitle}>Session Settings</div><div style={S.settingsCardSub}>Configure how long your session stays active.</div><div style={{ maxWidth: 300 }}><label style={S.label}>Auto-Logout After</label><select style={S.input}><option>30 minutes of inactivity</option><option>1 hour of inactivity</option><option>4 hours of inactivity</option><option>Never</option></select></div></div>
          </div>
        )}
        {settingsTab === "users" && (
          (currentUser?.role === "Admin" || currentUser?.role === "admin") ? (
          <div>
            <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Team & Users</h2><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Manage your sales team, roles, and who can be assigned leads and clients.</p></div>
              <button onClick={() => setShowAddUser(true)} style={S.btnPrimary}>+ Add User</button>
            </div>
            {showAddUser && (
              <div style={{ ...S.settingsCard, border: "2px solid " + GOLD, marginBottom: 20 }}>
                <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 16 }}>New Team Member</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
                  <div><label style={S.label}>Full Name *</label><input value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} placeholder="Alex Morgan" style={S.input} /></div>
                  <div><label style={S.label}>Email *</label><input value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} placeholder="alex@ashgropartners.com" style={S.input} /></div>
                  <div><label style={S.label}>Role</label><select value={userForm.role} onChange={e => setUserForm({...userForm, role: e.target.value})} style={S.input}><option>Sales Director</option><option>Sales Rep</option><option>Account Manager</option><option>Admin</option></select></div>
                  <div><label style={S.label}>Avatar Color</label><input type="color" value={userForm.color} onChange={e => setUserForm({...userForm, color: e.target.value})} style={{ ...S.input, height: 40, padding: 4, cursor: "pointer" }} /></div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => setShowAddUser(false)} style={S.btnGhost}>Cancel</button>
                  <button onClick={() => { if (!userForm.name || !userForm.email) return; const u = { id: uid(), ...userForm, initials: userForm.name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase() }; saveUsers && saveUsers([...(Array.isArray(users) ? users : []), u]); setUserForm({ name:"", email:"", role:"Sales Rep", color:"#0369A1" }); setShowAddUser(false); showToast && showToast("User added"); }} style={S.btnPrimary}>Add User</button>
                </div>
              </div>
            )}
            <div style={S.settingsCard}>
              <div style={S.settingsCardTitle}>Active Team Members</div>
              <div style={S.settingsCardSub}>All users who can be assigned leads and clients.</div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["Member","Role","Assigned Leads","Assigned Clients",""].map(h => <th key={h} style={{ ...S.th, padding: "10px 14px" }}>{h}</th>)}</tr></thead>
                <tbody>
                  {(Array.isArray(users) ? users : []).map(u => (
                    <tr key={u.id}>
                      <td style={{ ...S.td, padding: "12px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 36, height: 36, borderRadius: "50%", background: u.color, color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{u.initials}</div>
                          <div><div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div><div style={{ fontSize: 11, color: SLATE }}>{u.email}</div></div>
                        </div>
                      </td>
                      <td style={{ ...S.td, fontSize: 12, padding: "12px 14px" }}>{u.role}</td>
                      <td style={{ ...S.td, fontSize: 13, fontWeight: 700, padding: "12px 14px", textAlign: "center" }}>{(Array.isArray(leads) ? leads : []).filter(l => l.assignedTo === u.id).length}</td>
                      <td style={{ ...S.td, fontSize: 13, fontWeight: 700, padding: "12px 14px", textAlign: "center" }}>{(Array.isArray(clients) ? clients : []).filter(c => c.assignedTo === u.id).length}</td>
                      <td style={{ ...S.td, padding: "12px 14px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => saveCurrentUser(u)} style={{ ...S.btnSm, ...(currentUser?.id === u.id ? { background: GREEN_BG, color: GREEN, borderColor: "#86EFAC" } : {}) }}>{currentUser?.id === u.id ? "✓ Active" : "Switch To"}</button>
                          {currentUser?.role === "Admin" || currentUser?.role === "admin" ? (
                            <button
                              onClick={() => { setEditingUser(u); setUserEditForm({ name: u.name, email: u.email, role: u.role, active: u.active !== false }); }}
                              style={S.btnSm}
                            >
                              Edit
                            </button>
                          ) : null}
                          {(Array.isArray(users) ? users : []).length > 1 && <button onClick={() => { saveUsers((Array.isArray(users) ? users : []).filter(x => x.id !== u.id)); }} style={S.btnDanger}>Remove</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {editingUser && (
              <div style={S.modal}>
                <div style={{ ...S.modalBox, width: 480 }}>
                  <h3 style={{ margin: "0 0 20px", fontFamily: "Merriweather,serif", fontSize: 18, color: NAVY }}>Edit User</h3>
                  <div style={{ display: "grid", gap: 14, marginBottom: 20 }}>
                    <div>
                      <label style={S.label}>Full Name</label>
                      <input value={userEditForm.name || ""} onChange={e => setUserEditForm({ ...userEditForm, name: e.target.value })} style={S.input} />
                    </div>
                    <div>
                      <label style={S.label}>Email Address</label>
                      <input value={userEditForm.email || ""} onChange={e => setUserEditForm({ ...userEditForm, email: e.target.value })} style={S.input} />
                    </div>
                    <div>
                      <label style={S.label}>Role</label>
                      <select value={userEditForm.role || "Sales Rep"} onChange={e => setUserEditForm({ ...userEditForm, role: e.target.value })} style={S.input}>
                        <option value="Sales Rep">Sales Rep</option>
                        <option value="Sales Director">Sales Director</option>
                        <option value="Admin">Admin</option>
                      </select>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input type="checkbox" checked={userEditForm.active !== false} onChange={e => setUserEditForm({ ...userEditForm, active: e.target.checked })} id="activeToggle" />
                      <label htmlFor="activeToggle" style={{ fontSize: 13, color: NAVY, cursor: "pointer" }}>Active user</label>
                    </div>
                    <div style={{ borderTop: "1px solid " + BORDER, paddingTop: 14 }}>
                      <div style={{ fontSize: 12, color: SLATE, marginBottom: 8 }}>Password Reset</div>
                      <button
                        onClick={async () => {
                          const { supabase: sb } = await import("./supabaseClient");
                          if (sb) {
                            await sb.auth.resetPasswordForEmail(editingUser.email, { redirectTo: AUTH_SITE_URL });
                          }
                          showToast("Password reset email sent to " + editingUser.email);
                        }}
                        style={{ ...S.btnGhost, fontSize: 12 }}
                      >
                        Send Password Reset Email
                      </button>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button onClick={() => { setEditingUser(null); setUserEditForm({}); }} style={S.btnGhost}>Cancel</button>
                    <button onClick={saveEditUser} style={S.btnPrimary}>Save Changes</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          ) : (
            <div style={{ padding: 32, color: SLATE, fontSize: 14 }}>
              Only admins can manage this section.
            </div>
          )
        )}
        {settingsTab === "templates" && (() => {
          const TEMPLATE_CATEGORIES = ["Outbound","Follow-Up","Proposals","Reengagement","Clients","Other"];
          const VARIABLES = ["{{clubName}}","{{contactName}}","{{service}}","{{senderName}}","{{renewalDate}}","{{value}}"];
          return (
            <div>
              <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Email Templates</h2><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Reusable email templates for every stage of the sales cycle. Variables auto-fill with lead/client data.</p></div>
                <button onClick={() => { setTemplateForm({ name:"", category:"Outbound", subject:"", body:"", attachments: [] }); setEditingTemplate(null); setShowAddTemplate(true); }} style={S.btnPrimary}>+ New Template</button>
              </div>
              {showAddTemplate && (
                <div style={{ ...S.settingsCard, border: "2px solid " + GOLD, marginBottom: 20 }}>
                  <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 16 }}>{editingTemplate ? "Edit Template" : "New Email Template"}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                    <div><label style={S.label}>Template Name *</label><input value={templateForm.name} onChange={e => setTemplateForm({...templateForm, name: e.target.value})} placeholder="Initial Outreach" style={S.input} /></div>
                    <div><label style={S.label}>Category</label><select value={templateForm.category} onChange={e => setTemplateForm({...templateForm, category: e.target.value})} style={S.input}>{["Outbound","Follow-Up","Proposals","Reengagement","Clients","Other"].map(c => <option key={c}>{c}</option>)}</select></div>
                    <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Subject Line</label><input value={templateForm.subject} onChange={e => setTemplateForm({...templateForm, subject: e.target.value})} placeholder="Re: Membership Growth at {{clubName}}" style={S.input} /></div>
                    <div style={{ gridColumn: "1/-1" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <label style={S.label}>Email Body</label>
                        <div style={{ display: "flex", gap: 4 }}>{VARIABLES.map(v => <button key={v} onClick={() => setTemplateForm(f => ({...f, body: f.body + v}))} style={{ fontSize: 10, padding: "2px 6px", background: BEIGE_DARK, border: "none", borderRadius: 4, cursor: "pointer", color: NAVY, fontFamily: "monospace" }}>{v}</button>)}</div>
                      </div>
                      <textarea value={templateForm.body} onChange={e => setTemplateForm({...templateForm, body: e.target.value})} rows={10} style={{ ...S.input, resize: "vertical", lineHeight: 1.7 }} />
                    </div>
                  </div>
                  <AttachmentsPanel
                    attachments={templateForm.attachments || []}
                    onAdd={(a) => setTemplateForm(t => ({ ...t, attachments: [...(t.attachments||[]), a] }))}
                    onDelete={(id) => setTemplateForm(t => ({ ...t, attachments: (t.attachments||[]).filter(a => a.id !== id) }))}
                  />
                  <div style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => { setShowAddTemplate(false); setEditingTemplate(null); }} style={S.btnGhost}>Cancel</button>
                    <button onClick={() => { if (!templateForm.name) return; if (editingTemplate) { saveEmailTemplates((Array.isArray(emailTemplates) ? emailTemplates : []).map(t => t.id === editingTemplate ? {...t, ...templateForm, attachments: templateForm.attachments || []} : t)); setEditingTemplate(null); } else { saveEmailTemplates([...(Array.isArray(emailTemplates) ? emailTemplates : []), { id: uid(), ...templateForm, attachments: templateForm.attachments || [] }]); } setShowAddTemplate(false); showToast("Template saved"); }} style={S.btnPrimary}>{editingTemplate ? "Save Changes" : "Save Template"}</button>
                  </div>
                </div>
              )}
              {templatePreview && (
                <div style={S.modal}><div style={{ ...S.modalBox, width: 560 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                    <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 17, color: NAVY }}>{templatePreview.name}</div>
                    <button onClick={() => setTemplatePreview(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: SLATE }}>×</button>
                  </div>
                  <div style={{ background: BEIGE, borderRadius: 8, padding: "12px 16px", marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: SLATE, marginBottom: 4 }}>SUBJECT</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{templatePreview.subject}</div>
                  </div>
                  <div style={{ background: BEIGE, borderRadius: 8, padding: "14px 16px", fontSize: 13, color: NAVY, lineHeight: 1.8, whiteSpace: "pre-line", maxHeight: 360, overflowY: "auto" }}>{templatePreview.body}</div>
                  <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                    <button onClick={() => { navigator.clipboard.writeText(`Subject: ${templatePreview.subject}\n\n${templatePreview.body}`); showToast("Copied to clipboard"); setTemplatePreview(null); }} style={S.btnGold}>Copy</button>
                    <a href={`mailto:?subject=${encodeURIComponent(templatePreview.subject)}&body=${encodeURIComponent(templatePreview.body)}`} style={{ ...S.btnPrimary, textDecoration: "none" }}>✉ Open in Mail</a>
                  </div>
                </div></div>
              )}
              {TEMPLATE_CATEGORIES.map(cat => {
                const catTemplates = (Array.isArray(emailTemplates) ? emailTemplates : []).filter(t => t.category === cat);
                if (!catTemplates.length) return null;
                return (
                  <div key={cat} style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: SLATE, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>{cat}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {catTemplates.map(t => (
                        <div key={t.id} style={{ ...S.settingsCard, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 3 }}>{t.name}</div>
                            <div style={{ fontSize: 12, color: SLATE, fontStyle: "italic" }}>{t.subject}</div>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                            <button onClick={() => setTemplatePreview(t)} style={S.btnSm}>Preview</button>
                            <button onClick={() => { setTemplateForm({ name: t.name, category: t.category, subject: t.subject, body: t.body, attachments: t.attachments || [] }); setEditingTemplate(t.id); setShowAddTemplate(true); }} style={S.btnSm}>Edit</button>
                            <button onClick={() => { if (window.confirm("Delete this template?")) { saveEmailTemplates((Array.isArray(emailTemplates) ? emailTemplates : []).filter(x => x.id !== t.id)); showToast("Template deleted"); }}} style={S.btnDanger}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
        {settingsTab === "integrations" && (
          <div>
            <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Integrations & Web Intake</h2><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Connect your website, chatbot, and forms to capture leads automatically.</p></div>
              <button onClick={simulateWebLead} style={{ ...S.btnGold, fontSize: 12 }}>⚡ Simulate Web Lead</button>
            </div>
            {IntegrationsEmbed}
          </div>
        )}
        {settingsTab === "gmail" && (
          <div>
            <div style={{ marginBottom: 20 }}><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Gmail & Notifications</h2><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Sync your inbox to automatically track replies from leads and clients.</p></div>
            <div style={S.settingsCard}>
              <div style={S.settingsCardTitle}>Gmail Inbox Sync</div>
              <div style={S.settingsCardSub}>AshGro scans your Gmail inbox for replies from known leads and clients, logs them to the activity feed, and generates AI draft responses automatically.</div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 16, flexWrap: "wrap" }}>
                {gmailConnected ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ color: "#27ae60", fontSize: 13, fontWeight: 600 }}>✅ Gmail Connected</span>
                    <button
                      onClick={() => {
                        persist("gmail-access-token", null, false);
                        try { localStorage.removeItem("gmail-access-token"); } catch (_) {}
                        setGmailConnected(false);
                        showToast("Gmail disconnected");
                      }}
                      style={{ fontSize: 12, padding: "6px 14px", borderRadius: 6, border: "1px solid #e74c3c", color: "#e74c3c", background: "none", cursor: "pointer" }}>
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <button onClick={syncGmailEmails} style={S.btnGold}>Connect Gmail</button>
                )}
                {gmailConnected && (
                  <button onClick={syncGmailEmails} disabled={gmailSyncing} style={{ ...S.btnPrimary, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ display: "inline-block", animation: gmailSyncing ? "spin 1s linear infinite" : "none" }}>⟳</span>
                    {gmailSyncing ? "Syncing inbox..." : "Sync Gmail Now"}
                  </button>
                )}
                {lastSynced && <div style={{ fontSize: 12, color: SLATE }}>Last synced: <strong>{new Date(lastSynced).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</strong></div>}
                {gmailError && <div style={{ fontSize: 12, color: RED }}>{gmailError}</div>}
              </div>
            </div>
            {/* Google Calendar Card */}
            <div style={{ border: "1px solid #E8E3D8", borderRadius: 10, padding: 24, marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 16, color: NAVY }}>Google Calendar Sync</h3>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: SLATE }}>
                Sync your CRM events with Google Calendar for two-way scheduling.
              </p>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button
                  onClick={async () => {
                    if (supabase && userId) {
                      await supabase.from("crm_settings")
                        .delete()
                        .eq("user_id", userId)
                        .in("key", ["gcal-access-token", "gcal-refresh-token"]);
                    }
                    showToast("Google Calendar disconnected. Click Sync on the Calendar page to reconnect.");
                  }}
                  style={S.btnGhost}
                >
                  Disconnect Google Calendar
                </button>
                <span style={{ fontSize: 12, color: SLATE }}>Reconnect from the Calendar page after disconnecting.</span>
              </div>
            </div>
            <div style={S.settingsCard}>
              <div style={S.settingsCardTitle}>Monitored Contacts</div>
              <div style={S.settingsCardSub}>Gmail sync checks for replies from all leads and clients with email addresses on file.</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 14 }}>
                <div style={{ background: BEIGE, borderRadius: 10, padding: "14px 18px" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "Merriweather,serif", color: NAVY }}>{(Array.isArray(leads) ? leads : []).filter(l => l.email).length}</div>
                  <div style={{ fontSize: 12, color: SLATE, marginTop: 2 }}>Leads monitored</div>
                </div>
                <div style={{ background: BEIGE, borderRadius: 10, padding: "14px 18px" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "Merriweather,serif", color: NAVY }}>{(Array.isArray(clients) ? clients : []).filter(c => c.email).length}</div>
                  <div style={{ fontSize: 12, color: SLATE, marginTop: 2 }}>Clients monitored</div>
                </div>
              </div>
            </div>
            <div style={S.settingsCard}>
              <div style={S.settingsCardTitle}>Notifications</div>
              <div style={S.settingsCardSub}>Manage your inbox notifications. Clear all or dismiss individual replies from the bell icon in the nav.</div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 16 }}>
                <div style={{ fontSize: 13, color: NAVY }}><strong>{(Array.isArray(notifications) ? notifications : []).length}</strong> notifications · <strong>{(Array.isArray(notifications) ? notifications : []).filter(n => !n.read).length}</strong> unread</div>
                <button onClick={() => saveNotifications && saveNotifications([])} style={{ ...S.btnGhost, fontSize: 12 }}>Clear All Notifications</button>
              </div>
            </div>
          </div>
        )}
        {settingsTab === "proposals" && (
          (currentUser?.role === "Admin" || currentUser?.role === "admin") ? (
          <div>
            <div style={{ marginBottom: 20 }}><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Proposal Settings</h2><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Customize what the AI includes when generating proposals.</p></div>
            <div style={S.settingsCard}>
              <div style={S.settingsCardTitle}>Company Positioning</div>
              <div style={S.settingsCardSub}>This context is sent to the AI every time a proposal is generated.</div>
              <div style={{ display: "grid", gap: 14 }}>
                <div><label style={S.label}>Company Tagline</label><input defaultValue="Empowering private clubs to grow membership through elite sales talent and AI." style={S.input} /></div>
                <div><label style={S.label}>Key Differentiators (one per line)</label><textarea rows={5} defaultValue={"Built exclusively for private clubs\nCombines AI technology with human expertise\nProven sales process developed over 20+ years\nFull-service — from software to staffing"} style={{ ...S.input, resize: "vertical", lineHeight: 1.7 }} /></div>
                <div><label style={S.label}>Proof Points / Social Proof</label><textarea rows={4} defaultValue={"Trusted by leading private clubs across the Southeast\nAverage 34% increase in membership conversion rates\nClubs typically see ROI within 90 days"} style={{ ...S.input, resize: "vertical", lineHeight: 1.7 }} /></div>
                <div><label style={S.label}>Closing / CTA Language</label><input defaultValue="Let's schedule a 30-minute strategy call to confirm the details and get you started." style={S.input} /></div>
              </div>
              <button onClick={() => showToast("Proposal settings saved")} style={{ ...S.btnPrimary, marginTop: 16 }}>Save Settings</button>
            </div>
          </div>
          ) : (
            <div style={{ padding: 32, color: SLATE, fontSize: 14 }}>
              Only admins can manage this section.
            </div>
          )
        )}
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function CalendarPage({
  calMonth, calView, setCalView, calEvents, showAddEvent, eventForm, selectedCalEvent, gcalSyncing, gcalLastSynced, currentUser, leads, clients, users,
  setCalMonth, setEventForm, setShowAddEvent, setSelectedCalEvent, setGcalLastSynced, setSelectedLead, setSelectedClient, setPage,
  saveCalEvents, showToast, syncGoogleCalendar, userId,
}) {
  const [endHour, setEndHour] = useState("10");
  const [endMin, setEndMin] = useState("00");
  const [endAmPm, setEndAmPm] = useState("AM");
  const year = calMonth.getFullYear(), month = calMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = new Date().toISOString().split("T")[0];
  const EVENT_TYPES = ["Follow-Up","Demo","Meeting","Proposal Review","Renewal","Onboarding","Call","Other"];
  const EVENT_COLORS = { "Follow-Up": GOLD, "Demo": "#7C3AED", "Meeting": "#0369A1", "Proposal Review": "#0891B2", "Renewal": GREEN, "Onboarding": "#059669", "Call": "#C2410C", "Other": SLATE };

  const handleAddEvent = async () => {
    if (!eventForm.title || !eventForm.date) return;
    const endH = endAmPm === "PM" && endHour !== "12" ? +endHour + 12 : endAmPm === "AM" && endHour === "12" ? 0 : +endHour;
    const endTime = `${String(endH).padStart(2, "0")}:${endMin}`;
    const newEvent = { id: uid(), ...eventForm, endTime, assignedTo: eventForm.assignedTo || currentUser.id };
    saveCalEvents([...calEvents, newEvent]);
    setShowAddEvent(false);
    setEventForm({ title: "", date: "", time: "", type: "Follow-Up", leadId: "", clientId: "", assignedTo: "", notes: "" });
    setEndHour("10");
    setEndMin("00");
    setEndAmPm("AM");

    // Push to Google Calendar
    try {
      const resp = await fetch("/api/gcal-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "createEvent",
          user_id: userId,
          title: newEvent.title,
          date: newEvent.date,
          time: newEvent.time || "",
          endTime,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          notes: newEvent.notes || "",
          type: newEvent.type || "",
        }),
      });
      const data = await resp.json();
      if (resp.ok && data.eventId) {
        const updatedEvent = { ...newEvent, id: "gcal-" + data.eventId, fromGcal: true };
        saveCalEvents([...calEvents, newEvent].map(e => e.id === newEvent.id ? updatedEvent : e));
        showToast("Event added and synced to Google Calendar ✓");
      } else if (resp.ok) {
        showToast("Event added and synced to Google Calendar ✓");
      } else {
        showToast("Event saved locally — Google Calendar sync failed: " + (data.error || "Unknown error"), "error");
      }
    } catch (err) {
      showToast("Event saved locally — could not reach Google Calendar", "error");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (eventId?.startsWith("gcal-")) {
      try {
        await fetch("/api/gcal-sync", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "deleteEvent",
            user_id: userId,
            eventId,
          }),
        });
      } catch (err) {
        console.error("Failed to delete from Google Calendar:", err);
      }
    }
    saveCalEvents(calEvents.filter(e => e.id !== eventId));
    setSelectedCalEvent(null);
    showToast("Event deleted");
  };

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    return calEvents.filter(e => e.date === dateStr);
  };
  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const upcomingEvents = [...calEvents].filter(e => e.date >= todayStr).sort((a,b) => a.date.localeCompare(b.date)).slice(0, 10);

  return (
    <div style={{ padding: "24px 32px 24px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, paddingRight: 16 }}>
        <div><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 22, color: NAVY }}>Calendar</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Schedule follow-ups, demos, and meetings. Syncs with Google Calendar.</p></div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={syncGoogleCalendar} disabled={gcalSyncing} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "transparent", border: "1px solid " + BORDER, borderRadius: 8, fontSize: 12, color: gcalSyncing ? GOLD : SLATE, cursor: "pointer", fontFamily: "Open Sans, sans-serif" }}>
            <span style={{ display: "inline-block", animation: gcalSyncing ? "spin 1s linear infinite" : "none" }}>⟳</span>
            {gcalSyncing ? "Syncing..." : "Sync Google Calendar"}
          </button>
          <button onClick={() => setShowAddEvent(true)} style={S.btnPrimary}>+ Add Event</button>
        </div>
      </div>
      {showAddEvent && (
        <div style={S.modal}><div style={{ ...S.modalBox, width: 500 }}>
          <h3 style={{ margin: "0 0 20px", fontFamily: "Merriweather,serif", fontSize: 18, color: NAVY }}>Add Calendar Event</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Event Title *</label><input value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} placeholder="Demo call with Oakmont CC" style={S.input} /></div>
            <div><label style={S.label}>Date *</label><input type="date" value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})} style={S.input} /></div>
            <div>
              <label style={S.label}>Time</label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <select
                  value={(() => {
                    const h = parseInt(eventForm.time?.split(":")[0] || "9");
                    return h === 0 ? "12" : h > 12 ? String(h - 12) : String(h);
                  })()}
                  onChange={e => {
                    const isPM = (eventForm.time?.split(":")[0] || "9") >= 12;
                    let h = parseInt(e.target.value);
                    if (isPM) h = h === 12 ? 12 : h + 12;
                    else h = h === 12 ? 0 : h;
                    setEventForm({ ...eventForm, time: `${String(h).padStart(2,"0")}:${eventForm.time?.split(":")[1] || "00"}` });
                  }}
                  style={{ ...S.input, width: 70 }}
                >
                  {["12","1","2","3","4","5","6","7","8","9","10","11"].map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                <span style={{ color: SLATE, fontWeight: 700 }}>:</span>
                <select
                  value={eventForm.time?.split(":")[1] || "00"}
                  onChange={e => setEventForm({ ...eventForm, time: `${eventForm.time?.split(":")[0] || "09"}:${e.target.value}` })}
                  style={{ ...S.input, width: 70 }}
                >
                  {["00", "15", "30", "45"].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select
                  value={parseInt(eventForm.time?.split(":")[0] || "9") >= 12 ? "PM" : "AM"}
                  onChange={e => {
                    let h = parseInt(eventForm.time?.split(":")[0] || "9");
                    const mins = eventForm.time?.split(":")[1] || "00";
                    if (e.target.value === "PM" && h < 12) h = h + 12;
                    else if (e.target.value === "AM" && h >= 12) h = h - 12;
                    setEventForm({ ...eventForm, time: `${String(h).padStart(2,"0")}:${mins}` });
                  }}
                  style={{ ...S.input, width: 70 }}
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
            <div>
              <label style={S.label}>End Time</label>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <select value={endHour} onChange={e => setEndHour(e.target.value)} style={{ ...S.input, width: 70 }}>
                  {["1","2","3","4","5","6","7","8","9","10","11","12"].map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <span style={{ color: SLATE, fontWeight: 700 }}>:</span>
                <select value={endMin} onChange={e => setEndMin(e.target.value)} style={{ ...S.input, width: 70 }}>
                  {["00","15","30","45"].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select value={endAmPm} onChange={e => setEndAmPm(e.target.value)} style={{ ...S.input, width: 70 }}>
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
            <div><label style={S.label}>Event Type</label><select value={eventForm.type} onChange={e => setEventForm({...eventForm, type: e.target.value})} style={S.input}>{EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
            <div><label style={S.label}>Assign To</label><select value={eventForm.assignedTo} onChange={e => setEventForm({...eventForm, assignedTo: e.target.value})} style={S.input}><option value="">— Select Rep —</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select></div>
            <div><label style={S.label}>Linked Lead</label><select value={eventForm.leadId} onChange={e => setEventForm({...eventForm, leadId: e.target.value})} style={S.input}><option value="">— None —</option>{leads.map(l => <option key={l.id} value={l.id}>{l.clubName}</option>)}</select></div>
            <div><label style={S.label}>Linked Client</label><select value={eventForm.clientId} onChange={e => setEventForm({...eventForm, clientId: e.target.value})} style={S.input}><option value="">— None —</option>{clients.map(c => <option key={c.id} value={c.id}>{c.clubName}</option>)}</select></div>
            <div style={{ gridColumn: "1/-1" }}><label style={S.label}>Notes</label><textarea value={eventForm.notes} onChange={e => setEventForm({...eventForm, notes: e.target.value})} rows={3} style={{ ...S.input, resize: "vertical" }} /></div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <button onClick={() => { setShowAddEvent(false); setEndHour("10"); setEndMin("00"); setEndAmPm("AM"); }} style={S.btnGhost}>Cancel</button>
            <button onClick={handleAddEvent} style={S.btnPrimary}>Add Event</button>
          </div>
        </div></div>
      )}
      {selectedCalEvent && (
        <div style={S.modal}><div style={{ ...S.modalBox, width: 420 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: EVENT_COLORS[selectedCalEvent.type] || SLATE, flexShrink: 0 }} />
              <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 17, color: NAVY }}>{selectedCalEvent.title}</div>
            </div>
            <button onClick={() => setSelectedCalEvent(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: SLATE }}>×</button>
          </div>
          <div style={{ background: BEIGE, borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
            {[["Date", fmt(selectedCalEvent.date)], ["Time", selectedCalEvent.time || "All day"], ["Type", selectedCalEvent.type], ["Assigned", users.find(u => u.id === selectedCalEvent.assignedTo)?.name || "—"]].map(([k,v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13 }}><span style={{ color: SLATE }}>{k}</span><span style={{ fontWeight: 600, color: NAVY }}>{v}</span></div>
            ))}
          </div>
          {selectedCalEvent.notes && <div style={{ fontSize: 13, color: SLATE, lineHeight: 1.7, marginBottom: 16 }}>{selectedCalEvent.notes}</div>}
          {(selectedCalEvent.leadId || selectedCalEvent.clientId) && (
            <button onClick={() => { const lead = leads.find(l => sameId(l.id, selectedCalEvent.leadId)); const client = clients.find(c => sameId(c.id, selectedCalEvent.clientId)); setSelectedCalEvent(null); if (lead) { setSelectedLead(lead); setPage("lead-detail"); } else if (client) { setSelectedClient(client); setPage("client-detail"); } }} style={{ ...S.btnGold, width: "100%", justifyContent: "center", marginBottom: 12 }}>
              View {selectedCalEvent.leadId ? "Lead" : "Client"} Profile →
            </button>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => handleDeleteEvent(selectedCalEvent.id)} style={{ ...S.btnDanger, flex: 1 }}>Remove Event</button>
            <button onClick={() => setSelectedCalEvent(null)} style={{ ...S.btnGhost, flex: 1 }}>Close</button>
          </div>
        </div></div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 24 }}>
        <div style={{ ...S.card, width: "100%" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid " + BORDER, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button onClick={() => setCalMonth(new Date(year, month-1, 1))} style={{ ...S.btnGhost, padding: "6px 12px" }}>‹</button>
              <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 16, color: NAVY }}>{monthNames[month]} {year}</div>
              <button onClick={() => setCalMonth(new Date(year, month+1, 1))} style={{ ...S.btnGhost, padding: "6px 12px" }}>›</button>
            </div>
            <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
              <button onClick={() => setCalView("month")} style={{ padding: "4px 14px", borderRadius: 6, border: "1px solid " + GOLD, background: calView === "month" ? GOLD : "transparent", color: calView === "month" ? WHITE : GOLD, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Month</button>
              <button onClick={() => setCalView("week")} style={{ padding: "4px 14px", borderRadius: 6, border: "1px solid " + GOLD, background: calView === "week" ? GOLD : "transparent", color: calView === "week" ? WHITE : GOLD, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Week</button>
            </div>
          </div>
          <div style={{ padding: "0 8px 8px", width: "100%" }}>
            {calView === "week" ? (() => {
              const today = new Date();
              const startOfWeek = new Date(today);
              startOfWeek.setDate(today.getDate() - today.getDay());
              const weekDays = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(startOfWeek);
                d.setDate(startOfWeek.getDate() + i);
                return d;
              });
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
                  {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
                    <div key={d} style={{ textAlign: "center", fontWeight: 700, fontSize: 12, color: SLATE, padding: "6px 0", borderBottom: "1px solid #e5e0d6" }}>{d}</div>
                  ))}
                  {weekDays.map((d, i) => {
                    const ds = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                    const dayEvents = calEvents.filter(e => e.date === ds || (e.start && e.start.startsWith && e.start.startsWith(ds)));
                    const isToday = ds === new Date().toISOString().slice(0, 10);
                    return (
                      <div key={i} style={{ minHeight: 120, background: isToday ? "#f0eadd" : "#fff", border: "1px solid #e5e0d6", borderRadius: 6, padding: 6 }}>
                        <div style={{ fontWeight: isToday ? 800 : 500, fontSize: 13, color: isToday ? GOLD : NAVY, marginBottom: 4 }}>{d.getDate()}</div>
                        {dayEvents.map(ev => (
                          <div key={ev.id} onClick={() => setSelectedCalEvent(ev)} style={{ fontSize: 11, background: NAVY, color: WHITE, borderRadius: 4, padding: "2px 5px", marginBottom: 2, cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.title}</div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })() : (
            <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", textAlign: "center", padding: "10px 0", width: "100%", maxWidth: "none" }}>
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} style={{ fontSize: 11, fontWeight: 700, color: SLATE, padding: "4px 0" }}>{d}</div>)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, width: "100%", maxWidth: "none" }}>
              {Array.from({ length: firstDay }, (_, i) => <div key={"e"+i} />)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateStr = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                const dayEvents = getEventsForDay(day);
                const isToday = dateStr === todayStr;
                return (
                  <div key={day} style={{ minHeight: 100, padding: "6px 4px", borderRadius: 8, background: isToday ? "#FFFCF5" : "transparent", border: isToday ? "2px solid " + GOLD : "1px solid " + BORDER, cursor: "pointer", position: "relative" }}
                    onClick={() => { setEventForm({ ...eventForm, date: dateStr }); setShowAddEvent(true); }}>
                    <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 400, color: isToday ? GOLD : NAVY, marginBottom: 4 }}>{day}</div>
                    {dayEvents.slice(0,3).map((ev, ei) => (
                      <div key={ei} onClick={e => { e.stopPropagation(); setSelectedCalEvent(ev); }} style={{ fontSize: 10, fontWeight: 600, padding: "2px 5px", borderRadius: 4, marginBottom: 2, background: (EVENT_COLORS[ev.type] || SLATE) + "20", color: EVENT_COLORS[ev.type] || SLATE, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{ev.title}</div>
                    ))}
                    {dayEvents.length > 3 && <div style={{ fontSize: 9, color: SLATE, marginTop: 2 }}>+{dayEvents.length - 3} more</div>}
                  </div>
                );
              })}
            </div>
            </>
            )}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginLeft: 8 }}>
          <div style={{ ...S.card, padding: "18px 20px" }}>
            <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 14 }}>Upcoming Events</div>
            {upcomingEvents.length === 0 ? <div style={{ fontSize: 13, color: SLATE, fontStyle: "italic" }}>No upcoming events.</div> :
              upcomingEvents.map(ev => {
                const rep = users.find(u => u.id === ev.assignedTo);
                return (
                  <div key={ev.id} onClick={() => setSelectedCalEvent(ev)} style={{ padding: "10px 0", borderBottom: "1px solid " + BORDER, cursor: "pointer" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: EVENT_COLORS[ev.type] || SLATE, flexShrink: 0, marginTop: 3 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{ev.title}</div>
                        <div style={{ fontSize: 11, color: SLATE }}>{fmt(ev.date)}{ev.time ? " · " + ev.time : ""}</div>
                        {rep && <div style={{ fontSize: 10, color: rep.color, fontWeight: 600, marginTop: 2 }}>{rep.name}</div>}
                        {ev.fromGcal && <span style={{ fontSize: 9, background: "#EEF2FF", color: "#3730A3", padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>Google Calendar</span>}
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>
          {gcalLastSynced && (
            <div style={{ padding: "10px 14px", background: GREEN_BG, border: "1px solid #86EFAC", borderRadius: 10, fontSize: 11, color: GREEN }}>
              ✓ Synced {new Date(gcalLastSynced).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Dashboard({ leads, clients, stages, clientStatuses, users, setPage, setSelectedLead, setSelectedClient }) {
  const [dateRange, setDateRange] = useState("all");
  const now = new Date();
  const filterDate = (d) => {
    if (dateRange === "all" || !d) return true;
    const diff = now - new Date(d);
    if (dateRange === "7d") return diff <= 7 * 86400000;
    if (dateRange === "30d") return diff <= 30 * 86400000;
    if (dateRange === "90d") return diff <= 90 * 86400000;
    return true;
  };
  const filteredL = leads.filter(l => filterDate(l.createdAt));
  const closedWon = filteredL.filter(l => l.stage === "Closed Won");
  const activeMRR = clients.filter(c => c.status === "Active").reduce((s, c) => s + Number(c.value), 0);
  const atRisk = clients.filter(c => c.status === "At Risk").reduce((s, c) => s + Number(c.value), 0);
  const convRate = filteredL.length ? Math.round((closedWon.length / filteredL.length) * 100) : 0;
  const stageCounts = stages.map(s => ({ stage: s, count: leads.filter(l => l.stage === s).length, value: leads.filter(l => l.stage === s).reduce((a, l) => a + Number(l.value || 0), 0) }));
  const maxS = Math.max(...stageCounts.map(s => s.count), 1);
  const recentLeads = [...leads].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);
  const sourceData = Object.entries(leads.reduce((acc, l) => { const src = l.source || "Unknown"; acc[src] = (acc[src] || { count: 0, value: 0 }); acc[src].count++; acc[src].value += Number(l.value || 0); return acc; }, {})).sort((a, b) => b[1].count - a[1].count);
  const repData = users.map(u => ({ ...u, leads: leads.filter(l => l.assignedTo === u.id).length, won: leads.filter(l => l.assignedTo === u.id && l.stage === "Closed Won").length, mrr: clients.filter(c => c.assignedTo === u.id && c.status === "Active").reduce((s, c) => s + Number(c.value), 0) }));
  const upcomingRenewals = clients.filter(c => { if (!c.renewalDate) return false; const diff = (new Date(c.renewalDate) - now) / 86400000; return diff > 0 && diff <= 90; }).sort((a, b) => new Date(a.renewalDate) - new Date(b.renewalDate));

  // Top stat cards: counts and deltas
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const leadsAddedThisMonth = leads.filter(l => l.createdAt && new Date(l.createdAt) >= startOfMonth).length;
  const demoScheduledCount = leads.filter(l => l.stage === "Demo Scheduled").length;
  const proposalsSentCount = leads.filter(l => l.stage === "Proposal Sent").length;
  const followUpsDueCount = leads.filter(l => l.followUp && new Date(l.followUp) < now).length;

  const StatCard = ({ label, value, delta, deltaIsRed, icon }) => (
    <div
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(12,35,64,0.12)";
        const bar = e.currentTarget.querySelector(".stat-card-bar");
        if (bar) bar.style.opacity = "1";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 1px 4px rgba(12,35,64,0.06)";
        const bar = e.currentTarget.querySelector(".stat-card-bar");
        if (bar) bar.style.opacity = "0";
      }}
      style={{
        position: "relative",
        background: WHITE,
        border: `1px solid ${BEIGE_DRK}`,
        borderRadius: 10,
        padding: "18px 20px",
        boxShadow: "0 1px 4px rgba(12,35,64,0.06)",
        transition: "transform 0.2s, box-shadow 0.2s",
        overflow: "hidden",
      }}
    >
      <div className="stat-card-bar" style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3, background: `linear-gradient(90deg, ${NAVY}, ${GOLD})`, opacity: 0, transition: "opacity 0.2s" }} />
      {icon != null && <div style={{ position: "absolute", top: 12, right: 16, fontSize: 32, opacity: 0.1, pointerEvents: "none", lineHeight: 1 }}>{icon}</div>}
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: SLATE, fontFamily: "'Open Sans', sans-serif", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: NAVY, fontFamily: "'Merriweather', serif", lineHeight: 1.1 }}>{value}</div>
      {delta != null && <div style={{ fontSize: 11, marginTop: 4, color: deltaIsRed ? RED : GREEN }}>{delta}</div>}
    </div>
  );

  const KPI = ({ label, value, sub, color }) => (
    <div style={{ ...S.card, padding: "20px 24px" }}>
      <div style={{ fontSize: 11, color: SLATE, fontWeight: 600, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || NAVY, fontFamily: "Merriweather,serif" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: SLATE, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
        <StatCard label="Total Leads" value={leads.length} delta={leadsAddedThisMonth > 0 ? `+${leadsAddedThisMonth} this month` : null} deltaIsRed={false} icon="👥" />
        <StatCard label="Demo Scheduled" value={demoScheduledCount} icon="📅" />
        <StatCard label="Proposals Sent" value={proposalsSentCount} icon="📄" />
        <StatCard label="Follow-ups Due" value={followUpsDueCount} delta={followUpsDueCount > 0 ? `${followUpsDueCount} overdue` : null} deltaIsRed={true} icon="⏰" />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 22, color: NAVY }}>Dashboard</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {[["7d","7D"],["30d","30D"],["90d","90D"],["all","All"]].map(([v,l]) => (
            <button key={v} onClick={() => setDateRange(v)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid " + (dateRange === v ? GOLD : BORDER), background: dateRange === v ? "#FFFCF5" : WHITE, color: dateRange === v ? GOLD : SLATE, fontSize: 12, fontWeight: dateRange === v ? 700 : 400, cursor: "pointer", fontFamily: "Open Sans,sans-serif" }}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14, marginBottom: 22 }}>
        <KPI label="Total Leads" value={filteredL.length} sub={closedWon.length + " closed won"} />
        <KPI label="Conversion Rate" value={convRate + "%"} color={convRate > 20 ? GREEN : convRate > 10 ? "#C2410C" : SLATE} />
        <KPI label="Active MRR" value={fmtVal(activeMRR)} sub="from active clients" color={NAVY} />
        <KPI label="Annual Run Rate" value={"$" + ((activeMRR * 12) / 1000).toFixed(0) + "k"} color={GREEN} />
        <KPI label="At-Risk MRR" value={fmtVal(atRisk)} sub={clients.filter(c => c.status === "At Risk").length + " clients"} color={atRisk > 0 ? RED : SLATE} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
        <div style={{ ...S.card, padding: "22px 26px" }}>
          <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 18 }}>Pipeline Funnel</div>
          {stageCounts.map(({ stage, count, value }) => (
            <div key={stage} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, alignItems: "center" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}><span style={S.stagePill(stage)}>{stage}</span><span style={{ fontSize: 12, color: SLATE }}>{count}</span></div>
                <span style={{ fontSize: 12, fontWeight: 700, color: NAVY }}>{value > 0 ? fmtVal(value) + "/mo" : "—"}</span>
              </div>
              <div style={{ background: BEIGE_DARK, borderRadius: 4, height: 5 }}><div style={{ width: (count / maxS * 100) + "%", background: (STAGE_COLORS[stage] || {}).text || GOLD, borderRadius: 4, height: 5 }} /></div>
            </div>
          ))}
        </div>
        <div style={{ ...S.card, padding: "22px 26px" }}>
          <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 18 }}>Client Health</div>
          {clientStatuses.map(st => {
            const count = clients.filter(c => c.status === st).length;
            const mrr = clients.filter(c => c.status === st).reduce((s, c) => s + Number(c.value), 0);
            return (
              <div key={st} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid " + BORDER }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={S.statusPill(st)}>{st}</span><span style={{ fontSize: 12, color: SLATE }}>{count} client{count !== 1 ? "s" : ""}</span></div>
                <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{fmtVal(mrr)}/mo</span>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
        <div style={{ ...S.card, padding: "22px 26px" }}>
          <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 16 }}>Lead Source ROI</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Source","Leads","Pipeline","Avg Deal"].map(h => <th key={h} style={{ ...S.th, fontSize: 11, padding: "8px 10px" }}>{h}</th>)}</tr></thead>
            <tbody>
              {sourceData.map(([src, d]) => (<tr key={src}><td style={{ ...S.td, fontSize: 12, fontWeight: 600, padding: "8px 10px" }}>{src}</td><td style={{ ...S.td, fontSize: 12, textAlign: "center", padding: "8px 10px" }}>{d.count}</td><td style={{ ...S.td, fontSize: 12, padding: "8px 10px" }}>{fmtVal(d.value)}</td><td style={{ ...S.td, fontSize: 12, padding: "8px 10px" }}>{fmtVal(d.value / d.count)}</td></tr>))}
              {!sourceData.length && <tr><td colSpan={4} style={{ ...S.td, color: SLATE, fontSize: 12, textAlign: "center", padding: 20 }}>No data yet</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={{ ...S.card, padding: "22px 26px" }}>
          <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 16 }}>Rep Performance</div>
          {repData.map(rep => {
            const wr = rep.leads ? Math.round((rep.won / rep.leads) * 100) : 0;
            return (
              <div key={rep.id} style={{ padding: "10px 0", borderBottom: "1px solid " + BORDER }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 28, height: 28, borderRadius: "50%", background: rep.color, color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{rep.initials}</div><div><div style={{ fontSize: 13, fontWeight: 600 }}>{rep.name}</div><div style={{ fontSize: 11, color: SLATE }}>{rep.leads} leads · {rep.won} won · {fmtVal(rep.mrr)}/mo</div></div></div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: wr > 25 ? GREEN : wr > 10 ? GOLD : SLATE }}>{wr}%</span>
                </div>
                <div style={{ background: BEIGE_DARK, borderRadius: 4, height: 5 }}><div style={{ width: wr + "%", background: wr > 25 ? GREEN : wr > 10 ? GOLD : RED, borderRadius: 4, height: 5 }} /></div>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        <div style={S.card}>
          <div style={{ padding: "16px 22px", borderBottom: "1px solid " + BORDER, display: "flex", justifyContent: "space-between", alignItems: "center" }}><div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY }}>Recent Leads</div><button onClick={() => setPage("leads")} style={{ ...S.btnGold, padding: "5px 12px", fontSize: 12 }}>View All</button></div>
          {recentLeads.map(l => (<div key={l.id} onClick={() => { setSelectedLead(l); setPage("lead-detail"); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 22px", borderBottom: "1px solid " + BORDER, cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#FAFAF7"} onMouseLeave={e => e.currentTarget.style.background = ""}><div style={S.avatar}>{initials(l.contactName)}</div><div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 13 }}>{l.clubName}</div><div style={{ fontSize: 11, color: SLATE }}>{l.contactName} · {l.source || "—"}</div></div><span style={S.stagePill(l.stage)}>{l.stage}</span></div>))}
          {!recentLeads.length && <div style={{ padding: 32, textAlign: "center", color: SLATE, fontSize: 13 }}>No leads yet.</div>}
        </div>
        <div style={S.card}>
          <div style={{ padding: "16px 22px", borderBottom: "1px solid " + BORDER, display: "flex", justifyContent: "space-between", alignItems: "center" }}><div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY }}>Upcoming Renewals <span style={{ fontSize: 12, fontWeight: 400, color: SLATE }}>(90 days)</span></div><button onClick={() => setPage("clients")} style={{ ...S.btnGold, padding: "5px 12px", fontSize: 12 }}>View All</button></div>
          {upcomingRenewals.map(c => { const days = Math.round((new Date(c.renewalDate) - now) / 86400000); return (<div key={c.id} onClick={() => { setSelectedClient(c); setPage("client-detail"); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 22px", borderBottom: "1px solid " + BORDER, cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#FAFAF7"} onMouseLeave={e => e.currentTarget.style.background = ""}><div style={S.clientAvatar}>{initials(c.contactName)}</div><div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 13 }}>{c.clubName}</div><div style={{ fontSize: 11, color: SLATE }}>{c.service}</div></div><div style={{ textAlign: "right" }}><div style={{ fontSize: 12, fontWeight: 700, color: days < 30 ? RED : days < 60 ? "#C2410C" : GREEN }}>{days}d</div><div style={{ fontSize: 11, color: SLATE }}>{fmt(c.renewalDate)}</div></div></div>); })}
          {!upcomingRenewals.length && <div style={{ padding: 32, textAlign: "center", color: SLATE, fontSize: 13 }}>No renewals in the next 90 days.</div>}
        </div>
      </div>
    </div>
  );
}

const QUICK_PROMPTS = [
  "Give me today's daily brief",
  "Email the cold leads with a reengagement campaign",
  "What clients are at risk of churning?",
  "Draft a follow-up email for Riverview Athletic Club",
  "Which leads should I prioritize today?",
  "Write a renewal email for Stonebrook Golf Club",
  "Suggest an upsell strategy for my active clients",
  "What's my pipeline value this month?",
];

function AgentPage({
  agentInput,
  setAgentInput,
  agentMessages,
  sendAgentMessage,
  agentLoading,
  saveAgentMessages,
  chatEndRef,
  dailyBrief,
  briefLoading,
  briefGenerated,
  generateDailyBrief,
  syncGmailEmails,
  gmailSyncing,
  lastSynced,
  gmailError,
  notifications,
  unreadCount,
  setPage,
  setShowNotifPanel,
  stats,
  clients,
  S,
}) {
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "instant" });
  }, [agentMessages, chatEndRef]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "instant" });
  }, [chatEndRef]);

  const formatAgentText = (text) => {
    return text.split("\n").map((line, i) => {
      if (line.startsWith("**") && line.endsWith("**")) return <div key={i} style={{ fontWeight: 700, color: WHITE, marginTop: i > 0 ? 10 : 0, marginBottom: 4, fontSize: 13 }}>{line.replace(/\*\*/g, "")}</div>;
      if (line.startsWith("## ")) return <div key={i} style={{ fontWeight: 700, color: GOLD, fontSize: 14, marginTop: i > 0 ? 12 : 0, marginBottom: 6 }}>{line.replace("## ", "")}</div>;
      if (line.startsWith("### ")) return <div key={i} style={{ fontWeight: 700, color: "rgba(255,255,255,0.9)", fontSize: 13, marginTop: 10, marginBottom: 4 }}>{line.replace("### ", "")}</div>;
      if (line.match(/^\d+\.\s/)) return <div key={i} style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, lineHeight: 1.7, paddingLeft: 8 }}>{line}</div>;
      if (line.startsWith("- ") || line.startsWith("• ")) return <div key={i} style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, lineHeight: 1.7, paddingLeft: 8 }}>{"• " + line.replace(/^[-•]\s/, "")}</div>;
      if (line.trim() === "") return <div key={i} style={{ height: 6 }} />;
      return <div key={i} style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, lineHeight: 1.7 }}>{line}</div>;
    });
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 20, height: "calc(100vh - 120px)", minHeight: 600 }}>
      <div style={{ background: AGENT_BG, borderRadius: 16, display: "flex", flexDirection: "column", overflow: "hidden", border: "1px solid rgba(197,162,93,0.2)" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg, " + GOLD + ", #8B6914)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>✦</div>
          <div>
            <div style={{ color: WHITE, fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 16 }}>AshGro AI Agent</div>
            <div style={{ color: GOLD, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>Sales & Customer Intelligence</div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22C55E" }} />
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>Live</span>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
          {agentMessages.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✦</div>
              <div style={{ color: WHITE, fontFamily: "Merriweather,serif", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Your AI Sales Agent is ready.</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, lineHeight: 1.7 }}>I have full visibility into your pipeline, leads, and clients. Ask me anything — or use a quick prompt below to get started.</div>
            </div>
          )}
          {(Array.isArray(agentMessages) ? agentMessages : []).map((msg, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "88%", padding: "14px 18px", borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: msg.role === "user" ? "linear-gradient(135deg, " + GOLD + ", #9A7A3A)" : "rgba(255,255,255,0.06)", border: msg.role === "assistant" ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
                {msg.role === "user" ? <div style={{ color: WHITE, fontSize: 13, lineHeight: 1.6 }}>{msg.content}</div> : <div>{formatAgentText(msg.content)}</div>}
              </div>
              <div style={{ color: "rgba(255,255,255,0.25)", fontSize: 10, marginTop: 4, paddingLeft: 4, paddingRight: 4 }}>{msg.ts}</div>
            </div>
          ))}
          {agentLoading && (
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <div style={{ padding: "14px 18px", borderRadius: "18px 18px 18px 4px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {[0, 1, 2].map(n => <div key={n} style={{ width: 7, height: 7, borderRadius: "50%", background: GOLD, animation: "pulse 1.4s infinite", animationDelay: n * 0.2 + "s", opacity: 0.7 }} />)}
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              value={agentInput}
              onChange={e => setAgentInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendAgentMessage(agentInput); }}}
              placeholder="Ask anything about your pipeline..."
              style={{ flex: 1, padding: "12px 16px", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, color: WHITE, fontSize: 13, fontFamily: "Open Sans, sans-serif", outline: "none" }}
            />
            <button onClick={() => sendAgentMessage(agentInput)} disabled={agentLoading || !agentInput.trim()} style={{ width: 44, height: 44, borderRadius: 10, background: agentInput.trim() ? GOLD : "rgba(255,255,255,0.07)", border: "none", cursor: agentInput.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, transition: "background 0.2s", flexShrink: 0 }}>
              ↑
            </button>
          </div>
          <div style={{ marginTop: 8, textAlign: "center" }}>
            <button onClick={() => saveAgentMessages([])} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.2)", fontSize: 11, cursor: "pointer", fontFamily: "Open Sans, sans-serif" }}>Clear conversation</button>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, overflowY: "auto" }}>
        <div style={{ background: NAVY, borderRadius: 14, padding: "22px 24px", border: "1px solid rgba(197,162,93,0.25)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <div style={{ color: WHITE, fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14 }}>📋 Daily Brief</div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, marginTop: 2 }}>{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
            </div>
            <button onClick={generateDailyBrief} disabled={briefLoading} style={{ padding: "7px 14px", background: briefLoading ? "rgba(197,162,93,0.3)" : "rgba(197,162,93,0.15)", color: GOLD, border: "1px solid rgba(197,162,93,0.4)", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: briefLoading ? "default" : "pointer", fontFamily: "Open Sans, sans-serif", whiteSpace: "nowrap" }}>
              {briefLoading ? "Generating..." : briefGenerated ? "↻ Refresh" : "Generate"}
            </button>
          </div>
          {!briefGenerated && !briefLoading && <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, lineHeight: 1.6, textAlign: "center", padding: "12px 0" }}>Click Generate for your AI-powered daily sales brief.</div>}
          {briefLoading && <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, textAlign: "center", padding: "12px 0" }}>Analyzing your pipeline...</div>}
          {briefGenerated && dailyBrief && (
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, lineHeight: 1.8, maxHeight: 240, overflowY: "auto" }}>
              {dailyBrief.split("\n").map((line, i) => {
                if (line.match(/^#{1,3}\s/) || (line.startsWith("**") && line.endsWith("**"))) return <div key={i} style={{ color: GOLD, fontWeight: 700, fontSize: 12, marginTop: 10, marginBottom: 2 }}>{line.replace(/^#+\s/, "").replace(/\*\*/g, "")}</div>;
                if (line.trim() === "") return <div key={i} style={{ height: 4 }} />;
                return <div key={i}>{line}</div>;
              })}
            </div>
          )}
        </div>
        <div style={{ background: WHITE, borderRadius: 14, padding: "20px 22px", border: "1px solid " + BORDER }}>
          <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, color: NAVY, fontSize: 14, marginBottom: 14 }}>⚡ Quick Actions</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {QUICK_PROMPTS.map((prompt, i) => (
              <button key={i} onClick={() => setAgentInput(prompt)} style={{ padding: "10px 14px", background: BEIGE, border: "1px solid " + BORDER, borderRadius: 8, fontSize: 12, color: NAVY, cursor: "pointer", textAlign: "left", fontFamily: "Open Sans, sans-serif", lineHeight: 1.4, transition: "all 0.15s" }} onMouseEnter={e => { e.currentTarget.style.background = BEIGE_DARK; e.currentTarget.style.borderColor = GOLD; }} onMouseLeave={e => { e.currentTarget.style.background = BEIGE; e.currentTarget.style.borderColor = BORDER; }}>
                {prompt}
              </button>
            ))}
          </div>
        </div>
        <div style={{ background: WHITE, borderRadius: 14, padding: "20px 22px", border: "1px solid " + BORDER }}>
          <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, color: NAVY, fontSize: 14, marginBottom: 6 }}>📬 Email Reply Sync</div>
          <div style={{ fontSize: 12, color: SLATE, marginBottom: 14, lineHeight: 1.5 }}>
            Automatically checks Gmail for replies from your leads and clients and logs them to their activity feed.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: lastSynced || gmailError ? 10 : 0 }}>
            <button onClick={syncGmailEmails} disabled={gmailSyncing} style={{ ...S.btnPrimary, flex: 1, justifyContent: "center", background: gmailSyncing ? BEIGE_DARK : NAVY, color: gmailSyncing ? SLATE : WHITE }}>
              <span style={{ display: "inline-block", animation: gmailSyncing ? "spin 1s linear infinite" : "none" }}>⟳</span>
              {gmailSyncing ? "Scanning inbox..." : "Sync Gmail Now"}
            </button>
            {notifications.length > 0 && (
              <button onClick={() => { setPage("dashboard"); setShowNotifPanel(true); }} style={{ ...S.btnGold, padding: "9px 14px" }}>
                🔔 {unreadCount > 0 ? unreadCount + " new" : "View all"}
              </button>
            )}
          </div>
          {gmailError && <div style={{ fontSize: 12, color: RED, background: RED_BG, padding: "8px 12px", borderRadius: 8, border: "1px solid #FCA5A5" }}>{gmailError}</div>}
          {lastSynced && !gmailError && (
            <div style={{ fontSize: 11, color: SLATE, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: GREEN }}>✓</span> Last synced {new Date(lastSynced).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })} · {notifications.length} total replies logged
            </div>
          )}
          {!lastSynced && !gmailError && (
            <div style={{ fontSize: 11, color: SLATE }}>Never synced · {stats.leads + stats.clients} contacts being monitored</div>
          )}
        </div>
        <div style={{ background: WHITE, borderRadius: 14, padding: "20px 22px", border: "1px solid " + BORDER }}>
          <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, color: NAVY, fontSize: 14, marginBottom: 14 }}>📊 Pipeline Snapshot</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[{ label: "Total Leads", value: stats.leads, color: NAVY }, { label: "Active Opps", value: stats.active, color: "#1D4ED8" }, { label: "Clients", value: stats.clients, color: GREEN }, { label: "Active MRR", value: stats.mrr, color: GOLD }].map(({ label, value, color }) => (
              <div key={label} style={{ background: BEIGE, borderRadius: 8, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, color: SLATE, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color, fontFamily: "Merriweather,serif" }}>{value}</div>
              </div>
            ))}
          </div>
          {clients.filter(c => c.status === "At Risk").length > 0 && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 8, fontSize: 12, color: "#C2410C", display: "flex", alignItems: "center", gap: 8 }}>
              ⚠ {clients.filter(c => c.status === "At Risk").length} client{clients.filter(c => c.status === "At Risk").length > 1 ? "s" : ""} at risk
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KbEntryCard({ entry, S, setKbForm, setEditingKb, setShowAddKbEntry, saveKnowledgeBase, knowledgeBase }) {
  const [expanded, setExpanded] = useState(false);
  const wordCount = entry.content.split(/\s+/).filter(Boolean).length;
  const CatPill = ({ cat }) => <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: (KB_CAT_COLORS[cat] || KB_CAT_COLORS["Other"]).bg, color: (KB_CAT_COLORS[cat] || KB_CAT_COLORS["Other"]).text }}>{cat}</span>;
  return (
    <div style={{ background: WHITE, border: "1px solid " + BORDER, borderRadius: 10, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: NAVY }}>{entry.title}</span>
            <CatPill cat={entry.category} />
          </div>
          <div style={{ fontSize: 12, color: SLATE, marginBottom: 6, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: expanded ? 999 : 2, WebkitBoxOrient: "vertical" }}>
            {entry.content}
          </div>
          <div style={{ display: "flex", gap: 12, fontSize: 11, color: SLATE }}>
            <span>{wordCount} words</span>
            <span>·</span>
            <span>Added {entry.createdAt}</span>
            {entry.updatedAt && <><span>·</span><span>Updated {entry.updatedAt}</span></>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          <button onClick={() => setExpanded(!expanded)} style={{ ...S.btnSm, fontSize: 11 }}>{expanded ? "Collapse" : "Preview"}</button>
          <button onClick={() => { setKbForm({ title: entry.title, category: entry.category, content: entry.content }); setEditingKb(entry.id); setShowAddKbEntry(true); }} style={{ ...S.btnSm, fontSize: 11 }}>Edit</button>
          <button onClick={() => { if (window.confirm("Remove this entry?")) saveKnowledgeBase(knowledgeBase.filter(e => e.id !== entry.id)); }} style={{ ...S.btnDanger, padding: "5px 10px", fontSize: 11 }}>Remove</button>
        </div>
      </div>
      {expanded && (
        <div style={{ padding: "0 18px 16px" }}>
          <div style={{ background: BEIGE, borderRadius: 8, padding: "14px 16px", fontSize: 12, color: NAVY, lineHeight: 1.8, whiteSpace: "pre-wrap", maxHeight: 360, overflowY: "auto" }}>
            {entry.content}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AshGroCRM({ onLogout, userId, userEmail }) {
  const [leads, setLeads] = useState(SAMPLE_LEADS);
  const [clients, setClients] = useState(SAMPLE_CLIENTS);
  const [page, setPage] = useState("dashboard");
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [leadSources, setLeadSources] = useState(DEFAULT_LEAD_SOURCES);
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [clientStatuses, setClientStatuses] = useState(DEFAULT_CLIENT_STATUSES);
  const [customLeadFields, setCustomLeadFields] = useState([]);
  const [customClientFields, setCustomClientFields] = useState([]);
  const [companyName, setCompanyName] = useState("AshGro Partners");
  const [emailSignature, setEmailSignature] = useState("");
  const [companyEmail, setCompanyEmail] = useState("sales@ashgropartners.com");
  const [settingsTab, setSettingsTab] = useState("fields");
  const [search, setSearch] = useState(""); const [clientSearch, setClientSearch] = useState("");
  const leadSearchInputRef = useRef(null);
  const [filterStage, setFilterStage] = useState([]); const [filterStatus, setFilterStatus] = useState("All");
  const [showAddLead, setShowAddLead] = useState(false); const [showAddClient, setShowAddClient] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false); const [showConvert, setShowConvert] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_showFilterLead, _setShowFilterLead] = useState(false); const [showFilterClient, setShowFilterClient] = useState(false);
  const [newActivity, setNewActivity] = useState({ type: "Phone Call", content: "" });
  const [activityTarget, setActivityTarget] = useState("lead");
  const [convertData, setConvertData] = useState({ startDate: "", renewalDate: "", status: "Active" });
  const [newLead, setNewLead] = useState({ clubName: "", contactName: "", email: "", phone: "", title: "", stage: "New Lead", service: "", source: "", followUp: "", value: "", assignedTo: "" });
  const [newClient, setNewClient] = useState({ clubName: "", contactName: "", email: "", phone: "", title: "", service: "", source: "", status: "Active", startDate: "", renewalDate: "", value: "", assignedTo: "" });
  const [checkedLeads, setCheckedLeads] = useState([]); const [checkedClients, setCheckedClients] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkPreview, setBulkPreview] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkStep, setBulkStep] = useState("upload");
  const [intakeLeads, setIntakeLeads] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [showAddKbEntry, setShowAddKbEntry] = useState(false);
  const [kbForm, setKbForm] = useState({ title: "", category: "Services & Pricing", content: "" });
  const [editingKb, setEditingKb] = useState(null);
  const [kbSearch, setKbSearch] = useState("");
  const kbSearchInputRef = useRef(null);
  const [kbFilter, setKbFilter] = useState("All");
  const [leadPage, setLeadPage] = useState(1); const [clientPage, setClientPage] = useState(1);
  const [leadSortColumn, setLeadSortColumn] = useState(() => {
    if (typeof localStorage === "undefined") return null;
    try {
      const parsed = JSON.parse(localStorage.getItem("ashgro_leads_sort") || "null");
      return typeof parsed?.column === "string" ? parsed.column : null;
    } catch (_) {
      return null;
    }
  });
  const [leadSortDir, setLeadSortDir] = useState(() => {
    if (typeof localStorage === "undefined") return "asc";
    try {
      const parsed = JSON.parse(localStorage.getItem("ashgro_leads_sort") || "null");
      return parsed?.dir === "desc" ? "desc" : "asc";
    } catch (_) {
      return "asc";
    }
  });
  const [clientSortColumn, setClientSortColumn] = useState(() => {
    if (typeof localStorage === "undefined") return null;
    try {
      const parsed = JSON.parse(localStorage.getItem("ashgro_clients_sort") || "null");
      return typeof parsed?.column === "string" ? parsed.column : null;
    } catch (_) {
      return null;
    }
  });
  const [clientSortDir, setClientSortDir] = useState(() => {
    if (typeof localStorage === "undefined") return "asc";
    try {
      const parsed = JSON.parse(localStorage.getItem("ashgro_clients_sort") || "null");
      return parsed?.dir === "desc" ? "desc" : "asc";
    } catch (_) {
      return "asc";
    }
  });
  const [pwForm, setPwForm] = useState({ newPw: "", confirm: "" });
  const [pwUpdating, setPwUpdating] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(readHashTypeRecovery);
  const [recoveryPwForm, setRecoveryPwForm] = useState({ newPw: "", confirm: "" });
  const [recoveryPwSubmitting, setRecoveryPwSubmitting] = useState(false);
  const [newFieldForm, setNewFieldForm] = useState({ label: "", type: "Text", required: false, options: "", target: "lead" });
  const [showNewField, setShowNewField] = useState(false); const [editingField, setEditingField] = useState(null);
  const [editingList, setEditingList] = useState(null);
  const [toast, setToast] = useState(null);

  // Team / Users
  const DEFAULT_USERS = [
    { id: "u1", name: "Alex Morgan", email: "alex@ashgropartners.com", role: "Admin", color: "#7C3AED", initials: "AM" },
    { id: "u2", name: "Chris Davis", email: "chris@ashgropartners.com", role: "Sales Rep", color: "#0369A1", initials: "CD" },
    { id: "u3", name: "Taylor Smith", email: "taylor@ashgropartners.com", role: "Sales Rep", color: "#C2410C", initials: "TS" },
  ];
  const [users, setUsers] = useState(DEFAULT_USERS);
  const [currentUser, setCurrentUser] = useState(DEFAULT_USERS[0]);
  const [filterUser, setFilterUser] = useState([]);
  const [filterClientUser, setFilterClientUser] = useState("All");
  // eslint-disable-next-line no-unused-vars
  const [_showUserFilter, _setShowUserFilter] = useState(false);
  const [showClientUserFilter, setShowClientUserFilter] = useState(false);
  const [userForm, setUserForm] = useState({ name: "", email: "", role: "Sales Rep", color: "#0369A1" });
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({});

  // Email Templates
  const DEFAULT_TEMPLATES = [
    { id: "t1", name: "Initial Outreach", category: "Outbound", subject: "Modernize Membership Sales at {{clubName}}", body: "Hi {{contactName}},\n\nI came across {{clubName}} and wanted to reach out about how AshGro is helping clubs like yours modernize membership sales and grow revenue.\n\nOur platform combines AI-powered lead capture, a purpose-built CRM, and hands-on sales expertise — all designed specifically for private clubs.\n\nWould you be open to a quick 20-minute call to see if it's a fit?\n\nBest,\n{{senderName}}\nAshGro Membership Partners", attachments: [] },
    { id: "t2", name: "Follow-Up (No Response)", category: "Follow-Up", subject: "Re: AshGro for {{clubName}}", body: "Hi {{contactName}},\n\nJust following up on my last note — I know things get busy.\n\nI'd love to show you a quick demo of how AshGro CRM is helping membership directors at clubs similar to {{clubName}} increase tour conversions and streamline their pipeline.\n\nDo you have 15 minutes this week?\n\nBest,\n{{senderName}}", attachments: [] },
    { id: "t3", name: "Post-Demo Follow-Up", category: "Follow-Up", subject: "Great connecting with you, {{contactName}}", body: "Hi {{contactName}},\n\nThank you for taking the time to meet today — I really enjoyed learning more about {{clubName}} and where you're looking to grow.\n\nAs discussed, I'll send over the proposal for {{service}}. In the meantime, here's a quick recap of what we covered:\n\n• [Key point 1]\n• [Key point 2]\n• [Key point 3]\n\nLooking forward to next steps!\n\n{{senderName}}", attachments: [] },
    { id: "t4", name: "Proposal Intro", category: "Proposals", subject: "Your AshGro Proposal — {{clubName}}", body: "Hi {{contactName}},\n\nAttached is the proposal we discussed for {{clubName}}.\n\nThis outlines our recommended approach for {{service}}, including pricing, implementation timeline, and expected outcomes.\n\nI'm confident this will make a meaningful difference for your membership team. Happy to walk through it together — just let me know a time that works.\n\nBest,\n{{senderName}}\nAshGro Membership Partners", attachments: [] },
    { id: "t5", name: "Reengagement", category: "Reengagement", subject: "Checking in — {{clubName}}", body: "Hi {{contactName}},\n\nI wanted to reach back out — it's been a little while since we last connected about AshGro.\n\nA lot has changed on our end, and I'd love to share some new features that I think would be especially relevant for {{clubName}}.\n\nWould you be open to a fresh 15-minute conversation?\n\nBest,\n{{senderName}}", attachments: [] },
    { id: "t6", name: "Renewal Reminder", category: "Clients", subject: "Your AshGro Renewal — {{clubName}}", body: "Hi {{contactName}},\n\nI wanted to reach out ahead of your upcoming renewal on {{renewalDate}}.\n\nIt's been great having {{clubName}} as part of the AshGro family, and we'd love to continue supporting your team.\n\nI'll be in touch soon to discuss any questions and confirm the renewal. In the meantime, don't hesitate to reach out.\n\nBest,\n{{senderName}}", attachments: [] },
  ];
  const [emailTemplates, setEmailTemplates] = useState(DEFAULT_TEMPLATES);
  const [templateForm, setTemplateForm] = useState({ name: "", category: "Outbound", subject: "", body: "", attachments: [] });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [templatePreview, setTemplatePreview] = useState(null);

  // Proposal Generator
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalRecord, setProposalRecord] = useState(null);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [generatedProposal, setGeneratedProposal] = useState(null);
  const [proposalCopied, setProposalCopied] = useState(false);

  // Calendar
  const [calEvents, setCalEvents] = useState([]);
  const [calMonth, setCalMonth] = useState(new Date());
  // eslint-disable-next-line no-unused-vars
  const [calView, setCalView] = useState("month");
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [eventForm, setEventForm] = useState({ title: "", date: "", time: "", type: "Follow-Up", leadId: "", clientId: "", assignedTo: "", notes: "" });
  const [gcalSyncing, setGcalSyncing] = useState(false);
  const [gcalLastSynced, setGcalLastSynced] = useState(null);
  const [gcalJustConnected, setGcalJustConnected] = useState(false);
  const [selectedCalEvent, setSelectedCalEvent] = useState(null);

  // Edit modes
  const [editingLead, setEditingLead] = useState(false);
  const [editLeadForm, setEditLeadForm] = useState({});
  const [editingClient, setEditingClient] = useState(false);
  const [editClientForm, setEditClientForm] = useState({});

  // Saved lead views
  const [savedViews, setSavedViews] = useState([
    { id: "v1", name: "All Leads", filters: { stage: "All", user: "All", source: "All", service: "All" }, isDefault: true },
    { id: "v2", name: "My Leads", filters: { stage: "All", user: "u1", source: "All", service: "All" }, isDefault: false },
    { id: "v3", name: "Hot Pipeline", filters: { stage: "Demo Scheduled", user: "All", source: "All", service: "All" }, isDefault: false },
  ]);
  const [activeViewId, setActiveViewId] = useState("v1");
  const [showSaveView, setShowSaveView] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [filterSource, setFilterSource] = useState([]);
  const [filterService, setFilterService] = useState([]);

  // Agent state
  const [agentMessages, setAgentMessages] = useState([]);
  const [agentInput, setAgentInput] = useState("");
  const [agentLoading, setAgentLoading] = useState(false);
  const [dailyBrief, setDailyBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefGenerated, setBriefGenerated] = useState(false);
  const chatEndRef = useRef(null);

  // Gmail / Notification state
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [gmailSyncing, setGmailSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [gmailError, setGmailError] = useState(null);
  const [gmailConnected, setGmailConnected] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    const t = localStorage.getItem("gmail-access-token");
    return !!(t && t !== "null");
  });
  const notifRef = useRef(null);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  useEffect(() => { if (supabase) window._supabase = supabase; }, []);

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    (async () => {
      if (typeof window !== "undefined") {
        const raw = window.location.hash.replace(/^#/, "");
        if (raw) {
          const hp = new URLSearchParams(raw);
          if (hp.get("type") === "recovery") {
            await supabase.auth.getSession();
            if (!cancelled) setIsRecoveryMode(true);
          }
        }
      }
    })();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsRecoveryMode(true);
      }
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (readHashTypeRecovery()) return;
    const redirect = sessionStorage.getItem("postOAuthRedirect");
    if (redirect) {
      sessionStorage.removeItem("postOAuthRedirect");
      setPage(redirect);
    }
  }, []);

  useEffect(() => {
    // Handle Gmail OAuth redirect (skip Supabase password-recovery hashes — they also contain access_token)
    const hash = window.location.hash;
    if (hash.includes("access_token")) {
      const params = new URLSearchParams(hash.replace("#", "?"));
      if (params.get("type") === "recovery") {
        // Leave hash until user completes Set New Password flow
      } else {
        const token = params.get("access_token");
        if (token) {
          window.history.replaceState({}, document.title, window.location.pathname);
          setGmailError(null);
          persist("gmail-access-token", token, false);
          try { localStorage.setItem("gmail-access-token", token); } catch (_) {}
          setGmailConnected(true);
          showToast("Gmail connected! Click Sync to load replies.");
          setGmailSyncing(false);
        }
      }
    }

    if (!supabase || !userId) return;
    if (isRecoveryMode) return;
    (async () => {
      try {
        const [leadsRes, clientsRes, settingsRes] = await Promise.all([
          supabase.from("leads").select("data").eq("user_id", ORG_ID).maybeSingle(),
          supabase.from("clients").select("data").eq("user_id", ORG_ID).maybeSingle(),
          supabase.from("crm_settings").select("key, value").eq("user_id", userId),
        ]);
        let sharedRes = { data: [] };
        try {
          const result = await supabase
            .from("crm_settings")
            .select("key, value")
            .eq("user_id", "shared");
          if (result.data) sharedRes = result;
        } catch (e) {
          console.log("shared settings not available yet");
        }
        if (leadsRes?.data?.data) setLeads(Array.isArray(leadsRes.data.data) ? leadsRes.data.data : []);
        if (clientsRes?.data?.data) setClients(Array.isArray(clientsRes.data.data) ? clientsRes.data.data : []);
        const userMap = (settingsRes?.data || []).reduce((acc, { key, value }) => { acc[key] = value; return acc; }, {});
        const sharedMap = (sharedRes?.data || []).reduce((acc, { key, value }) => { acc[key] = value; return acc; }, {});
        const settingsMap = { ...userMap, ...sharedMap };
        if (settingsMap["ashgro-stages"]) setStages(Array.isArray(settingsMap["ashgro-stages"]) ? settingsMap["ashgro-stages"] : DEFAULT_STAGES);
        if (settingsMap["ashgro-sources"]) setLeadSources(Array.isArray(settingsMap["ashgro-sources"]) ? settingsMap["ashgro-sources"] : DEFAULT_LEAD_SOURCES);
        if (settingsMap["ashgro-services"]) setServices(Array.isArray(settingsMap["ashgro-services"]) ? settingsMap["ashgro-services"] : DEFAULT_SERVICES);
        if (settingsMap["ashgro-cstatus"]) setClientStatuses(Array.isArray(settingsMap["ashgro-cstatus"]) ? settingsMap["ashgro-cstatus"] : DEFAULT_CLIENT_STATUSES);
        if (settingsMap["ashgro-clf"]) {
          const rawCLF = settingsMap["ashgro-clf"];
          const parsedCLF = typeof rawCLF === "string" ? JSON.parse(rawCLF) : rawCLF;
          setCustomLeadFields(Array.isArray(parsedCLF) ? parsedCLF.filter(f => f && f.id && f.label) : []);
        }
        if (settingsMap["ashgro-ccf"]) {
          const rawCCF = settingsMap["ashgro-ccf"];
          const parsedCCF = typeof rawCCF === "string" ? JSON.parse(rawCCF) : rawCCF;
          setCustomClientFields(Array.isArray(parsedCCF) ? parsedCCF.filter(f => f && f.id && f.label) : []);
        }
        if (settingsMap["ashgro-company"]) setCompanyName(settingsMap["ashgro-company"]);
        if (settingsMap["ashgro-email-signature"]) setEmailSignature(settingsMap["ashgro-email-signature"]);
        if (settingsMap["ashgro-cemail"]) setCompanyEmail(settingsMap["ashgro-cemail"]);
        if (settingsMap["ashgro-agent-msgs"]) {
          const raw = settingsMap["ashgro-agent-msgs"];
          setAgentMessages(Array.isArray(raw) ? raw : []);
        }
        if (settingsMap["ashgro-notifications"]) setNotifications(Array.isArray(settingsMap["ashgro-notifications"]) ? settingsMap["ashgro-notifications"] : []);
        if (settingsMap["ashgro-last-synced"]) setLastSynced(typeof settingsMap["ashgro-last-synced"] === "string" ? settingsMap["ashgro-last-synced"] : null);
        if (settingsMap["ashgro-intake-leads"]) setIntakeLeads(Array.isArray(settingsMap["ashgro-intake-leads"]) ? settingsMap["ashgro-intake-leads"] : []);
        if (settingsMap["ashgro-kb"]) setKnowledgeBase(Array.isArray(settingsMap["ashgro-kb"]) ? settingsMap["ashgro-kb"] : []);
        if (settingsMap["ashgro-users"]) {
          const raw = settingsMap["ashgro-users"];
          const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
          setUsers(Array.isArray(parsed) ? parsed : DEFAULT_USERS);
        }
        const usersList = settingsMap["ashgro-users"] ? (() => { const raw = settingsMap["ashgro-users"]; const p = typeof raw === "string" ? JSON.parse(raw) : raw; return Array.isArray(p) ? p : DEFAULT_USERS; })() : DEFAULT_USERS;
        const authUser = (await supabase?.auth?.getUser?.())?.data?.user ?? null;
        if (authUser?.id) window._supabaseAuthId = authUser.id;
        const authEmail = authUser?.email ?? null;
        if (authEmail) {
          const matched = usersList.find(u => u.email?.toLowerCase() === authEmail.toLowerCase());
          if (matched) setCurrentUser(matched);
        }
        if (settingsMap["ashgro-templates"]) {
          const raw = settingsMap["ashgro-templates"];
          try {
            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
            setEmailTemplates(Array.isArray(parsed) ? parsed : []);
          } catch (_) { setEmailTemplates([]); }
        }
        if (settingsMap["ashgro-cal-events"]) {
          const raw = settingsMap["ashgro-cal-events"];
          try {
            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
            setCalEvents(Array.isArray(parsed) ? parsed : []);
          } catch (_) { setCalEvents([]); }
        }
        if (settingsMap["ashgro-gcal-synced"]) setGcalLastSynced(typeof settingsMap["ashgro-gcal-synced"] === "string" ? settingsMap["ashgro-gcal-synced"] : null);
        if (settingsMap["ashgro-saved-views"]) setSavedViews(Array.isArray(settingsMap["ashgro-saved-views"]) ? settingsMap["ashgro-saved-views"] : []);
        const token = settingsMap["gmail-access-token"];
        const validToken = token && token !== "null" ? (typeof token === "string" ? token : String(token)) : null;
        const rawLs = typeof localStorage !== "undefined" ? localStorage.getItem("gmail-access-token") : null;
        const hasToken = !!validToken || (rawLs && rawLs !== "null");
        if (validToken) { try { localStorage.setItem("gmail-access-token", validToken); } catch (_) {} }
        setGmailConnected(!!hasToken);
      } catch (e) {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isRecoveryMode]);

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    try {
      if (!leadSortColumn) {
        localStorage.removeItem("ashgro_leads_sort");
        return;
      }
      localStorage.setItem("ashgro_leads_sort", JSON.stringify({ column: leadSortColumn, dir: leadSortDir }));
    } catch (_) {}
  }, [leadSortColumn, leadSortDir]);

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    try {
      if (!clientSortColumn) {
        localStorage.removeItem("ashgro_clients_sort");
        return;
      }
      localStorage.setItem("ashgro_clients_sort", JSON.stringify({ column: clientSortColumn, dir: clientSortDir }));
    } catch (_) {}
  }, [clientSortColumn, clientSortDir]);

  // Close notification panel on outside click
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifPanel(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const persist = async (key, val, shared = false) => {
    if (!supabase) return;
    const uid = shared ? "shared" : userId;
    if (!uid) return;
    const value = (typeof val === "string" || typeof val === "number" || typeof val === "boolean") ? val : JSON.stringify(val);
    if (shared) {
      try {
        await fetch("/api/settings-sync", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ key, value, user_id: "shared" })
        });
      } catch (e) { console.error("persist shared error:", e.message); }
    } else {
      try {
        await supabase.from("crm_settings").upsert({ user_id: uid, key, value }, { onConflict: "user_id,key" });
      } catch (e) { console.error("persist error:", e.message); }
    }
  };
  const saveLeads = async (u) => {
    if (!supabase || !userId) {
      console.warn("saveLeads skipped — userId:", userId, "supabase:", !!supabase);
      showToast("Cannot save leads — please sign in.", "error");
      return false;
    }
    try {
      const { data: existing } = await supabase.from("leads").select("id").eq("user_id", ORG_ID).maybeSingle();
      const payload = {
        ...(existing?.id != null && { id: existing.id }),
        user_id: ORG_ID,
        data: u,
        updated_at: new Date().toISOString()
      };
      const result = await supabase.from("leads").upsert(payload, { onConflict: "id" });
      if (result.error) {
        console.error("saveLeads error:", JSON.stringify(result.error));
        showToast(result.error.message || "Failed to save leads.", "error");
        return false;
      }
      setLeads(u);
      return true;
    } catch (e) {
      console.error("saveLeads exception:", e.message);
      showToast(e.message || "Failed to save leads.", "error");
      return false;
    }
  };
  const saveClients = async (u) => {
    setClients(u);
    if (!supabase || !userId) return;
    try {
      const { data: existing } = await supabase
        .from("clients")
        .select("id")
        .eq("user_id", ORG_ID)
        .maybeSingle();
      const payload = {
        ...(existing?.id != null && { id: existing.id }),
        user_id: ORG_ID,
        data: u,
        updated_at: new Date().toISOString()
      };
      await supabase.from("clients").upsert(payload, { onConflict: "id" });
    } catch (e) {}
  };
  const saveStages = (u) => { setStages(u); persist("ashgro-stages", u, true); };
  const saveLeadSources = (u) => { setLeadSources(u); persist("ashgro-sources", u, true); };
  const saveServices = (u) => { setServices(u); persist("ashgro-services", u, true); };
  const saveClientStatuses = (u) => { setClientStatuses(u); persist("ashgro-cstatus", u, true); };
  const saveCLF = (u) => { setCustomLeadFields(u); persist("ashgro-clf", u, true); };
  const saveCCF = (u) => { setCustomClientFields(u); persist("ashgro-ccf", u, true); };
  const saveAgentMessages = (u) => { setAgentMessages(u); persist("ashgro-agent-msgs", u); };
  const saveNotifications = (u) => { setNotifications(u); persist("ashgro-notifications", u); };
  const saveIntakeLeads = (u) => { setIntakeLeads(u); persist("ashgro-intake-leads", u); };
  const saveKnowledgeBase = (u) => { setKnowledgeBase(u); persist("ashgro-kb", u); };
  const saveUsers = (u) => { setUsers(u); persist("ashgro-users", u, true); };
  const saveCurrentUser = (u) => { setCurrentUser(u); };

  const saveEditUser = () => {
    if (!userEditForm.name?.trim() || !userEditForm.email?.trim()) return;
    const updated = users.map(u => u.id === editingUser.id ? { ...u, ...userEditForm } : u);
    saveUsers(updated);
    if (currentUser?.id === editingUser.id) saveCurrentUser({ ...currentUser, ...userEditForm });
    setEditingUser(null);
    setUserEditForm({});
    showToast("User updated");
  };
  const saveEmailTemplates = (u) => { setEmailTemplates(u); persist("ashgro-templates", JSON.stringify(u)); };
  const saveCalEvents = (u) => { setCalEvents(u); persist("ashgro-cal-events", JSON.stringify(u)); };
  const saveSavedViews = (u) => { setSavedViews(u); persist("ashgro-saved-views", u); };

  // ── GMAIL SYNC ──────────────────────────────────────────────────────────────
  const triggerGmailOAuth = () => {
    sessionStorage.setItem("postOAuthRedirect", "inbox");
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = window.location.origin;
    const scope = encodeURIComponent([
      "https://www.googleapis.com/auth/gmail.readonly",
      "https://www.googleapis.com/auth/gmail.send"
    ].join(" "));
    const state = encodeURIComponent(JSON.stringify({ action: "gmail_sync" }));
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=${scope}&state=${state}&prompt=none`;
  };

  const syncGmailEmails = async () => {
    const rawToken = localStorage.getItem("gmail-access-token");
    const token = (!rawToken || rawToken === "null") ? null : rawToken;
    if (!token) {
      // No token — trigger OAuth
      sessionStorage.setItem("postOAuthRedirect", "inbox");
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      const redirectUri = window.location.origin;
      const scope = encodeURIComponent([
        "https://www.googleapis.com/auth/gmail.readonly",
        "https://www.googleapis.com/auth/gmail.send"
      ].join(" "));
      const state = encodeURIComponent(JSON.stringify({ action: "gmail_sync" }));
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=token&scope=${scope}&state=${state}&prompt=consent`;
      return;
    }
    setGmailSyncing(true);
    try {
      const allContacts = [
        ...leads.map(r => ({ id: r.id, type: "lead", name: r.clubName, email: r.email?.toLowerCase() })),
        ...clients.map(r => ({ id: r.id, type: "client", name: r.clubName, email: r.email?.toLowerCase() })),
      ].filter(c => c.email);
      const emailList = allContacts.map(c => c.email).join(", ");
      const resp = await fetch("/api/gmail-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, emailList, lastSynced })
      });
      const data = await resp.json();
      if (!resp.ok) {
        if (resp.status === 401 || data.error?.includes("Invalid Credentials") || data.error?.includes("invalid authentication")) {
          localStorage.removeItem("gmail-access-token");
          triggerGmailOAuth();
        } else {
          showToast("Gmail sync failed: " + (data.error || "Unknown error"), "error");
        }
        setGmailSyncing(false);
        return;
      }
      if (data.notifications && data.notifications.length > 0) {
        // Deduplicate against existing notifications
        const existingIds = new Set(notifications.map(n => n.id));
        const newNotifs = data.notifications.filter(n => !existingIds.has(n.id));
        if (newNotifs.length > 0) {
          saveNotifications([...newNotifs, ...notifications]);
          // Log replies to matching lead/client activity feeds
          let updatedLeads = [...leads];
          let updatedClients = [...clients];
          let leadsChanged = false;
          let clientsChanged = false;
          for (const notif of newNotifs) {
            const match = allContacts.find(c => c.email === notif.from?.toLowerCase());
            if (!match) continue;
            const activity = {
              id: `gmail-${notif.id}`,
              type: "Email Reply",
              date: new Date().toISOString(),
              notes: `Reply received from ${notif.from}\nSubject: ${notif.subject}\n\n${notif.snippet}`,
              content: `Reply received from ${notif.from}\nSubject: ${notif.subject}\n\n${notif.snippet}`
            };
            if (match.type === "lead") {
              updatedLeads = updatedLeads.map(l => sameId(l.id, match.id) ? { ...l, activity: [...(l.activity || []), activity] } : l);
              leadsChanged = true;
            } else {
              updatedClients = updatedClients.map(cl => sameId(cl.id, match.id) ? { ...cl, activity: [...(cl.activity || []), activity] } : cl);
              clientsChanged = true;
            }
          }
          if (leadsChanged) saveLeads(updatedLeads);
          if (clientsChanged) saveClients(updatedClients);
          showToast(`Synced — ${newNotifs.length} new repl${newNotifs.length === 1 ? "y" : "ies"} found`);
        } else {
          showToast("Inbox checked — no new replies.");
        }
      } else {
        showToast("Inbox checked — no new replies.");
      }
      setLastSynced(new Date().toISOString());
      persist("ashgro-last-synced", new Date().toISOString());
    } catch (e) {
      setGmailError("Gmail sync failed: " + e.message);
      showToast("Gmail sync failed", "error");
    }
    setGmailSyncing(false);
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const markAllRead = () => saveNotifications(notifications.map(n => ({ ...n, read: true })));
  const markRead = (id) => saveNotifications(notifications.map(n => n.id === id ? { ...n, read: true } : n));
  const clearNotification = (id) => saveNotifications(notifications.filter(n => n.id !== id));

  const filteredLeads = leads.filter(l => {
    const ms = filterStage.length === 0 || filterStage.includes(l.stage);
    const mu = filterUser.length === 0 || filterUser.includes(l.assignedTo);
    const mso = filterSource.length === 0 || filterSource.includes(l.source);
    const msv = filterService.length === 0 || filterService.includes(l.service);
    const q = search.toLowerCase();
    return ms && mu && mso && msv && (!q || l.clubName.toLowerCase().includes(q) || l.contactName.toLowerCase().includes(q));
  });
  const LEAD_SORT_CONFIG = {
    contactName: { type: "string", getValue: (l) => l.contactName || "" },
    clubName: { type: "string", getValue: (l) => l.clubName || "" },
    stage: { type: "string", getValue: (l) => l.stage || "" },
    source: { type: "string", getValue: (l) => l.source || "" },
    followUp: { type: "date", getValue: (l) => l.followUp || "" },
    createdAt: { type: "date", getValue: (l) => l.createdAt || "" },
    value: { type: "number", getValue: (l) => l.value },
  };
  const isEmptySortValue = (value) => value === null || value === undefined || value === "";
  const parseSortValue = (value, type) => {
    if (isEmptySortValue(value)) return null;
    if (type === "number") {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    }
    if (type === "date") {
      const t = new Date(value).getTime();
      return Number.isFinite(t) ? t : null;
    }
    return String(value).toLowerCase();
  };
  const compareSortValues = (a, b, dir, type) => {
    const va = parseSortValue(a, type);
    const vb = parseSortValue(b, type);
    const aEmpty = va === null;
    const bEmpty = vb === null;
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    let cmp = 0;
    if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
    else cmp = String(va).localeCompare(String(vb));
    return dir === "asc" ? cmp : -cmp;
  };
  const sortedLeads = leadSortColumn && LEAD_SORT_CONFIG[leadSortColumn] ? [...filteredLeads].sort((a, b) => {
    const conf = LEAD_SORT_CONFIG[leadSortColumn];
    return compareSortValues(conf.getValue(a), conf.getValue(b), leadSortDir, conf.type);
  }) : filteredLeads;
  const totalLeadPages = Math.max(1, Math.ceil(sortedLeads.length / 10));
  const paginatedLeads = sortedLeads.slice((leadPage - 1) * 10, leadPage * 10);

  const filteredClients = clients.filter(c => {
    const ms = filterStatus === "All" || c.status === filterStatus;
    const mu = filterClientUser === "All" || c.assignedTo === filterClientUser;
    const q = clientSearch.toLowerCase();
    return ms && mu && (!q || c.clubName.toLowerCase().includes(q) || c.contactName.toLowerCase().includes(q));
  });
  const CLIENT_SORT_CONFIG = {
    contactName: { type: "string", getValue: (c) => c.contactName || "" },
    clubName: { type: "string", getValue: (c) => c.clubName || "" },
    service: { type: "string", getValue: (c) => c.service || (Array.isArray(c.services) ? c.services.join(", ") : c.services || "") },
    createdAt: { type: "date", getValue: (c) => c.createdAt || "" },
    value: { type: "number", getValue: (c) => c.value },
    renewalDate: { type: "date", getValue: (c) => c.renewalDate || "" },
    source: { type: "string", getValue: (c) => c.source || "" },
  };
  const sortedClients = clientSortColumn && CLIENT_SORT_CONFIG[clientSortColumn] ? [...filteredClients].sort((a, b) => {
    const conf = CLIENT_SORT_CONFIG[clientSortColumn];
    return compareSortValues(conf.getValue(a), conf.getValue(b), clientSortDir, conf.type);
  }) : filteredClients;
  const totalClientPages = Math.max(1, Math.ceil(sortedClients.length / 10));
  const paginatedClients = sortedClients.slice((clientPage - 1) * 10, clientPage * 10);

  const handleAddLead = () => { if (!newLead.clubName || !newLead.contactName || !newLead.email) return; const lead = { ...newLead, id: Date.now(), createdAt: new Date().toISOString().slice(0, 10), activity: [], value: newLead.value || 0, customFields: {} }; saveLeads([lead, ...leads]); setNewLead({ clubName: "", contactName: "", email: "", phone: "", title: "", stage: "New Lead", service: "", source: "", followUp: "", value: "" }); setShowAddLead(false); };
  const handleAddClient = () => { if (!newClient.clubName || !newClient.contactName || !newClient.email) return; const client = { ...newClient, id: Date.now(), createdAt: new Date().toISOString().slice(0, 10), activity: [], value: newClient.value || 0, customFields: {} }; saveClients([client, ...clients]); setNewClient({ clubName: "", contactName: "", email: "", phone: "", title: "", service: "", source: "", status: "Active", startDate: "", renewalDate: "", value: "" }); setShowAddClient(false); };
  const handleAddActivity = () => {
    if (!newActivity.content.trim()) return;
    const now = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const act = { ...newActivity, date: now };
    if (activityTarget === "lead" && selectedLead) { const u = leads.map(l => sameId(l.id, selectedLead.id) ? { ...l, activity: [act, ...(l.activity || [])] } : l); saveLeads(u); setSelectedLead(u.find(l => sameId(l.id, selectedLead.id))); }
    else if (activityTarget === "client" && selectedClient) { const u = clients.map(c => c.id === selectedClient.id ? { ...c, activity: [act, ...c.activity] } : c); saveClients(u); setSelectedClient(u.find(c => c.id === selectedClient.id)); }
    setNewActivity({ type: "Phone Call", content: "" }); setShowAddActivity(false);
  };
  const handleConvert = () => {
    if (!selectedLead) return;
    const client = { id: Date.now(), clubName: selectedLead.clubName, contactName: selectedLead.contactName, email: selectedLead.email, phone: selectedLead.phone || "", title: selectedLead.title || "", service: selectedLead.service || "", source: selectedLead.source || "", status: convertData.status, createdAt: new Date().toISOString().slice(0, 10), startDate: convertData.startDate, renewalDate: convertData.renewalDate, value: selectedLead.value || 0, customFields: {}, activity: [{ type: "Converted from Lead", date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }), content: "Converted from lead. Service: " + (selectedLead.service || "—") + ". Original stage: " + (selectedLead.stage || "—") + "." }, ...(Array.isArray(selectedLead.activity) ? selectedLead.activity : [])] };
    saveLeads(leads.filter(l => !sameId(l.id, selectedLead.id))); saveClients([client, ...clients]);
    setShowConvert(false); setSelectedLead(null); setSelectedClient(client); setPage("client-detail");
  };
  const updateLeadStage = (id, stage) => { const u = leads.map(l => sameId(l.id, id) ? { ...l, stage } : l); saveLeads(u); if (sameId(selectedLead?.id, id)) setSelectedLead(u.find(l => sameId(l.id, id))); };
  const updateClientStatus = (id, status) => { const u = clients.map(c => c.id === id ? { ...c, status } : c); saveClients(u); if (selectedClient?.id === id) setSelectedClient(u.find(c => c.id === id)); };
  const updateLeadCustomField = (lid, fid, val) => {
    const u = leads.map(l => sameId(l.id, lid) ? { ...l, customFields: { ...(l.customFields || {}), [fid]: val } } : l);
    const updated = u.find(l => sameId(l.id, lid));
    setLeads(u);
    saveLeads(u);
    if (sameId(selectedLead?.id, lid)) {
      setSelectedLead(updated);
      if (editingLead) setEditLeadForm(updated);
    }
  };
  const updateClientCustomField = (cid, fid, val) => {
    const u = clients.map(c => c.id === cid ? { ...c, customFields: { ...(c.customFields || {}), [fid]: val } } : c);
    const updated = u.find(c => c.id === cid);
    setClients(u);
    saveClients(u);
    if (selectedClient?.id === cid) {
      setSelectedClient(updated);
      if (editingClient) setEditClientForm(updated);
    }
  };
  const deleteLead = async (id) => {
    const ok = await saveLeads(leads.filter(l => !sameId(l.id, id)));
    if (ok) {
      setSelectedLead(null);
      setPage("leads");
      showToast("Lead deleted");
    }
  };
  const deleteClient = (id) => { saveClients(clients.filter(c => c.id !== id)); setSelectedClient(null); setPage("clients"); };
  const addCustomField = () => { if (!newFieldForm.label.trim()) return; const field = { id: uid(), label: newFieldForm.label, type: newFieldForm.type, required: newFieldForm.required, options: newFieldForm.options.split(",").map(s => s.trim()).filter(Boolean) }; if (newFieldForm.target === "lead") saveCLF([...customLeadFields, field]); else saveCCF([...customClientFields, field]); setNewFieldForm({ label: "", type: "Text", required: false, options: "", target: "lead" }); setShowNewField(false); showToast("Custom field added"); };
  const deleteCustomField = (id, target) => { if (target === "lead") saveCLF(customLeadFields.filter(f => f.id !== id)); else saveCCF(customClientFields.filter(f => f.id !== id)); showToast("Field removed"); };
  const saveEditField = () => { if (!editingField) return; const updated = { ...editingField, options: typeof editingField.options === "string" ? editingField.options.split(",").map(s => s.trim()).filter(Boolean) : editingField.options }; if (editingField._target === "lead") saveCLF(customLeadFields.map(f => f.id === updated.id ? updated : f)); else saveCCF(customClientFields.map(f => f.id === updated.id ? updated : f)); setEditingField(null); showToast("Field updated"); };
  const handlePwChange = async () => {
    if (pwForm.newPw !== pwForm.confirm) {
      showToast("New password and confirmation do not match.", "error");
      return;
    }
    if (pwForm.newPw.length < 8) {
      showToast("New password must be at least 8 characters.", "error");
      return;
    }
    if (!supabase) {
      showToast("Cannot update password — sign in is not configured.", "error");
      return;
    }
    setPwUpdating(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password: pwForm.newPw });
      if (updErr) {
        showToast(updErr.message || "Could not update password.", "error");
        return;
      }
      showToast("Password updated successfully.");
      setPwForm({ newPw: "", confirm: "" });
    } catch (e) {
      showToast(e?.message || "Something went wrong.", "error");
    } finally {
      setPwUpdating(false);
    }
  };

  const handleRecoveryPasswordSubmit = async () => {
    if (recoveryPwForm.newPw !== recoveryPwForm.confirm) {
      showToast("New password and confirmation do not match.", "error");
      return;
    }
    if (recoveryPwForm.newPw.length < 8) {
      showToast("New password must be at least 8 characters.", "error");
      return;
    }
    if (!supabase) {
      showToast("Cannot update password — sign in is not configured.", "error");
      return;
    }
    setRecoveryPwSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: recoveryPwForm.newPw });
      if (error) {
        showToast(error.message || "Could not update password.", "error");
        return;
      }
      showToast("Password updated successfully.");
      const path = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", path);
      setPage("dashboard");
      setRecoveryPwForm({ newPw: "", confirm: "" });
      setIsRecoveryMode(false);
    } catch (e) {
      showToast(e?.message || "Something went wrong.", "error");
    } finally {
      setRecoveryPwSubmitting(false);
    }
  };

  // ── AGENT FUNCTIONS ──────────────────────────────────────────────────────
  const buildSystemPrompt = () => {
    const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

    const parseDate = (value) => {
      if (!value) return 0;
      const t = new Date(value).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    // Truncate the context to avoid exceeding model token limits.
    const recentLeads = [...leads]
      .sort((a, b) => parseDate(b.followUp || b.createdAt) - parseDate(a.followUp || a.createdAt))
      .slice(0, 50);
    const recentClients = [...clients]
      .sort((a, b) => parseDate(b.renewalDate || b.startDate) - parseDate(a.renewalDate || a.startDate))
      .slice(0, 25);

    const coldLeads = recentLeads.filter(l => {
      const t = parseDate(l.followUp || l.createdAt);
      return (Date.now() - t) > 7 * 24 * 60 * 60 * 1000 && !["Closed Won", "Closed Lost"].includes(l.stage);
    });
    const atRiskClients = recentClients.filter(c => c.status === "At Risk");
    const renewingSoon = recentClients.filter(c => {
      if (!c.renewalDate) return false;
      const d = new Date(c.renewalDate);
      const diff = (d - Date.now()) / (1000 * 60 * 60 * 24);
      return diff > 0 && diff <= 60;
    });

    const joinLimited = (items, limit) => {
      const slice = items.slice(0, limit);
      const rest = items.length - slice.length;
      return slice.join(", ") + (rest > 0 ? `, +${rest} more` : "");
    };

    const leadContext = recentLeads.map(l => {
      return `- ${l.contactName || "—"} | ${l.clubName || "—"} | Stage: ${l.stage || "—"} | Value: ${fmtVal(l.value)}/mo | Follow-up: ${l.followUp || "Not set"} | Source: ${l.source || "N/A"}`;
    }).join("\n");

    const clientContext = recentClients.map(c => {
      return `- ${c.contactName || "—"} | ${c.clubName || "—"} | Stage: ${c.status || "—"} | Value: ${fmtVal(c.value)}/mo | Follow-up: ${c.renewalDate || "Not set"} | Source: ${c.source || c.service || "N/A"}`;
    }).join("\n");

    return `You are the AshGro AI Sales & Customer Agent — an elite B2B sales assistant embedded inside the AshGro Membership Partners CRM. You work exclusively for the AshGro sales team to help them grow revenue, manage leads, and retain clients.

Today is ${today}. The company is ${companyName}, reachable at ${companyEmail}.

CURRENT PIPELINE DATA:
Total Leads: ${leads.length}
Active Leads (not closed): ${leads.filter(l => !["Closed Won","Closed Lost"].includes(l.stage)).length}
Total Clients: ${clients.length}
Active MRR: ${fmtVal(clients.filter(c => c.status === "Active").reduce((s,c) => s + Number(c.value), 0))}

LEADS:
${leadContext || "None"}

CLIENTS:
${clientContext || "None"}

ALERTS:
- Cold leads (follow-up >7 days): ${coldLeads.length ? joinLimited(coldLeads.map(l => l.clubName), 10) : "None"}
- At-risk clients: ${atRiskClients.length ? joinLimited(atRiskClients.map(c => c.clubName), 10) : "None"}
- Renewals within 60 days: ${renewingSoon.length ? joinLimited(renewingSoon.map(c => c.clubName + (c.renewalDate ? " (" + fmt(c.renewalDate) + ")" : "")), 10) : "None"}

YOUR CAPABILITIES:
You can: analyze pipeline health, write personalized emails and outreach, draft reengagement campaigns, suggest follow-up strategies, identify upsell opportunities, flag churn risks, create call scripts, summarize account history, and give prioritized daily action plans.

When asked to write an email, produce a complete, professional, ready-to-send email with subject line. When asked for a daily brief, be concise but insightful — lead with what matters most today. Always be direct, specific, and actionable. Reference actual club names, contacts, and data. Never be vague. You represent a premium, high-trust brand — your communication style should match.`;
  };

  const generateDailyBrief = async () => {
    setBriefLoading(true);
    setBriefGenerated(false);
    try {
      const resp = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: buildSystemPrompt(),
          messages: [{ role: "user", content: "Generate today's sales brief. Include: (1) a 2-sentence overall pipeline health summary, (2) top 3 priority actions for today with specific reasoning, (3) any urgent alerts I need to know about, (4) one quick win opportunity. Be specific and use real club names from my data. Format with clear sections." }]
        })
      });
      const data = await resp.json();
      const text = data.content?.find(b => b.type === "text")?.text || "Unable to generate brief.";
      setDailyBrief(text);
      setBriefGenerated(true);
    } catch(e) {
      setDailyBrief("Unable to connect to AI. Please check your connection and try again.");
      setBriefGenerated(true);
    }
    setBriefLoading(false);
  };

  const sendAgentMessage = async (input) => {
    if (!input.trim()) return;
    const userMsg = { role: "user", content: input };
    const newMessages = [...agentMessages, userMsg];
    const cleanMessages = newMessages.map(m => ({ role: m.role, content: m.content }));
    setAgentMessages(cleanMessages);
    setAgentInput("");
    setAgentLoading(true);

    const tools = [
      {
        type: "web_search_20250305",
        name: "web_search"
      },
      {
        name: "search_records",
        description: "Search leads and clients in the CRM by name, email, stage, status, or service.",
        input_schema: {
          type: "object",
          properties: {
            query: { type: "string", description: "Search term" },
            record_type: { type: "string", enum: ["leads", "clients", "both"], description: "Which records to search" },
            stage: { type: "string", description: "Filter by lead stage" },
            status: { type: "string", description: "Filter by client status" }
          },
          required: ["query"]
        }
      },
      {
        name: "get_pipeline_summary",
        description: "Get a summary of the current sales pipeline including lead counts by stage, MRR, and client health.",
        input_schema: { type: "object", properties: {} }
      },
      {
        name: "create_lead",
        description: "Create a new lead in the CRM pipeline.",
        input_schema: {
          type: "object",
          properties: {
            clubName: { type: "string" },
            contactName: { type: "string" },
            email: { type: "string" },
            phone: { type: "string" },
            title: { type: "string" },
            stage: { type: "string" },
            source: { type: "string" },
            service: { type: "string" },
            value: { type: "number" },
            notes: { type: "string" }
          },
          required: ["clubName", "contactName", "email"]
        }
      },
      {
        name: "update_lead",
        description: "Update an existing lead's stage, notes, follow-up date, or other fields.",
        input_schema: {
          type: "object",
          properties: {
            lead_id: { type: "string", description: "The lead's ID" },
            stage: { type: "string" },
            notes: { type: "string" },
            followUp: { type: "string", description: "Follow-up date in YYYY-MM-DD format" },
            value: { type: "number" }
          },
          required: ["lead_id"]
        }
      },
      {
        name: "create_client",
        description: "Create a new client record in the CRM.",
        input_schema: {
          type: "object",
          properties: {
            clubName: { type: "string" },
            contactName: { type: "string" },
            email: { type: "string" },
            phone: { type: "string" },
            service: { type: "string" },
            value: { type: "number" },
            status: { type: "string" },
            startDate: { type: "string" },
            renewalDate: { type: "string" }
          },
          required: ["clubName", "contactName", "email"]
        }
      },
      {
        name: "update_client",
        description: "Update an existing client's status, renewal date, or other fields.",
        input_schema: {
          type: "object",
          properties: {
            client_id: { type: "string" },
            status: { type: "string" },
            renewalDate: { type: "string" },
            value: { type: "number" },
            notes: { type: "string" }
          },
          required: ["client_id"]
        }
      },
      {
        name: "log_activity",
        description: "Log a call, email, meeting, or note on a lead or client record.",
        input_schema: {
          type: "object",
          properties: {
            record_id: { type: "string" },
            record_type: { type: "string", enum: ["lead", "client"] },
            activity_type: { type: "string", enum: ["Phone Call", "Email", "Demo", "Meeting", "Tour", "Text Message", "Note", "Other"] },
            content: { type: "string", description: "Notes about the activity" }
          },
          required: ["record_id", "record_type", "activity_type", "content"]
        }
      },
      {
        name: "create_calendar_event",
        description: "Create a calendar event or meeting.",
        input_schema: {
          type: "object",
          properties: {
            title: { type: "string" },
            date: { type: "string", description: "Date in YYYY-MM-DD format" },
            time: { type: "string", description: "Time in HH:MM format" },
            type: { type: "string", enum: ["Follow-Up", "Demo", "Meeting", "Proposal Review", "Renewal", "Onboarding", "Call", "Other"] },
            notes: { type: "string" },
            leadId: { type: "string" },
            clientId: { type: "string" }
          },
          required: ["title", "date"]
        }
      },
      {
        name: "send_email",
        description: "Send an email to a lead or client using Gmail.",
        input_schema: {
          type: "object",
          properties: {
            to_email: { type: "string" },
            to_name: { type: "string" },
            subject: { type: "string" },
            body: { type: "string", description: "Full email body in plain text" },
            record_id: { type: "string", description: "Lead or client ID to log activity against" },
            record_type: { type: "string", enum: ["lead", "client"] }
          },
          required: ["to_email", "subject", "body"]
        }
      },
      {
        name: "research_prospect",
        description: "Research a private club or prospect using web search to gather intel for outreach.",
        input_schema: {
          type: "object",
          properties: {
            club_name: { type: "string" },
            location: { type: "string" }
          },
          required: ["club_name"]
        }
      },
      {
        name: "score_lead",
        description: "Analyze a lead and return a fit score, priority ranking, and recommended next action.",
        input_schema: {
          type: "object",
          properties: {
            lead_id: { type: "string" }
          },
          required: ["lead_id"]
        }
      },
      {
        name: "generate_outreach",
        description: "Generate a personalized cold outreach email for a prospect based on their profile and research.",
        input_schema: {
          type: "object",
          properties: {
            club_name: { type: "string" },
            contact_name: { type: "string" },
            research_notes: { type: "string" },
            service: { type: "string" }
          },
          required: ["club_name", "contact_name"]
        }
      },
      {
        name: "find_prospects",
        description: "Search the web for private clubs or prospects that match target criteria for AshGro services.",
        input_schema: {
          type: "object",
          properties: {
            location: { type: "string" },
            club_type: { type: "string" },
            criteria: { type: "string" }
          },
          required: ["location"]
        }
      },
      {
        name: "generate_followup",
        description: "Generate a personalized follow-up email based on a lead's last activity, stage, and history.",
        input_schema: {
          type: "object",
          properties: {
            lead_id: { type: "string" }
          },
          required: ["lead_id"]
        }
      }
    ];

    const systemPrompt = buildSystemPrompt();
    console.log("AI Agent context estimated char count (system prompt):", systemPrompt.length);

    try {
      const resp = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 15000,
          tools,
          system: systemPrompt,
          messages: newMessages
        })
      });
      console.log("AI Agent /api/chat fetch response (initial):", resp);
      const responseText = await resp.text();
      let data;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch (parseErr) {
        data = { raw: responseText };
      }
      console.log("AI Agent /api/chat parsed body (initial):", data);
      if (!resp.ok) {
        console.error("AI Agent /api/chat non-OK (initial):", {
          status: resp.status,
          statusText: resp.statusText,
          data
        });
        throw new Error(data?.error || data?.message || `AI Agent API error (${resp.status})`);
      }

      // Process tool calls
      const toolResults = [];
      for (const block of (data.content || [])) {
        if (block.type === "tool_use") {
          const result = await handleAgentTool(block.name, block.input);
          toolResults.push({ type: "tool_result", tool_use_id: block.id, content: JSON.stringify(result) });
        }
      }

      // If there were tool calls, send results back for final response
      if (toolResults.length > 0) {
        const assistantToolMsg = { role: "assistant", content: data.content };
        const toolResultMsg = { role: "user", content: toolResults.map(r => ({ type: r.type, tool_use_id: r.tool_use_id, content: r.content })) };
        const messagesWithTools = [...newMessages, assistantToolMsg, toolResultMsg];
        const finalResp = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: "claude-sonnet-4-20250514",
            max_tokens: 15000,
            tools,
            system: systemPrompt,
            messages: messagesWithTools
          })
        });
        console.log("AI Agent /api/chat fetch response (final):", finalResp);
        const finalResponseText = await finalResp.text();
        let finalData;
        try {
          finalData = finalResponseText ? JSON.parse(finalResponseText) : null;
        } catch (parseErr) {
          finalData = { raw: finalResponseText };
        }
        console.log("AI Agent /api/chat parsed body (final):", finalData);
        if (!finalResp.ok) {
          console.error("AI Agent /api/chat non-OK (final):", {
            status: finalResp.status,
            statusText: finalResp.statusText,
            data: finalData
          });
          throw new Error(finalData?.error || finalData?.message || `AI Agent final API error (${finalResp.status})`);
        }
        const finalText = (finalData.content || []).filter(b => b.type === "text").map(b => b.text).join("");
        const assistantMsg = { role: "assistant", content: finalText };
        const updatedMessages = [...newMessages, assistantMsg];
        const cleanMessages = updatedMessages.map(m => ({ role: m.role, content: m.content }));
        setAgentMessages(cleanMessages);
        saveAgentMessages(cleanMessages);
      } else {
        const text = (data.content || []).filter(b => b.type === "text").map(b => b.text).join("");
        const assistantMsg = { role: "assistant", content: text };
        const updatedMessages = [...newMessages, assistantMsg];
        const cleanMessages = updatedMessages.map(m => ({ role: m.role, content: m.content }));
        setAgentMessages(cleanMessages);
        saveAgentMessages(cleanMessages);
      }
    } catch (e) {
      console.error("AI Agent /api/chat error caught:", e);
      const errMsg = { role: "assistant", content: "Sorry, I ran into an error: " + (e?.message || String(e)) };
      setAgentMessages([...newMessages, errMsg]);
    }
    setAgentLoading(false);
  };

  const handleAgentTool = async (toolName, input) => {
    switch (toolName) {
      case "get_pipeline_summary": {
        const activeClients = clients.filter(c => c.status === "Active");
        const mrr = activeClients.reduce((s, c) => s + Number(c.value || 0), 0);
        const stageCounts = stages.reduce((acc, s) => { acc[s] = leads.filter(l => l.stage === s).length; return acc; }, {});
        return { totalLeads: leads.length, totalClients: clients.length, activeMRR: mrr, stageCounts, atRisk: clients.filter(c => c.status === "At Risk").length };
      }
      case "search_records": {
        const q = input.query?.toLowerCase() || "";
        let results = [];
        if (!input.record_type || input.record_type === "leads" || input.record_type === "both") {
          let filtered = q ? leads.filter(l => l.clubName?.toLowerCase().includes(q) || l.contactName?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q)) : [...leads];
          if (input.stage) filtered = filtered.filter(l => l.stage === input.stage);
          results = [...results, ...filtered.map(l => ({ ...l, _type: "lead" }))];
        }
        if (!input.record_type || input.record_type === "clients" || input.record_type === "both") {
          let filtered = q ? clients.filter(c => c.clubName?.toLowerCase().includes(q) || c.contactName?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)) : [...clients];
          if (input.status) filtered = filtered.filter(c => c.status === input.status);
          results = [...results, ...filtered.map(c => ({ ...c, _type: "client" }))];
        }
        return { results, count: results.length };
      }
      case "create_lead": {
        const newLead = { ...input, id: Date.now(), createdAt: new Date().toISOString().split("T")[0], activity: [], value: input.value || 0, customFields: {}, stage: input.stage || "New Lead", source: input.source || "AI Agent" };
        saveLeads([newLead, ...leads]);
        showToast("Lead created: " + input.clubName);
        return { success: true, id: newLead.id, message: "Lead created successfully" };
      }
      case "update_lead": {
        const updated = leads.map(l => sameId(l.id, input.lead_id) ? { ...l, ...input } : l);
        saveLeads(updated);
        showToast("Lead updated");
        return { success: true, message: "Lead updated successfully" };
      }
      case "create_client": {
        const newClient = { ...input, id: Date.now(), activity: [], value: input.value || 0, customFields: {}, status: input.status || "Active" };
        saveClients([newClient, ...clients]);
        showToast("Client created: " + input.clubName);
        return { success: true, id: newClient.id, message: "Client created successfully" };
      }
      case "update_client": {
        const updated = clients.map(c => c.id === Number(input.client_id) || c.id === input.client_id ? { ...c, ...input } : c);
        saveClients(updated);
        showToast("Client updated");
        return { success: true, message: "Client updated successfully" };
      }
      case "log_activity": {
        const now = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
        const act = { type: input.activity_type, date: now, content: input.content };
        if (input.record_type === "lead") {
          const updated = leads.map(l => sameId(l.id, input.record_id) ? { ...l, activity: [act, ...(l.activity || [])] } : l);
          saveLeads(updated);
        } else {
          const updated = clients.map(c => c.id === Number(input.record_id) || c.id === input.record_id ? { ...c, activity: [act, ...(c.activity || [])] } : c);
          saveClients(updated);
        }
        showToast("Activity logged");
        return { success: true, message: "Activity logged successfully" };
      }
      case "create_calendar_event": {
        const event = { ...input, id: uid(), assignedTo: currentUser?.id || "" };
        saveCalEvents([...calEvents, event]);
        showToast("Event created: " + input.title);
        return { success: true, message: "Calendar event created" };
      }
      case "send_email": {
        try {
          const resp = await fetch("/api/send-email", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input)
          });
          const result = await resp.json();
          if (result.success && input.record_id) {
            await handleAgentTool("log_activity", {
              record_id: input.record_id,
              record_type: input.record_type || "lead",
              activity_type: "Email",
              content: `Email sent: "${input.subject}"`
            });
          }
          return result;
        } catch (e) {
          return { success: false, error: e.message };
        }
      }
      case "research_prospect": {
        return { success: true, message: "Research initiated via web search", club: input.club_name };
      }
      case "score_lead": {
        const lead = leads.find(l => sameId(l.id, input.lead_id));
        if (!lead) return { error: "Lead not found" };
        const activityScore = Math.min((lead.activity?.length || 0) * 10, 40);
        const valueScore = lead.value > 300 ? 30 : lead.value > 100 ? 20 : 10;
        const stageScore = { "New Lead": 5, "Contacted": 10, "Demo Scheduled": 20, "Proposal Sent": 30, "Closed Won": 40, "Closed Lost": 0 }[lead.stage] || 10;
        const total = activityScore + valueScore + stageScore;
        const priority = total >= 70 ? "High" : total >= 40 ? "Medium" : "Low";
        return { lead: lead.clubName, score: total, priority, recommendation: priority === "High" ? "Follow up immediately" : priority === "Medium" ? "Schedule a demo" : "Send initial outreach" };
      }
      case "generate_followup": {
        const lead = leads.find(l => sameId(l.id, input.lead_id));
        if (!lead) return { error: "Lead not found" };
        const lastActivity = lead.activity?.[0];
        return {
          success: true,
          lead: lead.clubName,
          contact: lead.contactName,
          email: lead.email,
          stage: lead.stage,
          lastActivity: lastActivity ? `${lastActivity.type} on ${lastActivity.date}: ${lastActivity.content}` : "No previous activity",
          suggestedSubject: `Following up — ${lead.clubName} + AshGro`,
          context: `Lead is in ${lead.stage} stage, value $${lead.value}/mo, service interest: ${lead.service || "not specified"}`
        };
      }
      case "generate_outreach": {
        return {
          success: true,
          club: input.club_name,
          contact: input.contact_name,
          research: input.research_notes || "No research provided",
          service: input.service || "AshGro CRM Pro",
          suggestedSubject: `${input.club_name} — Membership Growth with AshGro`
        };
      }
      case "find_prospects": {
        return {
          success: true,
          message: "Searching web for prospects",
          location: input.location,
          criteria: input.criteria || "private clubs and country clubs"
        };
      }
      default:
        return { error: "Unknown tool: " + toolName };
    }
  };

  const stats = { leads: leads.length, active: leads.filter(l => l.stage !== "Closed Won" && l.stage !== "Closed Lost").length, clients: clients.length, mrr: fmtVal(clients.filter(c => c.status === "Active").reduce((s, c) => s + Number(c.value), 0)) };

  // eslint-disable-next-line no-unused-vars
  const syncGoogleCalendar = async () => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    const authUserId = authUser?.id;
    if (!authUserId) {
      showToast("Please log in to sync Google Calendar", "error");
      return;
    }
    setGcalSyncing(true);
    try {
      console.log("gcal sync: calling /api/gcal-sync with action=sync");
      const resp = await fetch("/api/gcal-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "sync", user_id: authUserId }),
      });
      console.log("gcal sync: response status =", resp.status);
      const data = await resp.json();
      console.log("gcal sync: response data =", data);
      if (resp.status === 401) {
        setGcalSyncing(false);
        if (!gcalJustConnected) {
          sessionStorage.setItem("postOAuthRedirect", "calendar");
          window.location.href = `/api/gcal-sync?action=auth&user_id=${encodeURIComponent(authUserId)}`;
        } else {
          showToast("Google Calendar connected but sync failed. Please try syncing manually.", "error");
          setGcalJustConnected(false);
        }
        return;
      }
      if (!resp.ok) {
        showToast(data.error || "Google Calendar sync failed.", "error");
        return;
      }
      const mapped = (data.events || []).map(e => {
        const start = e.start || "";
        const date = start.slice(0, 10) || "";
        const time = e.allDay ? "" : (start.slice(11, 16) || "");
        return {
          id: "gcal-" + (e.id || uid()),
          title: e.summary || "(No title)",
          date,
          time,
          type: "Meeting",
          notes: e.description || "",
          fromGcal: true,
          assignedTo: currentUser?.id || authUserId,
        };
      });
      // Deduplicate: remove any local events that were already synced back from Google Calendar
      const localOnly = calEvents.filter(e =>
        !e.id?.startsWith("gcal-") &&
        !mapped.some(g => g.title === e.title && g.date === e.date)
      );
      const merged = [...localOnly, ...mapped];
      saveCalEvents(merged);
      persist("ashgro-gcal-synced", new Date().toISOString());
      setGcalLastSynced(new Date().toISOString());
      setGcalJustConnected(false);
      if (mapped.length === 0) {
        showToast("No events found. Make sure Google Calendar is connected in your account settings.", "error");
      } else {
        showToast(`${mapped.length} events synced from Google Calendar`);
      }
    } catch (e) {
      showToast("Google Calendar sync failed.", "error");
    } finally {
      setGcalSyncing(false);
    }
  };

  // Auto-sync Google Calendar when user opens the Calendar page
  useEffect(() => {
    if (page === "calendar") {
      syncGoogleCalendar();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // On load, if returning from Google OAuth with gcal=connected, show toast and sync
  useEffect(() => {
    if (!currentUser?.id) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal") === "connected") {
      showToast("Google Calendar connected successfully!");
      window.history.replaceState({}, "", window.location.pathname);
      setGcalJustConnected(true);
      setTimeout(() => syncGoogleCalendar(), 1500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  // REMOVED: _removedLeadsList, Settings, LeadFormModal, ClientFormModal, ConvertModal, ActivityModal, CSV block, BulkUploadModal - using module-level components
  // eslint-disable-next-line no-unused-vars
  const _removedLeadsList_PLACEHOLDER = () => {
    const applyView = (view) => {
      setActiveViewId(view.id);
      setFilterStage(Array.isArray(view.filters.stage) ? view.filters.stage : []);
      setFilterUser(Array.isArray(view.filters.user) ? view.filters.user : []);
      setFilterSource(Array.isArray(view.filters.source) ? view.filters.source : []);
      setFilterService(Array.isArray(view.filters.service) ? view.filters.service : []);
      setLeadPage(1);
    };
    const saveCurrentView = () => {
      if (!newViewName.trim()) return;
      const v = { id: uid(), name: newViewName.trim(), filters: { stage: filterStage, user: filterUser, source: filterSource, service: filterService }, isDefault: false };
      const updated = [...savedViews, v];
      saveSavedViews(updated);
      setActiveViewId(v.id);
      setNewViewName("");
      setShowSaveView(false);
      showToast("View saved — " + v.name);
    };
    const deleteView = (id) => {
      const updated = savedViews.filter(v => v.id !== id);
      saveSavedViews(updated);
      if (activeViewId === id) applyView(updated[0]);
    };
    const activeFilters = [
    ...filterStage,
    ...filterUser.map(id => users.find(u => u.id === id)?.name).filter(Boolean),
    ...filterSource,
    ...filterService,
  ];

    return (
      <div>
        {/* Saved Views bar */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: SLATE, textTransform: "uppercase", letterSpacing: 0.5, marginRight: 4 }}>Views:</span>
          {savedViews.map(v => (
            <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 0, borderRadius: 8, border: "1px solid " + (activeViewId === v.id ? GOLD : BORDER), overflow: "hidden" }}>
              <button onClick={() => applyView(v)} style={{ padding: "5px 12px", background: activeViewId === v.id ? "#FFFCF5" : WHITE, color: activeViewId === v.id ? GOLD : SLATE, border: "none", fontSize: 12, fontWeight: activeViewId === v.id ? 700 : 400, cursor: "pointer", fontFamily: "Open Sans,sans-serif" }}>{v.name}</button>
              {!v.isDefault && <button onClick={() => deleteView(v.id)} style={{ padding: "5px 8px", background: activeViewId === v.id ? "#FFFCF5" : WHITE, color: SLATE, border: "none", borderLeft: "1px solid " + BORDER, fontSize: 11, cursor: "pointer", lineHeight: 1 }}>×</button>}
            </div>
          ))}
          {showSaveView ? (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input value={newViewName} onChange={e => setNewViewName(e.target.value)} onKeyDown={e => e.key === "Enter" && saveCurrentView()} placeholder="View name..." style={{ ...S.input, width: 140, padding: "5px 10px", fontSize: 12 }} autoFocus />
              <button onClick={saveCurrentView} style={{ ...S.btnPrimary, padding: "5px 12px", fontSize: 12 }}>Save</button>
              <button onClick={() => setShowSaveView(false)} style={{ ...S.btnGhost, padding: "5px 10px", fontSize: 12 }}>Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowSaveView(true)} style={{ padding: "5px 12px", borderRadius: 8, border: "1px dashed " + BORDER, background: "transparent", color: SLATE, fontSize: 12, cursor: "pointer", fontFamily: "Open Sans,sans-serif" }}>+ Save View</button>
          )}
        </div>

        {/* Filters toolbar */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: SLATE, fontSize: 13, pointerEvents: "none" }}>🔍</span>
            <input
              ref={leadSearchInputRef}
              value={search}
              onChange={e => { setSearch(e.target.value); setLeadPage(1); }}
              placeholder="Search leads..."
              style={{ ...S.input, width: 220, paddingLeft: 32, paddingRight: 28 }}
            />
            {search.length > 0 && (
              <button
                type="button"
                onClick={() => { setSearch(""); setLeadPage(1); requestAnimationFrame(() => leadSearchInputRef.current?.focus()); }}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#5B6770",
                  fontSize: "14px",
                }}
              >
                ×
              </button>
            )}
          </div>
          {/* Stage filter */}
          <MultiSelect
            label="All Stages"
            options={stages.map(s => ({ value: s, label: s }))}
            value={filterStage}
            onChange={(v) => { setFilterStage(v); setLeadPage(1); }}
          />
          {/* Rep filter */}
          <MultiSelect
            label="All Reps"
            options={users.map(u => ({ value: u.id, label: u.name }))}
            value={filterUser}
            onChange={(v) => { setFilterUser(v); setLeadPage(1); }}
          />
          {/* Source filter */}
          <MultiSelect
            label="All Sources"
            options={leadSources.map(s => ({ value: s, label: s }))}
            value={filterSource}
            onChange={(v) => { setFilterSource(v); setLeadPage(1); }}
          />
          {/* Service filter */}
          <MultiSelect
            label="All Services"
            options={services.map(s => ({ value: s, label: s }))}
            value={filterService}
            onChange={(v) => { setFilterService(v); setLeadPage(1); }}
          />
          {/* Clear filters */}
          {activeFilters.length > 0 && (
            <button onClick={() => { setFilterStage([]); setFilterUser([]); setFilterSource([]); setFilterService([]); setLeadPage(1); }} style={{ fontSize: 12, color: RED, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontFamily: "Open Sans,sans-serif" }}>
              Clear ({activeFilters.length})
            </button>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {checkedLeads.length > 0 && <button style={S.btnGold}>✉ Bulk Email ({checkedLeads.length})</button>}
            <button onClick={() => { setBulkStep("upload"); setShowBulkUpload(true); }} style={S.btnGold}>⬆ Import CSV</button>
            <button onClick={() => setShowAddLead(true)} style={S.btnPrimary}>+ Add Lead</button>
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            {activeFilters.map((f, i) => <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, background: NAVY, color: WHITE, fontWeight: 600 }}>{f}</span>)}
          </div>
        )}

        <div style={S.card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={{ ...S.th, width: 44 }}><input type="checkbox" checked={checkedLeads.length === paginatedLeads.length && paginatedLeads.length > 0} onChange={() => setCheckedLeads(checkedLeads.length === paginatedLeads.length ? [] : paginatedLeads.map(l => l.id))} /></th>
              {[
                { col: "clubName", label: "Club Name" },
                { col: "email", label: "Email" },
                { col: "phone", label: "Phone" },
                { col: "followUp", label: "Follow-up" },
                { col: "stage", label: "Stage" },
                { col: "source", label: "Source" },
                { col: "assignedTo", label: "Assigned To" },
                { col: "createdAt", label: "Inquiry Date" },
              ].map(({ col, label }) => (
                <th key={col} style={{ ...S.th, cursor: "pointer", userSelect: "none" }} onClick={() => { if (leadSortColumn === col) setLeadSortDir(d => d === "asc" ? "desc" : "asc"); else { setLeadSortColumn(col); setLeadSortDir("asc"); } setLeadPage(1); }}>
                  {label} {leadSortColumn === col ? (leadSortDir === "asc" ? "↑" : "↓") : ""}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {paginatedLeads.map(l => {
                const rep = users.find(u => u.id === l.assignedTo);
                return (
                  <tr key={l.id} onMouseEnter={e => e.currentTarget.style.background = "#FAFAF7"} onMouseLeave={e => e.currentTarget.style.background = ""} style={{ cursor: "pointer" }}>
                    <td style={{ ...S.td, width: 44 }} onClick={e => { e.stopPropagation(); setCheckedLeads(prev => prev.includes(l.id) ? prev.filter(x => x !== l.id) : [...prev, l.id]); }}><input type="checkbox" checked={checkedLeads.includes(l.id)} onChange={() => {}} /></td>
                    <td style={S.td} onClick={() => { setSelectedLead(l); setEditingLead(false); setPage("lead-detail"); }}><div style={{ display: "flex", alignItems: "center", gap: 10 }}><div style={S.avatar}>{initials(l.contactName)}</div><div><div style={{ fontWeight: 600 }}>{l.clubName}</div><div style={{ fontSize: 11, color: SLATE }}>{l.contactName}</div></div></div></td>
                    <td style={{ ...S.td, color: GOLD }} onClick={() => { setSelectedLead(l); setEditingLead(false); setPage("lead-detail"); }}>{l.email}</td>
                    <td style={{ ...S.td, color: SLATE }} onClick={() => { setSelectedLead(l); setEditingLead(false); setPage("lead-detail"); }}>{l.phone || "—"}</td>
                    <td style={{ ...S.td, color: SLATE }} onClick={() => { setSelectedLead(l); setEditingLead(false); setPage("lead-detail"); }}>{l.followUp ? "📅 " + fmt(l.followUp) : "—"}</td>
                    <td style={S.td} onClick={e => e.stopPropagation()}><select value={l.stage} onChange={e => updateLeadStage(l.id, e.target.value)} style={S.stageSelect(l.stage)}>{stages.map(s => <option key={s} value={s}>{s}</option>)}</select></td>
                    <td style={{ ...S.td, color: SLATE }} onClick={() => { setSelectedLead(l); setEditingLead(false); setPage("lead-detail"); }}>{l.source || "—"}</td>
                    <td style={S.td} onClick={() => { setSelectedLead(l); setEditingLead(false); setPage("lead-detail"); }}>
                      {rep ? <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 22, height: 22, borderRadius: "50%", background: rep.color, color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>{rep.initials}</div><span style={{ fontSize: 12 }}>{rep.name}</span></div> : <span style={{ color: SLATE, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ ...S.td, color: SLATE }} onClick={() => { setSelectedLead(l); setEditingLead(false); setPage("lead-detail"); }}>{fmt(l.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredLeads.length === 0 && <div style={{ padding: 48, textAlign: "center", color: SLATE, fontSize: 13 }}>No leads found.</div>}
          <Pagination cur={leadPage} total={totalLeadPages} onChange={setLeadPage} />
        </div>
      </div>
    );
  };

  // ── BULK CSV UPLOAD ──────────────────────────────────────────────────────────
  const CSV_COLUMNS = ["clubName","contactName","email","phone","title","stage","service","source","value","followUp"];
  const CSV_TEMPLATE = CSV_COLUMNS.join(",") + "\nOakmont Country Club,James Whitfield,jwhitfield@oakmont.com,+1-404-555-0021,Membership Director,New Lead,AshGro CRM Pro,Referral,129.99,2026-04-01\n";

  const parseCSV = (text) => {
    const lines = text.trim().split("\n").filter(l => l.trim());
    if (lines.length < 2) return { rows: [], errors: ["CSV must have a header row and at least one data row."] };
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/\s+/g,"").replace(/[^a-z]/g,""));
    const colMap = {};
    CSV_COLUMNS.forEach(col => {
      const idx = headers.findIndex(h => h === col.toLowerCase() || h === col.replace(/([A-Z])/g, m => m.toLowerCase()));
      if (idx !== -1) colMap[col] = idx;
    });
    const rows = []; const errors = [];
    lines.slice(1).forEach((line, i) => {
      const vals = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
      const row = {}; CSV_COLUMNS.forEach(col => { row[col] = colMap[col] !== undefined ? (vals[colMap[col]] || "") : ""; });
      if (!row.clubName) { errors.push(`Row ${i+2}: Missing Club Name`); return; }
      if (!row.email || !row.email.includes("@")) { errors.push(`Row ${i+2}: Missing or invalid Email`); return; }
      rows.push({ ...row, id: Date.now() + i, createdAt: new Date().toISOString().split("T")[0], activity: [], customFields: {}, stage: row.stage || "New Lead", source: row.source || "Bulk Import", value: parseFloat(row.value) || 0 });
    });
    return { rows, errors };
  };

  const handleCSVFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const { rows, errors } = parseCSV(e.target.result);
      setBulkPreview(rows); setBulkErrors(errors); setBulkStep("preview");
    };
    reader.readAsText(file);
  };

  const confirmBulkImport = async () => {
    const deduped = bulkPreview.filter(r => !leads.some(l => l.email.toLowerCase() === r.email.toLowerCase()));
    const skipped = bulkPreview.length - deduped.length;
    const ok = await saveLeads([...deduped, ...leads]);
    if (ok) {
      setBulkStep("done");
      showToast(`${deduped.length} leads imported${skipped > 0 ? `, ${skipped} skipped (duplicates)` : ""}`);
    }
  };

  const BulkUploadModal = () => (
    <div style={S.modal}><div style={{ ...S.modalBox, width: 640 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div><h3 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>Bulk Import Leads</h3><p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Upload a CSV file to import multiple leads at once.</p></div>
        <button onClick={() => { setShowBulkUpload(false); setBulkStep("upload"); setBulkPreview([]); setBulkErrors([]); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: SLATE }}>×</button>
      </div>

      {bulkStep === "upload" && (
        <div>
          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = "#FFFCF5"; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = BEIGE; }}
            onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = BEIGE; handleCSVFile(e.dataTransfer.files[0]); }}
            style={{ border: "2px dashed " + BORDER, borderRadius: 12, padding: "40px 24px", textAlign: "center", background: BEIGE, marginBottom: 20, cursor: "pointer", transition: "all 0.2s" }}
            onClick={() => document.getElementById("csv-file-input").click()}
          >
            <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: 14, marginBottom: 4 }}>Drop your CSV here or click to browse</div>
            <div style={{ fontSize: 12, color: SLATE }}>Accepts .csv files · Duplicates are automatically skipped</div>
            <input id="csv-file-input" type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={e => handleCSVFile(e.target.files[0])} />
          </div>

          {/* Required columns */}
          <div style={{ background: BEIGE, borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: SLATE, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Required & Supported Columns</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {CSV_COLUMNS.map(col => (<span key={col} style={{ padding: "3px 10px", borderRadius: 6, background: ["clubName","email"].includes(col) ? NAVY : WHITE, color: ["clubName","email"].includes(col) ? WHITE : NAVY, border: "1px solid " + BORDER, fontSize: 11, fontFamily: "monospace" }}>{col}{["clubName","email"].includes(col) ? " *" : ""}</span>))}
            </div>
            <div style={{ fontSize: 11, color: SLATE, marginTop: 8 }}>* Required. All other columns are optional.</div>
          </div>

          {/* Download template */}
          <button onClick={() => { const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "ashgro-leads-template.csv"; a.click(); }} style={{ ...S.btnGold, width: "100%", justifyContent: "center" }}>
            ⬇ Download CSV Template
          </button>
        </div>
      )}

      {bulkStep === "preview" && (
        <div>
          {bulkErrors.length > 0 && (
            <div style={{ background: RED_BG, border: "1px solid #FCA5A5", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: RED, fontSize: 13, marginBottom: 6 }}>⚠ {bulkErrors.length} row{bulkErrors.length > 1 ? "s" : ""} skipped</div>
              {bulkErrors.map((e, i) => <div key={i} style={{ fontSize: 12, color: RED }}>{e}</div>)}
            </div>
          )}
          <div style={{ background: GREEN_BG, border: "1px solid #86EFAC", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: GREEN, fontSize: 13 }}>✓ {bulkPreview.length} leads ready to import</div>
            <div style={{ fontSize: 12, color: GREEN, marginTop: 2 }}>Existing leads with matching emails will be skipped automatically.</div>
          </div>
          <div style={{ maxHeight: 280, overflowY: "auto", border: "1px solid " + BORDER, borderRadius: 10, marginBottom: 20 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ position: "sticky", top: 0, background: WHITE }}>
                {["Club Name","Contact","Email","Stage","Service","Value"].map(h => <th key={h} style={{ ...S.th, padding: "10px 12px" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {bulkPreview.map((r, i) => {
                  const isDupe = leads.some(l => l.email.toLowerCase() === r.email.toLowerCase());
                  return (
                    <tr key={i} style={{ opacity: isDupe ? 0.4 : 1 }}>
                      <td style={{ ...S.td, padding: "8px 12px" }}><div style={{ fontWeight: 600, fontSize: 12 }}>{r.clubName}</div>{isDupe && <div style={{ fontSize: 10, color: RED }}>duplicate — will skip</div>}</td>
                      <td style={{ ...S.td, fontSize: 12, padding: "8px 12px" }}>{r.contactName || "—"}</td>
                      <td style={{ ...S.td, fontSize: 12, color: GOLD, padding: "8px 12px" }}>{r.email}</td>
                      <td style={{ ...S.td, padding: "8px 12px" }}><span style={S.stagePill(r.stage)}>{r.stage}</span></td>
                      <td style={{ ...S.td, fontSize: 12, padding: "8px 12px" }}>{r.service || "—"}</td>
                      <td style={{ ...S.td, fontSize: 12, fontWeight: 700, padding: "8px 12px" }}>{r.value ? fmtVal(r.value) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => { setBulkStep("upload"); setBulkPreview([]); setBulkErrors([]); }} style={S.btnGhost}>← Back</button>
            <button onClick={confirmBulkImport} disabled={bulkPreview.length === 0} style={S.btnPrimary}>Import {bulkPreview.length} Leads</button>
          </div>
        </div>
      )}

      {bulkStep === "done" && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
          <div style={{ fontFamily: "Merriweather,serif", fontSize: 18, fontWeight: 700, color: NAVY, marginBottom: 8 }}>Import Complete</div>
          <div style={{ fontSize: 13, color: SLATE, marginBottom: 24 }}>Your leads have been added to the pipeline.</div>
          <button onClick={() => { setShowBulkUpload(false); setBulkStep("upload"); setBulkPreview([]); setBulkErrors([]); setPage("leads"); }} style={S.btnPrimary}>View Leads</button>
        </div>
      )}
    </div></div>
  );

  // ── INTEGRATIONS PAGE ────────────────────────────────────────────────────────
  const WEBHOOK_URL = process.env.REACT_APP_WEBHOOK_URL || "https://api.ashgropartners.com/v1/leads/intake";
  const WIDGET_ID = "ashgro-" + btoa(process.env.REACT_APP_WIDGET_DOMAIN || "ashgropartners.com").slice(0, 8);

  const simulateWebLead = () => {
    const clubs = ["Pine Valley Club", "Belleair Country Club", "Meridian Golf Club", "Highlands Athletic Club", "Lakewood Estates Club"];
    const names = [["Christopher", "Moore"], ["Amanda", "Wells"], ["Thomas", "Burke"], ["Rachel", "Kim"], ["William", "Nash"]];
    const sources = ["AI Web Agent", "Contact Form", "Demo Request Form"];
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const [first, last] = pick(names);
    const club = pick(clubs);
    const src = pick(sources);
    const svcList = Array.isArray(services) && services.length ? services : ASHGRO_SERVICE_OPTIONS;
    const svc = pick(svcList);
    const newIntakeLead = {
      id: uid(), clubName: club, contactName: first + " " + last,
      email: first.toLowerCase() + "." + last.toLowerCase() + "@" + club.toLowerCase().replace(/\s+/g,"") + ".com",
      phone: "+1-404-555-" + String(Math.floor(Math.random() * 9000) + 1000),
      title: "Membership Director", source: src, service: svc,
      receivedAt: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
      receivedDate: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      status: "new",
    };
    const updated = [newIntakeLead, ...intakeLeads].slice(0, 50);
    saveIntakeLeads(updated);
    showToast("New web lead received from " + club + "!");
  };

  const acceptIntakeLead = (il) => {
    const lead = { id: Date.now(), clubName: il.clubName, contactName: il.contactName, email: il.email, phone: il.phone || "", title: il.title || "", stage: "New Lead", service: il.service || "", source: il.source || "Website", followUp: "", value: 0, createdAt: new Date().toISOString().split("T")[0], customFields: {}, activity: [{ type: "Web Inquiry", date: il.receivedDate + ", " + il.receivedAt, content: "Lead submitted via " + (il.source || "website") + ". Automatically captured and added to pipeline." }] };
    saveLeads([lead, ...leads]);
    saveIntakeLeads(intakeLeads.map(i => i.id === il.id ? { ...i, status: "accepted" } : i));
    showToast(il.clubName + " added to pipeline");
  };

  const dismissIntakeLead = (id) => saveIntakeLeads(intakeLeads.map(i => i.id === id ? { ...i, status: "dismissed" } : i));
  const newIntakeCount = intakeLeads.filter(i => i.status === "new").length;

  const Integrations = () => {
    const [activeTab, setActiveTab] = useState("webhook");
    const [copied, setCopied] = useState(null);
    const copy = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 2000); };

    const webhookPayload = `{
  "clubName": "Oakmont Country Club",
  "contactName": "James Whitfield",
  "email": "jwhitfield@oakmont.com",
  "phone": "+1-404-555-0021",
  "title": "Membership Director",
  "service": "AshGro CRM Pro",
  "source": "AI Web Agent",
  "message": "Interested in learning more about your CRM."
}`;

    const embedSnippet = `<!-- AshGro Lead Capture Widget -->
<script>
  window.AshGroConfig = {
    widgetId: "${WIDGET_ID}",
    primaryColor: "#0C2340",
    accentColor: "#C5A25D",
    position: "bottom-right",
    greeting: "Hi! I'm your AshGro Club Concierge.",
    webhookUrl: "${WEBHOOK_URL}"
  };
</script>
<script src="https://cdn.ashgropartners.com/widget/v1/chat.js" async></script>`;

    const formSnippet = `<!-- AshGro Contact Form Integration -->
<form id="ashgro-lead-form">
  <input name="clubName" placeholder="Club Name" required />
  <input name="contactName" placeholder="Your Name" required />
  <input name="email" type="email" placeholder="Email Address" required />
  <input name="phone" placeholder="Phone Number" />
  <select name="service">
${ASHGRO_SERVICE_OPTIONS.map((s) => `    <option>${s}</option>`).join("\n")}
  </select>
  <button type="submit">Request a Demo</button>
</form>
<script>
  document.getElementById('ashgro-lead-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.target));
    await fetch('https://crm.ashgropartners.com/api/leads-webhook', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, source: 'Contact Form' })
    });
  });
</script>`;

    const curlExample = `curl -X POST ${WEBHOOK_URL} \\
  -H "Content-Type: application/json" \\
  -H "X-AshGro-Key: <your-api-key>" \\
  -d '${webhookPayload.replace(/\n/g, "\\n")}'`;

    const tabStyle = (active) => ({ padding: "9px 18px", border: "none", background: active ? WHITE : "transparent", color: active ? NAVY : SLATE, fontSize: 13, fontFamily: "Open Sans, sans-serif", fontWeight: active ? 700 : 400, cursor: "pointer", borderRadius: "8px 8px 0 0", borderBottom: active ? "2px solid " + GOLD : "2px solid transparent" });
    const codeBox = { background: "#0F172A", borderRadius: 10, padding: "16px 20px", fontFamily: "monospace", fontSize: 12, color: "#E2E8F0", lineHeight: 1.7, overflowX: "auto", whiteSpace: "pre", position: "relative" };
    const CopyBtn = ({ text, k }) => (<button onClick={() => copy(text, k)} style={{ position: "absolute", top: 10, right: 10, background: copied === k ? GREEN : "rgba(255,255,255,0.1)", color: copied === k ? WHITE : "rgba(255,255,255,0.6)", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontFamily: "Open Sans, sans-serif" }}>{copied === k ? "✓ Copied" : "Copy"}</button>);

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 22, color: NAVY }}>Integrations & Web Intake</h2>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: SLATE }}>Connect your website, chatbot, and forms to automatically capture leads into your pipeline.</p>
          </div>
          <button onClick={simulateWebLead} style={{ ...S.btnGold, fontSize: 12, padding: "8px 16px" }}>
            ⚡ Simulate Web Lead
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 20 }}>
          {/* Left — setup tabs */}
          <div>
            {/* Live Intake Feed (shown at top if there are any) */}
            {intakeLeads.length > 0 && (
              <div style={{ ...S.card, marginBottom: 20, overflow: "visible" }}>
                <div style={{ padding: "16px 22px", borderBottom: "1px solid " + BORDER, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 15, color: NAVY }}>Live Web Lead Inbox</div>
                    {newIntakeCount > 0 && <span style={{ background: "#EF4444", color: WHITE, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>{newIntakeCount} new</span>}
                  </div>
                  <button onClick={() => saveIntakeLeads([])} style={{ fontSize: 11, color: SLATE, background: "none", border: "none", cursor: "pointer" }}>Clear all</button>
                </div>
                <div style={{ maxHeight: 320, overflowY: "auto" }}>
                  {intakeLeads.map(il => (
                    <div key={il.id} style={{ padding: "14px 22px", borderBottom: "1px solid " + BORDER, display: "flex", gap: 14, alignItems: "flex-start", background: il.status === "new" ? "#FFFCF5" : WHITE, opacity: il.status === "dismissed" ? 0.45 : 1 }}>
                      <div style={{ width: 38, height: 38, borderRadius: "50%", background: il.status === "accepted" ? GREEN_BG : GOLD, color: il.status === "accepted" ? GREEN : WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0, border: il.status === "new" ? "2px solid " + GOLD : "2px solid transparent" }}>{initials(il.contactName)}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: NAVY }}>{il.clubName}</div>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span style={{ fontSize: 10, color: SLATE }}>{il.receivedDate}, {il.receivedAt}</span>
                            {il.status === "new" && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444", display: "inline-block" }} />}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: SLATE }}>{il.contactName} · {il.title}</div>
                        <div style={{ fontSize: 12, color: GOLD, marginTop: 2 }}>{il.email}</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ fontSize: 11, background: BEIGE_DARK, color: NAVY, padding: "2px 8px", borderRadius: 4 }}>{il.source}</span>
                          {il.service && <span style={{ fontSize: 11, background: "#EEF2FF", color: "#3730A3", padding: "2px 8px", borderRadius: 4 }}>{il.service}</span>}
                          {il.status === "new" && (<>
                            <button onClick={() => acceptIntakeLead(il)} style={{ marginLeft: "auto", ...S.btnGreen, padding: "4px 12px", fontSize: 11 }}>+ Add to Pipeline</button>
                            <button onClick={() => dismissIntakeLead(il.id)} style={{ ...S.btnGhost, padding: "4px 10px", fontSize: 11, color: SLATE }}>Dismiss</button>
                          </>)}
                          {il.status === "accepted" && <span style={{ marginLeft: "auto", fontSize: 11, color: GREEN, fontWeight: 700 }}>✓ Added to pipeline</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Setup Tabs */}
            <div style={S.card}>
              <div style={{ display: "flex", borderBottom: "1px solid " + BORDER, padding: "0 8px", background: BEIGE, gap: 2 }}>
                {[["webhook","🔗 Webhook API"],["chatbot","🤖 AI Chatbot Widget"],["form","📋 Form Embed"],["csv","📂 Bulk CSV Import"]].map(([id, label]) => (
                  <button key={id} onClick={() => setActiveTab(id)} style={tabStyle(activeTab === id)}>{label}</button>
                ))}
              </div>
              <div style={{ padding: "28px 28px" }}>

                {/* WEBHOOK TAB */}
                {activeTab === "webhook" && (
                  <div>
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 6 }}>Webhook Endpoint</div>
                      <p style={{ margin: "0 0 16px", fontSize: 13, color: SLATE, lineHeight: 1.7 }}>POST lead data to this endpoint from any tool — Zapier, Make, your own app, or directly from your AI chatbot. Leads appear instantly in your pipeline.</p>
                      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                        <div style={{ flex: 1, padding: "10px 14px", background: BEIGE, border: "1px solid " + BORDER, borderRadius: 8, fontFamily: "monospace", fontSize: 12, color: NAVY }}>{WEBHOOK_URL}</div>
                        <button onClick={() => copy(WEBHOOK_URL, "url")} style={{ ...S.btnGold, padding: "10px 16px", whiteSpace: "nowrap" }}>{copied === "url" ? "✓ Copied" : "Copy URL"}</button>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: SLATE, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Request Body (JSON)</div>
                        <div style={{ ...codeBox, position: "relative" }}>{webhookPayload}<CopyBtn text={webhookPayload} k="payload" /></div>
                      </div>
                      <div style={{ marginBottom: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: SLATE, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>cURL Example</div>
                        <div style={{ ...codeBox, position: "relative" }}>{curlExample}<CopyBtn text={curlExample} k="curl" /></div>
                      </div>
                    </div>
                    <div style={{ background: BEIGE, borderRadius: 10, padding: "14px 18px" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 8 }}>✦ Works with</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {["Zapier","Make (Integromat)","HubSpot","Typeform","Webflow","Wix","Custom HTML forms","Any REST client"].map(t => (<span key={t} style={{ padding: "4px 10px", background: WHITE, border: "1px solid " + BORDER, borderRadius: 6, fontSize: 12, color: NAVY }}>{t}</span>))}
                      </div>
                    </div>
                  </div>
                )}

                {/* CHATBOT TAB */}
                {activeTab === "chatbot" && (() => {
                  const KB_CATEGORIES = ["Services & Pricing", "FAQs", "About AshGro", "Club Consulting", "Onboarding", "Objection Handling", "Case Studies", "Other"];
                  const filteredKb = knowledgeBase.filter(e => {
                    const matchCat = kbFilter === "All" || e.category === kbFilter;
                    const matchQ = !kbSearch || e.title.toLowerCase().includes(kbSearch.toLowerCase()) || e.content.toLowerCase().includes(kbSearch.toLowerCase());
                    return matchCat && matchQ;
                  });
                  const totalWords = knowledgeBase.reduce((s, e) => s + e.content.split(/\s+/).filter(Boolean).length, 0);

                  const handleFileUpload = (file) => {
                    if (!file) return;
                    const ext = file.name.split(".").pop().toLowerCase();
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const raw = ev.target.result;
                      let content = "";
                      if (ext === "txt" || ext === "md") {
                        content = raw;
                      } else {
                        // For PDF/DOCX: extract visible text best-effort from raw string
                        content = raw.replace(/[^\x20-\x7E\n]/g, " ").replace(/\s{3,}/g, "\n").trim().slice(0, 8000);
                        if (!content || content.length < 50) {
                          content = "[File content extracted — please review and edit as needed.]\n\nFile: " + file.name;
                        }
                      }
                      const title = file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                      setKbForm({ title, category: "Services & Pricing", content: content.slice(0, 8000) });
                      setShowAddKbEntry(true);
                    };
                    reader.readAsText(file);
                  };

                  const saveKbEntry = () => {
                    if (!kbForm.title.trim() || !kbForm.content.trim()) return;
                    if (editingKb) {
                      saveKnowledgeBase(knowledgeBase.map(e => e.id === editingKb ? { ...e, ...kbForm, updatedAt: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) } : e));
                      setEditingKb(null);
                    } else {
                      saveKnowledgeBase([...knowledgeBase, { id: uid(), ...kbForm, createdAt: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }), wordCount: kbForm.content.split(/\s+/).filter(Boolean).length }]);
                    }
                    setKbForm({ title: "", category: "Services & Pricing", content: "" });
                    setShowAddKbEntry(false);
                    showToast(editingKb ? "Entry updated" : "Knowledge base entry added");
                  };

                  const catColors = { "Services & Pricing": { bg: "#EEF2FF", text: "#3730A3" }, "FAQs": { bg: "#FFF7ED", text: "#C2410C" }, "About AshGro": { bg: GREEN_BG, text: GREEN }, "Club Consulting": { bg: "#F0F9FF", text: "#0369A1" }, "Onboarding": { bg: "#FDF4FF", text: "#7E22CE" }, "Objection Handling": { bg: RED_BG, text: RED }, "Case Studies": { bg: "#FFFBEB", text: "#92400E" }, "Other": { bg: BEIGE_DARK, text: SLATE } };
                  const CatPill = ({ cat }) => <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: (catColors[cat] || catColors["Other"]).bg, color: (catColors[cat] || catColors["Other"]).text }}>{cat}</span>;

                  return (
                    <div>
                      {/* Embed section */}
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 6 }}>AI Chatbot Widget</div>
                        <p style={{ margin: "0 0 12px", fontSize: 13, color: SLATE, lineHeight: 1.7 }}>Paste this into your website's <code style={{ background: BEIGE, padding: "1px 6px", borderRadius: 4, fontSize: 12 }}>&lt;head&gt;</code> tag. The chatbot will reference your knowledge base when answering visitor questions.</p>
                        <div style={{ ...codeBox, position: "relative", fontSize: 11 }}>{embedSnippet}<CopyBtn text={embedSnippet} k="embed" /></div>
                      </div>

                      {/* ── KNOWLEDGE BASE ────────────────────────────── */}
                      <div style={{ borderTop: "2px solid " + GOLD, paddingTop: 24 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                          <div>
                            <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 4 }}>📚 Chatbot Knowledge Base</div>
                            <div style={{ fontSize: 12, color: SLATE }}>
                              {knowledgeBase.length} document{knowledgeBase.length !== 1 ? "s" : ""} · {totalWords.toLocaleString()} words · Used by the AI chatbot to answer visitor questions
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <label style={{ ...S.btnGold, cursor: "pointer", padding: "8px 14px", fontSize: 12 }}>
                              ⬆ Upload File
                              <input type="file" accept=".txt,.md,.pdf,.docx,.doc" style={{ display: "none" }} onChange={e => handleFileUpload(e.target.files[0])} />
                            </label>
                            <button onClick={() => { setKbForm({ title: "", category: "Services & Pricing", content: "" }); setEditingKb(null); setShowAddKbEntry(true); }} style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 14px" }}>+ Add Entry</button>
                          </div>
                        </div>

                        {/* Add / Edit form */}
                        {showAddKbEntry && (
                          <div style={{ background: BEIGE, border: "1px solid " + BORDER, borderRadius: 12, padding: "20px 22px", marginBottom: 20 }}>
                            <div style={{ fontWeight: 700, color: NAVY, fontSize: 14, marginBottom: 16 }}>{editingKb ? "Edit Entry" : "New Knowledge Base Entry"}</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                              <div style={{ gridColumn: "1/-1" }}>
                                <label style={S.label}>Title *</label>
                                <input value={kbForm.title} onChange={e => setKbForm({ ...kbForm, title: e.target.value })} placeholder="e.g. AshGro CRM Pricing & Plans" style={S.input} />
                              </div>
                              <div>
                                <label style={S.label}>Category</label>
                                <select value={kbForm.category} onChange={e => setKbForm({ ...kbForm, category: e.target.value })} style={S.input}>
                                  {KB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </div>
                              <div style={{ display: "flex", alignItems: "flex-end" }}>
                                <div style={{ fontSize: 12, color: SLATE, background: WHITE, border: "1px solid " + BORDER, borderRadius: 8, padding: "8px 12px", width: "100%" }}>
                                  <span style={{ fontWeight: 700 }}>{kbForm.content.split(/\s+/).filter(Boolean).length}</span> words · <span style={{ fontWeight: 700 }}>{kbForm.content.length}</span> chars
                                  {kbForm.content.length > 7000 && <span style={{ color: "#F97316", marginLeft: 6 }}>⚠ Long — consider splitting</span>}
                                </div>
                              </div>
                              <div style={{ gridColumn: "1/-1" }}>
                                <label style={S.label}>Content *<span style={{ fontWeight: 400, color: SLATE }}> — paste docs, FAQs, pricing tables, talking points, anything the chatbot should know</span></label>
                                <textarea
                                  value={kbForm.content}
                                  onChange={e => setKbForm({ ...kbForm, content: e.target.value })}
                                  rows={10}
                                  placeholder={"Example:\n\nAshGro CRM starts at $79.99/month for the base plan.\nAshGro CRM Pro is $129.99/month and includes bulk email, unlimited templates, and advanced reporting.\nThe AI Club Concierge bundle (CRM Pro + AI Agent) is $349.99/month..."}
                                  style={{ ...S.input, resize: "vertical", lineHeight: 1.7, fontFamily: "Open Sans, sans-serif" }}
                                />
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                              <button onClick={() => { setShowAddKbEntry(false); setEditingKb(null); setKbForm({ title: "", category: "Services & Pricing", content: "" }); }} style={S.btnGhost}>Cancel</button>
                              <button onClick={saveKbEntry} disabled={!kbForm.title.trim() || !kbForm.content.trim()} style={{ ...S.btnPrimary, opacity: !kbForm.title.trim() || !kbForm.content.trim() ? 0.5 : 1 }}>
                                {editingKb ? "Save Changes" : "Add to Knowledge Base"}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Search + filter bar */}
                        {knowledgeBase.length > 0 && (
                          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                            <div style={{ position: "relative", flex: 1 }}>
                              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: SLATE, fontSize: 13 }}>🔍</span>
                              <input
                                ref={kbSearchInputRef}
                                value={kbSearch}
                                onChange={e => setKbSearch(e.target.value)}
                                placeholder="Search knowledge base..."
                                style={{ ...S.input, paddingLeft: 32, paddingRight: 28 }}
                              />
                              {kbSearch.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => { setKbSearch(""); requestAnimationFrame(() => kbSearchInputRef.current?.focus()); }}
                                  style={{
                                    position: "absolute",
                                    right: 8,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#5B6770",
                                    fontSize: "14px",
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                            <select value={kbFilter} onChange={e => setKbFilter(e.target.value)} style={{ ...S.input, width: "auto", minWidth: 160 }}>
                              <option value="All">All Categories</option>
                              {KB_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                        )}

                        {/* Entry cards */}
                        {knowledgeBase.length === 0 && !showAddKbEntry ? (
                          <div
                            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = "#FFFCF5"; }}
                            onDragLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = BEIGE; }}
                            onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = BEIGE; handleFileUpload(e.dataTransfer.files[0]); }}
                            style={{ border: "2px dashed " + BORDER, borderRadius: 12, padding: "40px 24px", textAlign: "center", background: BEIGE, cursor: "pointer", transition: "all 0.2s" }}
                          >
                            <div style={{ fontSize: 40, marginBottom: 12 }}>📚</div>
                            <div style={{ fontWeight: 700, color: NAVY, fontSize: 14, marginBottom: 6 }}>Your knowledge base is empty</div>
                            <div style={{ fontSize: 13, color: SLATE, marginBottom: 20, lineHeight: 1.7 }}>Add docs, FAQs, service info, pricing, and talking points so the AI chatbot can answer visitor questions accurately.</div>
                            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                              {["Services & Pricing", "FAQs", "About AshGro", "Objection Handling"].map(cat => (
                                <button key={cat} onClick={() => { setKbForm({ title: cat, category: cat, content: "" }); setShowAddKbEntry(true); }} style={{ ...S.btnGhost, fontSize: 12, padding: "7px 14px" }}>+ {cat}</button>
                              ))}
                            </div>
                          </div>
                        ) : filteredKb.length === 0 ? (
                          <div style={{ padding: "24px 0", textAlign: "center", color: SLATE, fontSize: 13 }}>No entries match your search.</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {filteredKb.map(entry => (
                              <KbEntryCard
                                key={entry.id}
                                entry={entry}
                                S={S}
                                setKbForm={setKbForm}
                                setEditingKb={setEditingKb}
                                setShowAddKbEntry={setShowAddKbEntry}
                                saveKnowledgeBase={saveKnowledgeBase}
                                knowledgeBase={knowledgeBase}
                              />
                            ))}
                          </div>
                        )}

                        {/* KB stats footer */}
                        {knowledgeBase.length > 0 && (
                          <div style={{ marginTop: 16, padding: "12px 16px", background: BEIGE, borderRadius: 10, display: "flex", gap: 20, flexWrap: "wrap" }}>
                            {KB_CATEGORIES.map(cat => {
                              const count = knowledgeBase.filter(e => e.category === cat).length;
                              if (!count) return null;
                              return <div key={cat} style={{ display: "flex", alignItems: "center", gap: 6 }}><CatPill cat={cat} /><span style={{ fontSize: 11, color: SLATE }}>{count}</span></div>;
                            })}
                            <div style={{ marginLeft: "auto", fontSize: 11, color: SLATE }}>{totalWords.toLocaleString()} total words in knowledge base</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* FORM TAB */}
                {activeTab === "form" && (
                  <div>
                    <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 6 }}>HTML Form Integration</div>
                    <p style={{ margin: "0 0 16px", fontSize: 13, color: SLATE, lineHeight: 1.7 }}>Add this to any page on your website — demo request pages, contact pages, footer forms. Submissions post directly to your CRM with source tracking.</p>
                    <div style={{ ...codeBox, position: "relative" }}>{formSnippet}<CopyBtn text={formSnippet} k="form" /></div>
                    <div style={{ marginTop: 16, background: BEIGE, borderRadius: 10, padding: "14px 18px" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 6 }}>💡 Tip</div>
                      <div style={{ fontSize: 12, color: SLATE, lineHeight: 1.6 }}>You can customize the <code style={{ background: WHITE, padding: "1px 5px", borderRadius: 4 }}>source</code> value to track exactly which page or form the lead came from — e.g. "Homepage Demo Form", "Pricing Page CTA", "Blog Footer".</div>
                    </div>
                  </div>
                )}

                {/* CSV TAB */}
                {activeTab === "csv" && (
                  <div>
                    <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 16, color: NAVY, marginBottom: 6 }}>Bulk CSV Import</div>
                    <p style={{ margin: "0 0 20px", fontSize: 13, color: SLATE, lineHeight: 1.7 }}>Upload a spreadsheet of leads all at once. Great for importing from another CRM, a trade show contact list, or a spreadsheet your team has been maintaining.</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
                      {[["✓ Duplicate detection", "Skips leads already in the system by email"], ["✓ Error reporting", "Flags rows with missing or invalid data before import"], ["✓ Stage mapping", "Maps any valid stage name automatically"], ["✓ Source tracking", "Marks all imports as Bulk Import by default"]].map(([title, desc]) => (
                        <div key={title} style={{ background: BEIGE, borderRadius: 8, padding: "12px 14px" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: GREEN, marginBottom: 3 }}>{title}</div>
                          <div style={{ fontSize: 12, color: SLATE }}>{desc}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button onClick={() => setShowBulkUpload(true)} style={{ ...S.btnPrimary, flex: 1, justifyContent: "center" }}>📂 Open Import Wizard</button>
                      <button onClick={() => { const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "ashgro-leads-template.csv"; a.click(); }} style={{ ...S.btnGold }}>⬇ Template</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right — status panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Integration Status */}
            <div style={{ ...S.card, padding: "22px 24px" }}>
              <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 16 }}>Integration Status</div>
              {[
                { label: "Webhook Endpoint", status: "live", detail: "Accepting POST requests" },
                { label: "AI Chatbot Widget", status: knowledgeBase.length > 0 ? "live" : "setup", detail: knowledgeBase.length > 0 ? knowledgeBase.length + " KB docs · " + knowledgeBase.reduce((s,e) => s + e.content.split(/\s+/).filter(Boolean).length, 0).toLocaleString() + " words" : "Add knowledge base to activate" },
                { label: "Gmail Sync", status: "live", detail: lastSynced ? "Last synced " + new Date(lastSynced).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "Never synced" },
                { label: "Bulk CSV Import", status: "live", detail: "Ready to use" },
              ].map(({ label, status, detail }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid " + BORDER }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{label}</div>
                    <div style={{ fontSize: 11, color: SLATE }}>{detail}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 6, background: status === "live" ? GREEN_BG : BEIGE_DARK, color: status === "live" ? GREEN : SLATE }}>{status === "live" ? "● Live" : "○ Setup"}</span>
                </div>
              ))}
            </div>

            {/* Lead Sources breakdown */}
            <div style={{ ...S.card, padding: "22px 24px" }}>
              <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 16 }}>Leads by Source</div>
              {leadSources.map(src => {
                const count = leads.filter(l => l.source === src).length;
                const pct = leads.length ? Math.round((count / leads.length) * 100) : 0;
                if (!count) return null;
                return (
                  <div key={src} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: NAVY, fontWeight: 600 }}>{src}</span>
                      <span style={{ color: SLATE }}>{count} · {pct}%</span>
                    </div>
                    <div style={{ background: BEIGE_DARK, borderRadius: 4, height: 5 }}><div style={{ width: pct + "%", background: GOLD, borderRadius: 4, height: 5 }} /></div>
                  </div>
                );
              })}
              {leads.length === 0 && <div style={{ fontSize: 12, color: SLATE, fontStyle: "italic" }}>No leads yet.</div>}
            </div>

            {/* Web lead intake counter */}
            <div style={{ ...S.card, padding: "22px 24px" }}>
              <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY, marginBottom: 4 }}>Web Intake Stats</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 14 }}>
                {[{ label: "Received", value: intakeLeads.length }, { label: "Accepted", value: intakeLeads.filter(i => i.status === "accepted").length }, { label: "Pending", value: newIntakeCount }].map(({ label, value }) => (
                  <div key={label} style={{ background: BEIGE, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "Merriweather,serif", color: NAVY }}>{value}</div>
                    <div style={{ fontSize: 11, color: SLATE, marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── PROPOSAL GENERATOR ──────────────────────────────────────────────────────
  const generateProposal = async () => {
    if (!proposalRecord) return;
    setProposalLoading(true); setGeneratedProposal(null);
    const r = proposalRecord;
    try {
      const resp = await fetch("/api/chat", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 2000,
          messages: [{ role: "user", content: `Write a professional, persuasive sales proposal for AshGro Membership Partners. This is a B2B proposal for a private club.

CLIENT DETAILS:
- Club: ${r.clubName}
- Contact: ${r.contactName}, ${r.title || ""}
- Service Interest: ${r.service || "AshGro CRM Pro"}
- Proposed Monthly Investment: ${r.value ? fmtVal(r.value) : "TBD"}
- Lead Source: ${r.source || "N/A"}

Write a structured proposal with these sections:
1. Executive Summary (2-3 sentences tailored to their club)
2. The Challenge We Solve (specific to private club membership sales)
3. Our Recommended Solution (specific to the service they're interested in)
4. What's Included (bullet list of features/deliverables)
5. Investment (monthly + annual pricing)
6. Why AshGro (3 differentiators)
7. Next Steps (clear CTA)

Tone: Premium, consultative, confident. Like a boutique firm, not a software company. Use the club's name throughout. Format with clear section headers using ##.` }] })
      });
      const data = await resp.json();
      setGeneratedProposal(data.content?.find(b => b.type === "text")?.text || "");
    } catch(e) { setGeneratedProposal("Unable to generate proposal. Please try again."); }
    setProposalLoading(false);
  };

  const ProposalModal = () => {
    if (!showProposalModal) return null;
    const formatProposal = (text) => text?.split("\n").map((line, i) => {
      if (line.startsWith("## ")) return <div key={i} style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 16, color: NAVY, marginTop: 24, marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid " + BORDER }}>{line.replace("## ", "")}</div>;
      if (line.startsWith("- ") || line.startsWith("• ")) return <div key={i} style={{ fontSize: 13, color: NAVY, lineHeight: 1.8, paddingLeft: 16 }}>{"• " + line.replace(/^[-•]\s/, "")}</div>;
      if (line.trim() === "") return <div key={i} style={{ height: 8 }} />;
      return <div key={i} style={{ fontSize: 13, color: NAVY, lineHeight: 1.8 }}>{line}</div>;
    });
    return (
      <div style={S.modal}><div style={{ ...S.modalBox, width: 720, maxHeight: "90vh" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div><h3 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 20, color: NAVY }}>AI Proposal Generator</h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>{proposalRecord?.clubName} · {proposalRecord?.service || "Service TBD"}</p></div>
          <button onClick={() => { setShowProposalModal(false); setGeneratedProposal(null); }} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: SLATE }}>×</button>
        </div>
        {!generatedProposal && !proposalLoading && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
            <div style={{ fontFamily: "Merriweather,serif", fontSize: 17, color: NAVY, marginBottom: 8 }}>Ready to generate a proposal</div>
            <div style={{ fontSize: 13, color: SLATE, marginBottom: 24, lineHeight: 1.7 }}>The AI will write a full, branded proposal for {proposalRecord?.clubName} based on their profile, service interest, and deal value.</div>
            <button onClick={generateProposal} style={{ ...S.btnPrimary, padding: "12px 28px", fontSize: 14 }}>✦ Generate Proposal</button>
          </div>
        )}
        {proposalLoading && <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div style={{ display: "inline-flex", gap: 8 }}>{[0,1,2].map(n => <div key={n} style={{ width: 10, height: 10, borderRadius: "50%", background: GOLD, animation: "pulse 1.4s infinite", animationDelay: n * 0.2 + "s" }} />)}</div>
          <div style={{ fontSize: 13, color: SLATE, marginTop: 16 }}>Drafting your proposal...</div>
        </div>}
        {generatedProposal && !proposalLoading && (
          <div>
            <div style={{ background: BEIGE, borderRadius: 10, padding: "24px 28px", maxHeight: 440, overflowY: "auto", marginBottom: 16 }}>
              {formatProposal(generatedProposal)}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { navigator.clipboard.writeText(generatedProposal); setProposalCopied(true); setTimeout(() => setProposalCopied(false), 2000); }} style={{ ...S.btnGold }}>{proposalCopied ? "✓ Copied!" : "Copy Proposal"}</button>
              <button onClick={generateProposal} style={S.btnGhost}>↻ Regenerate</button>
              <a href={`mailto:${proposalRecord?.email}?subject=Your AshGro Proposal — ${proposalRecord?.clubName}&body=${encodeURIComponent(generatedProposal)}`} style={{ ...S.btnPrimary, textDecoration: "none" }}>✉ Open in Mail</a>
            </div>
          </div>
        )}
      </div></div>
    );
  };

  // ── ANALYTICS PAGE ───────────────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const _Analytics = () => {
    const [dateRange, setDateRange] = useState("all");
    const now = new Date();
    const filterDate = (d) => {
      if (dateRange === "all") return true;
      const date = new Date(d);
      if (dateRange === "7d") return (now - date) <= 7 * 86400000;
      if (dateRange === "30d") return (now - date) <= 30 * 86400000;
      if (dateRange === "90d") return (now - date) <= 90 * 86400000;
      return true;
    };
    const filteredL = leads.filter(l => filterDate(l.createdAt));
    const closedWon = filteredL.filter(l => l.stage === "Closed Won");
    const closedLost = filteredL.filter(l => l.stage === "Closed Lost");
    const convRate = filteredL.length ? Math.round((closedWon.length / filteredL.length) * 100) : 0;
    const activeMRR = clients.filter(c => c.status === "Active").reduce((s, c) => s + Number(c.value), 0);
    const totalARR = activeMRR * 12;
    // eslint-disable-next-line no-unused-vars
    const _avgDeal = closedWon.length ? closedWon.reduce((s, l) => s + Number(l.value), 0) / closedWon.length : 0;
    const atRisk = clients.filter(c => c.status === "At Risk").reduce((s, c) => s + Number(c.value), 0);

    const sourceData = leads.reduce((acc, l) => { const src = l.source || "Unknown"; acc[src] = (acc[src] || { count: 0, value: 0 }); acc[src].count++; acc[src].value += Number(l.value || 0); return acc; }, {});
    const stageData = stages.map(s => ({ stage: s, count: leads.filter(l => l.stage === s).length, value: leads.filter(l => l.stage === s).reduce((a, l) => a + Number(l.value || 0), 0) }));
    const maxStageCount = Math.max(...stageData.map(s => s.count), 1);

    const repData = users.map(u => ({
      ...u,
      leads: leads.filter(l => l.assignedTo === u.id).length,
      won: leads.filter(l => l.assignedTo === u.id && l.stage === "Closed Won").length,
      mrr: clients.filter(c => c.assignedTo === u.id && c.status === "Active").reduce((s, c) => s + Number(c.value), 0),
    }));

    const StatCard = ({ label, value, sub, color }) => (
      <div style={{ ...S.card, padding: "22px 24px" }}>
        <div style={{ fontSize: 11, color: SLATE, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "Merriweather,serif", color: color || NAVY }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: SLATE, marginTop: 4 }}>{sub}</div>}
      </div>
    );

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div><h2 style={{ margin: 0, fontFamily: "Merriweather,serif", fontSize: 22, color: NAVY }}>Analytics & Reporting</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: SLATE }}>Pipeline performance, rep activity, and revenue trends.</p></div>
          <div style={{ display: "flex", gap: 8 }}>
            {[["7d","7 Days"],["30d","30 Days"],["90d","90 Days"],["all","All Time"]].map(([v, l]) => (
              <button key={v} onClick={() => setDateRange(v)} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + (dateRange === v ? GOLD : BORDER), background: dateRange === v ? "#FFFCF5" : WHITE, color: dateRange === v ? GOLD : SLATE, fontSize: 12, fontWeight: dateRange === v ? 700 : 400, cursor: "pointer", fontFamily: "Open Sans, sans-serif" }}>{l}</button>
            ))}
          </div>
        </div>

        {/* KPI Row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14, marginBottom: 24 }}>
          <StatCard label="Total Leads" value={filteredL.length} sub={`${closedWon.length} closed won`} />
          <StatCard label="Conversion Rate" value={convRate + "%"} sub={`${closedLost.length} closed lost`} color={convRate > 20 ? GREEN : convRate > 10 ? "#C2410C" : RED} />
          <StatCard label="Active MRR" value={fmtVal(activeMRR)} sub="from active clients" color={NAVY} />
          <StatCard label="Annual Run Rate" value={"$" + (totalARR / 1000).toFixed(0) + "k"} sub="projected ARR" color={GREEN} />
          <StatCard label="At-Risk MRR" value={fmtVal(atRisk)} sub={`${clients.filter(c=>c.status==="At Risk").length} clients`} color={atRisk > 0 ? RED : SLATE} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
          {/* Pipeline Funnel */}
          <div style={{ ...S.card, padding: "24px 28px" }}>
            <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 20 }}>Pipeline Funnel</div>
            {stageData.map(({ stage, count, value }) => (
              <div key={stage} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={S.stagePill(stage)}>{stage}</span>
                    <span style={{ fontSize: 12, color: SLATE }}>{count} leads</span>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: NAVY }}>{value > 0 ? fmtVal(value) + "/mo" : "—"}</span>
                </div>
                <div style={{ background: BEIGE_DARK, borderRadius: 4, height: 6 }}>
                  <div style={{ width: (count / maxStageCount * 100) + "%", background: (STAGE_COLORS[stage] || {}).text || GOLD, borderRadius: 4, height: 6, transition: "width 0.4s" }} />
                </div>
              </div>
            ))}
          </div>

          {/* Lead Source Performance */}
          <div style={{ ...S.card, padding: "24px 28px" }}>
            <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 20 }}>Lead Source ROI</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                {["Source","Leads","Pipeline Value","Avg Deal"].map(h => <th key={h} style={{ ...S.th, padding: "8px 10px", fontSize: 11 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {Object.entries(sourceData).sort((a,b) => b[1].count - a[1].count).map(([src, data]) => (
                  <tr key={src}>
                    <td style={{ ...S.td, fontSize: 12, fontWeight: 600, padding: "8px 10px" }}>{src}</td>
                    <td style={{ ...S.td, fontSize: 12, padding: "8px 10px", textAlign: "center" }}>{data.count}</td>
                    <td style={{ ...S.td, fontSize: 12, padding: "8px 10px" }}>{fmtVal(data.value)}</td>
                    <td style={{ ...S.td, fontSize: 12, padding: "8px 10px" }}>{fmtVal(data.value / data.count)}</td>
                  </tr>
                ))}
                {Object.keys(sourceData).length === 0 && <tr><td colSpan={4} style={{ ...S.td, textAlign: "center", color: SLATE, fontSize: 12 }}>No data yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Rep Performance */}
        <div style={{ ...S.card, padding: "24px 28px", marginBottom: 20 }}>
          <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 20 }}>Rep Performance</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Rep","Role","Leads Assigned","Closed Won","Active MRR","Win Rate"].map(h => <th key={h} style={{ ...S.th, padding: "10px 14px", fontSize: 11 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {repData.map(rep => {
                const wr = rep.leads ? Math.round((rep.won / rep.leads) * 100) : 0;
                return (
                  <tr key={rep.id} onMouseEnter={e => e.currentTarget.style.background = "#FAFAF7"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ ...S.td, padding: "12px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: rep.color, color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{rep.initials}</div>
                        <div><div style={{ fontWeight: 600, fontSize: 13 }}>{rep.name}</div><div style={{ fontSize: 11, color: SLATE }}>{rep.email}</div></div>
                      </div>
                    </td>
                    <td style={{ ...S.td, fontSize: 12, padding: "12px 14px" }}>{rep.role}</td>
                    <td style={{ ...S.td, fontSize: 13, fontWeight: 700, padding: "12px 14px", textAlign: "center" }}>{rep.leads}</td>
                    <td style={{ ...S.td, fontSize: 13, fontWeight: 700, padding: "12px 14px", textAlign: "center", color: GREEN }}>{rep.won}</td>
                    <td style={{ ...S.td, fontSize: 13, fontWeight: 700, padding: "12px 14px" }}>{fmtVal(rep.mrr)}/mo</td>
                    <td style={{ ...S.td, padding: "12px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, background: BEIGE_DARK, borderRadius: 4, height: 6 }}><div style={{ width: wr + "%", background: wr > 25 ? GREEN : wr > 10 ? GOLD : RED, borderRadius: 4, height: 6 }} /></div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: wr > 25 ? GREEN : wr > 10 ? GOLD : SLATE, minWidth: 32 }}>{wr}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {repData.every(r => r.leads === 0) && <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: SLATE, fontSize: 12, padding: 24 }}>Assign leads to reps to see performance data.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Client Health */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ ...S.card, padding: "24px 28px" }}>
            <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 20 }}>Client Health Overview</div>
            {clientStatuses.map(st => {
              const count = clients.filter(c => c.status === st).length;
              const mrr = clients.filter(c => c.status === st).reduce((s, c) => s + Number(c.value), 0);
              return (
                <div key={st} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid " + BORDER }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={S.statusPill(st)}>{st}</span>
                    <span style={{ fontSize: 12, color: SLATE }}>{count} client{count !== 1 ? "s" : ""}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{fmtVal(mrr)}/mo</span>
                </div>
              );
            })}
          </div>
          <div style={{ ...S.card, padding: "24px 28px" }}>
            <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 20 }}>Upcoming Renewals</div>
            {clients.filter(c => { if (!c.renewalDate) return false; const diff = (new Date(c.renewalDate) - now) / 86400000; return diff > 0 && diff <= 90; }).sort((a,b) => new Date(a.renewalDate) - new Date(b.renewalDate)).map(c => {
              const days = Math.round((new Date(c.renewalDate) - now) / 86400000);
              return (
                <div key={c.id} onClick={() => { setSelectedClient(c); setPage("client-detail"); }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid " + BORDER, cursor: "pointer" }}>
                  <div><div style={{ fontSize: 13, fontWeight: 600 }}>{c.clubName}</div><div style={{ fontSize: 11, color: SLATE }}>{c.service}</div></div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: days < 30 ? RED : days < 60 ? "#C2410C" : GREEN }}>{days} days</div>
                    <div style={{ fontSize: 11, color: SLATE }}>{fmt(c.renewalDate)}</div>
                  </div>
                </div>
              );
            })}
            {clients.filter(c => { if (!c.renewalDate) return false; const diff = (new Date(c.renewalDate) - now) / 86400000; return diff > 0 && diff <= 90; }).length === 0 && <div style={{ color: SLATE, fontSize: 13, fontStyle: "italic" }}>No renewals in the next 90 days.</div>}
          </div>
        </div>
      </div>
    );
  };

  if (isRecoveryMode) {
    return (
      <div style={S.app}>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 0.4; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1); } } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <link href="https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet" />
        <div
          style={{ position: "fixed", inset: 0, zIndex: 1, background: "rgba(12, 35, 64, 0.94)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, minHeight: "100vh" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="recovery-pw-title"
        >
          <div style={{ ...S.modalBox, width: "min(440px, 100%)", maxWidth: 440, background: BEIGE, border: "2px solid " + GOLD, boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
            <h2 id="recovery-pw-title" style={{ margin: "0 0 8px", fontFamily: "Merriweather,serif", fontSize: 22, color: NAVY }}>Set New Password</h2>
            <p style={{ margin: "0 0 22px", fontSize: 13, color: SLATE, lineHeight: 1.6 }}>Choose a new password for your account. You must complete this step to continue.</p>
            <div style={{ display: "grid", gap: 14, marginBottom: 22 }}>
              <div>
                <label style={S.label}>New Password</label>
                <input type="password" value={recoveryPwForm.newPw} onChange={e => setRecoveryPwForm({ ...recoveryPwForm, newPw: e.target.value })} placeholder="At least 8 characters" style={S.input} autoComplete="new-password" disabled={recoveryPwSubmitting} />
              </div>
              <div>
                <label style={S.label}>Confirm New Password</label>
                <input type="password" value={recoveryPwForm.confirm} onChange={e => setRecoveryPwForm({ ...recoveryPwForm, confirm: e.target.value })} placeholder="Re-enter new password" style={S.input} autoComplete="new-password" disabled={recoveryPwSubmitting} />
              </div>
            </div>
            <button type="button" onClick={handleRecoveryPasswordSubmit} disabled={recoveryPwSubmitting} style={{ ...S.btnPrimary, width: "100%", opacity: recoveryPwSubmitting ? 0.75 : 1, cursor: recoveryPwSubmitting ? "wait" : "pointer" }}>
              {recoveryPwSubmitting ? "Saving…" : "Submit"}
            </button>
          </div>
        </div>
        {toast && <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 99999, background: toast.type === "success" ? NAVY : RED, color: WHITE, padding: "12px 20px", borderRadius: 10, fontSize: 13, fontFamily: "Open Sans, sans-serif", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", display: "flex", alignItems: "center", gap: 8 }}>{toast.type === "success" ? "✓" : "✕"} {toast.msg}</div>}
      </div>
    );
  }

  return (
    <div style={S.app}>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 0.4; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1); } } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <link href="https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet" />
      {showAddLead && (
        <LeadFormModal
          newLead={newLead}
          setNewLead={setNewLead}
          stages={stages}
          leadSources={leadSources}
          services={services}
          users={users}
          setShowAddLead={setShowAddLead}
          handleAddLead={handleAddLead}
        />
      )}
      {showAddClient && (
        <ClientFormModal
          newClient={newClient}
          setNewClient={setNewClient}
          leadSources={leadSources}
          services={services}
          clientStatuses={clientStatuses}
          users={users}
          setShowAddClient={setShowAddClient}
          handleAddClient={handleAddClient}
        />
      )}
      {showAddActivity && (
        <ActivityModal
          newActivity={newActivity}
          setNewActivity={setNewActivity}
          setShowAddActivity={setShowAddActivity}
          handleAddActivity={handleAddActivity}
        />
      )}
      {showConvert && (
        <ConvertModal
          selectedLead={selectedLead}
          convertData={convertData}
          setConvertData={setConvertData}
          clientStatuses={clientStatuses}
          setShowConvert={setShowConvert}
          handleConvert={handleConvert}
        />
      )}
      {showBulkUpload && (
        <BulkUploadModal
          bulkStep={bulkStep}
          bulkPreview={bulkPreview}
          bulkErrors={bulkErrors}
          leads={leads}
          setBulkStep={setBulkStep}
          setBulkPreview={setBulkPreview}
          setBulkErrors={setBulkErrors}
          setShowBulkUpload={setShowBulkUpload}
          setPage={setPage}
          confirmBulkImport={confirmBulkImport}
        />
      )}
      {showProposalModal && (
        <ProposalModal
          showProposalModal={showProposalModal}
          proposalRecord={proposalRecord}
          generatedProposal={generatedProposal}
          proposalLoading={proposalLoading}
          proposalCopied={proposalCopied}
          setShowProposalModal={setShowProposalModal}
          setGeneratedProposal={setGeneratedProposal}
          setProposalCopied={setProposalCopied}
          generateProposal={generateProposal}
        />
      )}
      {toast && <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 99999, background: toast.type === "success" ? NAVY : RED, color: WHITE, padding: "12px 20px", borderRadius: 10, fontSize: 13, fontFamily: "Open Sans, sans-serif", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", display: "flex", alignItems: "center", gap: 8 }}>{toast.type === "success" ? "✓" : "✕"} {toast.msg}</div>}
      <div style={{ display: "flex", minHeight: "100vh" }}>
        <aside style={{ width: 220, flexShrink: 0, background: NAVY_DEEP, minHeight: "100vh", display: "flex", flexDirection: "column", position: "sticky", top: 0, zIndex: 100, alignSelf: "flex-start" }}>
          <div style={{ padding: "24px 20px", borderBottom: "1px solid rgba(197,162,93,0.15)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, background: `linear-gradient(135deg, ${GOLD}, #d4b472)`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Merriweather', serif", fontWeight: 700, fontSize: 13, color: NAVY_DEEP }}>AG</div>
              <div>
                <div style={{ fontFamily: "'Merriweather', serif", fontSize: 15, fontWeight: 700, color: "#fff", letterSpacing: "0.02em" }}>AshGro</div>
                <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, marginTop: 3 }}>Membership Partners</div>
              </div>
            </div>
          </div>
          <nav style={{ padding: "16px 12px", flex: 1 }}>
            {[{ label: "Dashboard", icon: "⊞", page: "dashboard" }, { label: "Leads", icon: "◎", page: "leads" }, { label: "Clients", icon: "◻", page: "clients" }, { label: "Calendar", icon: "⊡", page: "calendar" }, { label: "Growth Calculator", icon: "📊", page: "calculator" }].map(item => {
              const isActive = page === item.page || (page === "lead-detail" && item.page === "leads") || (page === "client-detail" && item.page === "clients");
              return (
              <div key={item.page} onClick={() => setPage(item.page)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, marginBottom: 2, fontSize: 13, fontWeight: isActive ? 600 : 500, color: isActive ? GOLD : "rgba(255,255,255,0.72)", background: isActive ? "rgba(197,162,93,0.12)" : "transparent", cursor: "pointer", borderLeft: isActive ? `3px solid ${GOLD}` : "3px solid transparent", transition: "all 0.15s" }}>
                <span style={{ fontSize: 14, width: 18, textAlign: "center" }}>{item.icon}</span>
                {item.label}
              </div>
            ); })}
            <div style={{ marginTop: 16, marginBottom: 6, fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(197,162,93,0.6)", padding: "0 12px" }}>Intelligence</div>
            <div onClick={() => setPage("agent")} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, fontSize: 13, color: page === "agent" ? GOLD : "rgba(197,162,93,0.55)", background: page === "agent" ? "rgba(197,162,93,0.12)" : "transparent", cursor: "pointer", transition: "all 0.15s", borderLeft: page === "agent" ? `3px solid ${GOLD}` : "3px solid transparent" }}>
              <span style={{ fontSize: 14, width: 18, textAlign: "center" }}>✦</span> AI Agent
            </div>
            <div onClick={() => setPage("inbox")} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, fontSize: 13, color: page === "inbox" ? GOLD : "rgba(197,162,93,0.55)", background: page === "inbox" ? "rgba(197,162,93,0.12)" : "transparent", cursor: "pointer", transition: "all 0.15s", borderLeft: page === "inbox" ? `3px solid ${GOLD}` : "3px solid transparent" }}>
              <span style={{ fontSize: 14, width: 18, textAlign: "center" }}>✉</span> Inbox
            </div>
            <div style={{ marginTop: 16, marginBottom: 6, fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(197,162,93,0.6)", padding: "0 12px" }}>Admin</div>
            <div onClick={() => { setPage("settings"); if (newIntakeCount > 0) setSettingsTab("integrations"); }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, fontSize: 13, fontWeight: page === "settings" ? 600 : 500, color: page === "settings" ? GOLD : "rgba(255,255,255,0.72)", background: page === "settings" ? "rgba(197,162,93,0.12)" : "transparent", cursor: "pointer", transition: "all 0.15s", borderLeft: page === "settings" ? `3px solid ${GOLD}` : "3px solid transparent" }}>
              <span style={{ fontSize: 14, width: 18, textAlign: "center" }}>⚙</span> Settings
              {newIntakeCount > 0 && <span style={{ marginLeft: 6, background: "#EF4444", color: WHITE, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 10 }}>{newIntakeCount}</span>}
            </div>
          </nav>
          <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(197,162,93,0.1)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "#003B2E", border: "1px solid rgba(197,162,93,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: GOLD, flexShrink: 0 }}>
                {(currentUser?.name || "U").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>{currentUser?.name || "User"}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginTop: 1 }}>{currentUser?.role || "Admin"}</div>
              </div>
              <button onClick={() => { if (onLogout) onLogout(); else supabase?.auth?.signOut(); }} style={{ marginLeft: "auto", background: "none", border: "none", color: "rgba(255,255,255,0.2)", cursor: "pointer", fontSize: 12 }}>⎋</button>
            </div>
          </div>
        </aside>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#F7F4ED", minHeight: "100vh" }}>
          <header style={{ height: 60, background: "#fff", borderBottom: "1px solid #DDD8CC", display: "flex", alignItems: "center", padding: "0 32px", gap: 12, flexShrink: 0, boxShadow: "0 1px 3px rgba(12,35,64,0.06)" }}>
            <span style={{ fontFamily: "'Merriweather', serif", fontSize: 18, fontWeight: 700, color: NAVY, letterSpacing: "-0.01em" }}>
              {page === "leads" || page === "lead-detail" ? "Leads" : page === "clients" || page === "client-detail" ? "Clients" : page === "dashboard" ? "Dashboard" : page === "calendar" ? "Calendar" : page === "calculator" ? "Growth Calculator" : page === "agent" ? "AI Agent" : page === "inbox" ? "Inbox" : "Settings"}
            </span>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={syncGmailEmails} disabled={gmailSyncing} title="Sync Gmail replies" style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", border: "1px solid #DDD8CC", borderRadius: 6, fontSize: 12, color: gmailSyncing ? GOLD : SLATE, cursor: gmailSyncing ? "default" : "pointer", background: "#fff", fontFamily: "'Open Sans', sans-serif" }}>
                <span style={{ display: "inline-block", animation: gmailSyncing ? "spin 1s linear infinite" : "none", fontSize: 13 }}>⟳</span>
                {gmailSyncing ? "Syncing..." : lastSynced ? "Sync" : "Sync Gmail"}
              </button>
              <div ref={notifRef} style={{ position: "relative" }}>
                <button onClick={() => setShowNotifPanel(!showNotifPanel)} style={{ position: "relative", background: "none", border: "none", cursor: "pointer", padding: "6px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, color: unreadCount > 0 ? NAVY : SLATE }} title="Notifications">
                  <span style={{ fontSize: 18 }}>🔔</span>
                  {unreadCount > 0 && <span style={{ position: "absolute", top: 2, right: 2, width: 16, height: 16, borderRadius: "50%", background: "#EF4444", color: WHITE, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Open Sans, sans-serif" }}>{unreadCount > 9 ? "9+" : unreadCount}</span>}
                </button>
                {showNotifPanel && (
                  <div style={{ position: "absolute", top: "calc(100% + 10px)", right: 0, width: 380, background: WHITE, border: "1px solid " + BORDER, borderRadius: 14, boxShadow: "0 12px 40px rgba(0,0,0,0.15)", zIndex: 300, overflow: "hidden" }}>
                    <div style={{ padding: "16px 20px", borderBottom: "1px solid " + BORDER, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontFamily: "Merriweather,serif", fontWeight: 700, fontSize: 14, color: NAVY }}>Inbox Replies</div>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        {lastSynced && <span style={{ fontSize: 10, color: SLATE }}>Synced {new Date(lastSynced).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</span>}
                        {unreadCount > 0 && <button onClick={markAllRead} style={{ fontSize: 11, color: GOLD, background: "none", border: "none", cursor: "pointer", fontFamily: "Open Sans, sans-serif" }}>Mark all read</button>}
                      </div>
                    </div>
                    <div style={{ maxHeight: 400, overflowY: "auto" }}>
                      {notifications.length === 0 ? (
                        <div style={{ padding: "32px 20px", textAlign: "center" }}>
                          <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
                          <div style={{ fontSize: 13, color: SLATE }}>No email replies yet.</div>
                          <div style={{ fontSize: 12, color: SLATE, marginTop: 4 }}>Click Sync Gmail to check your inbox.</div>
                          {gmailError && <div style={{ marginTop: 12, fontSize: 12, color: RED, background: RED_BG, padding: "8px 12px", borderRadius: 8 }}>{gmailError}</div>}
                        </div>
                      ) : (
                        notifications.map(n => (
                          <div key={n.id} onClick={() => { markRead(n.id); setShowNotifPanel(false); if (n.type === "lead") { const lead = leads.find(l => sameId(l.id, n.recordId)); if (lead) { setSelectedLead(lead); setPage("lead-detail"); } } else { const client = clients.find(c => sameId(c.id, n.recordId)); if (client) { setSelectedClient(client); setPage("client-detail"); } } }} style={{ padding: "14px 20px", borderBottom: "1px solid " + BORDER, cursor: "pointer", background: n.read ? WHITE : "#FFFCF5", display: "flex", gap: 12, alignItems: "flex-start", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = BEIGE} onMouseLeave={e => e.currentTarget.style.background = n.read ? WHITE : "#FFFCF5"}>
                            <div style={{ width: 36, height: 36, borderRadius: "50%", background: n.type === "lead" ? GOLD : NAVY, color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{initials(n.contact)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                                <div style={{ fontWeight: 700, fontSize: 13, color: NAVY }}>{n.name}</div>
                                {!n.read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: GOLD, flexShrink: 0, marginTop: 4 }} />}
                              </div>
                              <div style={{ fontSize: 12, color: GOLD, marginBottom: 3 }}>📧 {n.subject}</div>
                              <div style={{ fontSize: 12, color: SLATE, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{n.snippet}</div>
                              <div style={{ fontSize: 10, color: SLATE, marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ display: "flex", gap: 5 }}>
                                  <span style={{ background: n.type === "lead" ? "#EEF2FF" : GREEN_BG, color: n.type === "lead" ? "#3730A3" : GREEN, padding: "2px 7px", borderRadius: 4, fontWeight: 600, fontSize: 10 }}>{n.type === "lead" ? "Lead" : "Client"}</span>
                                  {n.hasDraft && <span style={{ background: GREEN_BG, color: GREEN, padding: "2px 7px", borderRadius: 4, fontWeight: 600, fontSize: 10 }}>✦ Draft Ready</span>}
                                </div>
                                <span>{n.date}</span>
                              </div>
                            </div>
                            <button onClick={e => { e.stopPropagation(); clearNotification(n.id); }} style={{ background: "none", border: "none", color: SLATE, cursor: "pointer", fontSize: 14, lineHeight: 1, flexShrink: 0, padding: 2 }} title="Dismiss">×</button>
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div style={{ padding: "10px 20px", borderTop: "1px solid " + BORDER, display: "flex", justifyContent: "center" }}>
                        <button onClick={() => saveNotifications([])} style={{ fontSize: 12, color: SLATE, background: "none", border: "none", cursor: "pointer", fontFamily: "Open Sans, sans-serif" }}>Clear all notifications</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 12, color: SLATE }}>{userEmail || currentUser?.email || companyEmail}</span>
              {onLogout && (
                <button onClick={onLogout} style={{ padding: "6px 14px", background: "transparent", color: SLATE, border: "1px solid " + BORDER, borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "Open Sans, sans-serif", transition: "all 0.2s" }} onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }} onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.color = SLATE; }}>
                  Logout
                </button>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", background: BEIGE, borderRadius: 20, border: "1px solid " + BORDER, cursor: "pointer" }} onClick={() => { setPage("settings"); setSettingsTab("users"); }}>
                <div style={{ width: 24, height: 24, borderRadius: "50%", background: currentUser.color, color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{currentUser.initials}</div>
                <span style={{ fontSize: 12, color: NAVY, fontWeight: 600 }}>{currentUser.name}</span>
              </div>
            </div>
          </header>
      <div style={{ ...S.body, overflowX: "auto", overflowY: "auto", ...(page === "agent" ? { maxWidth: "none", padding: "24px 32px" } : page === "calendar" ? { maxWidth: "none", padding: 0, width: "100%" } : page === "calculator" ? { maxWidth: "none", padding: 0, width: "100%" } : page === "inbox" ? { maxWidth: "none", padding: "24px 32px" } : {}) }}>
        {page === "dashboard" && <Dashboard leads={leads} clients={clients} stages={stages} clientStatuses={clientStatuses} users={users} setPage={setPage} setSelectedLead={setSelectedLead} setSelectedClient={setSelectedClient} />}
        {page === "leads" && (
          <LeadsList
            savedViews={savedViews}
            activeViewId={activeViewId}
            newViewName={newViewName}
            showSaveView={showSaveView}
            search={search}
            filterStage={filterStage}
            filterUser={filterUser}
            filterSource={filterSource}
            filterService={filterService}
            stages={stages}
            users={users}
            leadSources={leadSources}
            services={services}
            checkedLeads={checkedLeads}
            paginatedLeads={paginatedLeads}
            filteredLeads={filteredLeads}
            leadPage={leadPage}
            totalLeadPages={totalLeadPages}
            leadSortColumn={leadSortColumn}
            leadSortDir={leadSortDir}
            setActiveViewId={setActiveViewId}
            setFilterStage={setFilterStage}
            setFilterUser={setFilterUser}
            setFilterSource={setFilterSource}
            setFilterService={setFilterService}
            setLeadPage={setLeadPage}
            setNewViewName={setNewViewName}
            setShowSaveView={setShowSaveView}
            setSearch={setSearch}
            setCheckedLeads={setCheckedLeads}
            setLeadSortColumn={setLeadSortColumn}
            setLeadSortDir={setLeadSortDir}
            setBulkStep={setBulkStep}
            setShowBulkUpload={setShowBulkUpload}
            setShowAddLead={setShowAddLead}
            saveSavedViews={saveSavedViews}
            showToast={showToast}
            updateLeadStage={updateLeadStage}
            setSelectedLead={setSelectedLead}
            setEditingLead={setEditingLead}
            setPage={setPage}
            deleteLead={deleteLead}
            leads={leads}
            saveLeads={saveLeads}
          />
        )}
        {page === "lead-detail" && (
          <LeadDetail
            selectedLead={selectedLead}
            editingLead={editingLead}
            editLeadForm={editLeadForm}
            leads={leads}
            stages={stages}
            leadSources={leadSources}
            services={services}
            users={users}
            customLeadFields={customLeadFields}
            setPage={setPage}
            setEditingLead={setEditingLead}
            setEditLeadForm={setEditLeadForm}
            setSelectedLead={setSelectedLead}
            setActivityTarget={setActivityTarget}
            setShowAddActivity={setShowAddActivity}
            setConvertData={setConvertData}
            setShowConvert={setShowConvert}
            setProposalRecord={setProposalRecord}
            setShowProposalModal={setShowProposalModal}
            updateLeadStage={updateLeadStage}
            updateLeadCustomField={updateLeadCustomField}
            deleteLead={deleteLead}
            saveLeads={saveLeads}
            showEmailModal={showEmailModal}
            setShowEmailModal={setShowEmailModal}
            showToast={showToast}
            currentUser={currentUser}
            saveClients={saveClients}
            clients={clients}
            emailTemplates={emailTemplates}
            emailSignature={emailSignature}
          />
        )}
        {page === "clients" && (
          <ClientsList
            clientSearch={clientSearch}
            showFilterClient={showFilterClient}
            filterStatus={filterStatus}
            showClientUserFilter={showClientUserFilter}
            filterClientUser={filterClientUser}
            clientStatuses={clientStatuses}
            users={users}
            checkedClients={checkedClients}
            paginatedClients={paginatedClients}
            filteredClients={filteredClients}
            clientPage={clientPage}
            totalClientPages={totalClientPages}
            clientSortColumn={clientSortColumn}
            clientSortDir={clientSortDir}
            setClientSearch={setClientSearch}
            setClientPage={setClientPage}
            setShowFilterClient={setShowFilterClient}
            setFilterStatus={setFilterStatus}
            setShowClientUserFilter={setShowClientUserFilter}
            setFilterClientUser={setFilterClientUser}
            setCheckedClients={setCheckedClients}
            setClientSortColumn={setClientSortColumn}
            setClientSortDir={setClientSortDir}
            setPage={setPage}
            setSelectedClient={setSelectedClient}
            setShowAddClient={setShowAddClient}
            updateClientStatus={updateClientStatus}
            deleteClient={deleteClient}
          />
        )}
        {page === "client-detail" && (
          <ClientDetail
            selectedClient={selectedClient}
            editingClient={editingClient}
            editClientForm={editClientForm}
            clients={clients}
            clientStatuses={clientStatuses}
            services={services}
            users={users}
            customClientFields={customClientFields}
            setPage={setPage}
            setEditingClient={setEditingClient}
            setEditClientForm={setEditClientForm}
            setSelectedClient={setSelectedClient}
            setActivityTarget={setActivityTarget}
            setShowAddActivity={setShowAddActivity}
            updateClientStatus={updateClientStatus}
            updateClientCustomField={updateClientCustomField}
            deleteClient={deleteClient}
            saveClients={saveClients}
            showEmailModal={showEmailModal}
            setShowEmailModal={setShowEmailModal}
            showToast={showToast}
            currentUser={currentUser}
            saveLeads={saveLeads}
            leads={leads}
            emailTemplates={emailTemplates}
            emailSignature={emailSignature}
          />
        )}
        {page === "inbox" && <GmailInbox currentUser={currentUser} signature={emailSignature} />}
        {page === "agent" && (
          <AgentPage
            agentInput={agentInput}
            setAgentInput={setAgentInput}
            agentMessages={agentMessages}
            sendAgentMessage={sendAgentMessage}
            agentLoading={agentLoading}
            saveAgentMessages={saveAgentMessages}
            chatEndRef={chatEndRef}
            dailyBrief={dailyBrief}
            briefLoading={briefLoading}
            briefGenerated={briefGenerated}
            generateDailyBrief={generateDailyBrief}
            syncGmailEmails={syncGmailEmails}
            gmailSyncing={gmailSyncing}
            lastSynced={lastSynced}
            gmailError={gmailError}
            notifications={notifications}
            unreadCount={unreadCount}
            setPage={setPage}
            setShowNotifPanel={setShowNotifPanel}
            stats={stats}
            clients={clients}
            S={S}
          />
        )}
        {page === "calendar" && (
          <div style={{ flex: 1, minWidth: 0, overflow: "auto" }}>
            <CalendarPage
            calMonth={calMonth}
            calView={calView}
            setCalView={setCalView}
            calEvents={calEvents}
            showAddEvent={showAddEvent}
            eventForm={eventForm}
            selectedCalEvent={selectedCalEvent}
            gcalSyncing={gcalSyncing}
            gcalLastSynced={gcalLastSynced}
            currentUser={currentUser}
            leads={leads}
            clients={clients}
            users={users}
            setCalMonth={setCalMonth}
            setEventForm={setEventForm}
            setShowAddEvent={setShowAddEvent}
            setSelectedCalEvent={setSelectedCalEvent}
            setGcalLastSynced={setGcalLastSynced}
            setSelectedLead={setSelectedLead}
            setSelectedClient={setSelectedClient}
            setPage={setPage}
            saveCalEvents={saveCalEvents}
            showToast={showToast}
            syncGoogleCalendar={syncGoogleCalendar}
            userId={userId}
          />
        </div>
        )}
        {page === "calculator" && (
          <div style={{ width: "100%", padding: 0, overflowX: "auto" }}>
            <AshGroCalculator />
          </div>
        )}
        {page === "settings" && (
          <Settings
            editingList={editingList}
            setEditingList={setEditingList}
            settingsTab={settingsTab}
            setSettingsTab={setSettingsTab}
            newIntakeCount={newIntakeCount}
            showNewField={showNewField}
            setShowNewField={setShowNewField}
            newFieldForm={newFieldForm}
            setNewFieldForm={setNewFieldForm}
            addCustomField={addCustomField}
            editingField={editingField}
            setEditingField={setEditingField}
            saveEditField={saveEditField}
            deleteCustomField={deleteCustomField}
            customLeadFields={customLeadFields}
            customClientFields={customClientFields}
            stages={stages}
            leadSources={leadSources}
            services={services}
            clientStatuses={clientStatuses}
            saveStages={saveStages}
            saveLeadSources={saveLeadSources}
            saveServices={saveServices}
            saveClientStatuses={saveClientStatuses}
            companyName={companyName}
            setCompanyName={setCompanyName}
            companyEmail={companyEmail}
            setCompanyEmail={setCompanyEmail}
            emailSignature={emailSignature}
            setEmailSignature={setEmailSignature}
            persist={persist}
            pwForm={pwForm}
            setPwForm={setPwForm}
            handlePwChange={handlePwChange}
            pwUpdating={pwUpdating}
            showAddUser={showAddUser}
            setShowAddUser={setShowAddUser}
            userForm={userForm}
            setUserForm={setUserForm}
            saveUsers={saveUsers}
            users={users}
            saveCurrentUser={saveCurrentUser}
            currentUser={currentUser}
            editingUser={editingUser}
            setEditingUser={setEditingUser}
            userEditForm={userEditForm}
            setUserEditForm={setUserEditForm}
            saveEditUser={saveEditUser}
            leads={leads}
            clients={clients}
            templateForm={templateForm}
            setTemplateForm={setTemplateForm}
            editingTemplate={editingTemplate}
            setEditingTemplate={setEditingTemplate}
            showAddTemplate={showAddTemplate}
            setShowAddTemplate={setShowAddTemplate}
            saveEmailTemplates={saveEmailTemplates}
            emailTemplates={emailTemplates}
            templatePreview={templatePreview}
            setTemplatePreview={setTemplatePreview}
            simulateWebLead={simulateWebLead}
            IntegrationsEmbed={<Integrations />}
            syncGmailEmails={syncGmailEmails}
            gmailSyncing={gmailSyncing}
            lastSynced={lastSynced}
            gmailError={gmailError}
            gmailConnected={gmailConnected}
            setGmailConnected={setGmailConnected}
            saveNotifications={saveNotifications}
            notifications={notifications}
            showToast={showToast}
            saveLeads={saveLeads}
            saveClients={saveClients}
            supabase={supabase}
            userId={userId}
          />
        )}
      </div>
        </div>
      </div>
    </div>
  );
}

